import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import '../styles/KatKarsilig.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import KatCizimPanel from '../components/KatCizimPanel';
import ProjeGorunumleri from '../components/ProjeGorunumleri';
import KatKarsiligGorunumPaylasim from '../components/KatKarsiligGorunumPaylasim';
import { PaylasimRenkSkalasi } from '../components/BolumPaylasimDetay';
import { ADIMLAR, AdimKey, AdimCubugu, AdimRehberi, KontrolMaddesi, adimDurumu } from '../components/KatKarsiligAdimRehberi';
import yonlerImg from '../images/yonler.png';
import { useTranslation } from 'react-i18next';
import { ortakSablonIndir, ortakExcelOku, bolumSablonIndir, bolumExcelOku } from '../utils/katKarsiligExcel';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Arsa {
    arsaID: number;
    takipNo: string;
    ada: string | null;
    parsel: string | null;
    il: string | null;
    ilce: string | null;
    mahalle: string | null;
    arsaMetrekare: number | null;
    arsaTutar: number | null;
    aciklama: string | null;
    aktifMi: boolean;
    muteahhitler: Muteahhit[];
    ortaklar: ArsaOrtak[];
    bloklar: Blok[];
    cepheler: Cephe[];
    bolumler: Bolum[];
}

interface Muteahhit {
    muteahhitID: number;
    arsaID: number;
    adSoyad: string;
    telefon: string | null;
    eposta: string | null;
    vergiNo: string | null;
    not: string | null;
    hisseMetre: number | null;
    payYuzde: number;
    toplamPayYuzde: number;
    payTL: number | null;
    aldigiPayTL: number | null;
}

interface ArsaOrtak {
    ortakID: number;
    arsaID: number;
    adSoyad: string;
    telefon: string | null;
    eposta: string | null;
    tcKimlik: string | null;
    hisseMetre: number | null;
    hisseOrani: number;
    not: string | null;
    payTL: number | null;
    aldigiPayTL: number | null;
}

interface Blok {
    blokID: number;
    arsaID: number;
    blokAdi: string;
    katSayisi: number | null;
    en: number | null;
    boy: number | null;
    aciklama: string | null;
    katlar: Kat[];
}

interface Kat {
    katID: number;
    blokID: number;
    katAdi: string;
    siraNo: number;
    en: number | null;
    boy: number | null;
}

interface Cephe {
    cepheID: number;
    arsaID: number;
    cepheAdi: string;
    yon: string | null;
    aciklama: string | null;
}

interface Bolum {
    bolumID: number;
    arsaID: number;
    blokID: number | null;
    katID: number | null;
    cephe1ID: number | null;
    cephe2ID: number | null;
    cephe3ID: number | null;
    cephe4ID: number | null;
    bolumAdi: string;
    bolumTipi: string | null;
    turu: string | null;
    m2: number;
    fiyat: number;
    birimFiyat: number;
    paylasılanAlan: number;
    not: string | null;
    xVal: number | null;
    yVal: number | null;
    blok: { blokAdi: string } | null;
    kat: { katAdi: string } | null;
    cephe1: { cepheAdi: string } | null;
    cephe2: { cepheAdi: string } | null;
    cephe3: { cepheAdi: string } | null;
    cephe4: { cepheAdi: string } | null;
    paylasimlar: Paylasim[];
}

interface Paylasim {
    paylasimID: number;
    bolumID: number;
    ortakID: number | null;
    muteahhitID: number | null;
    oranYuzde: number;
    aciklamaNot: string | null;
    ortak: { adSoyad: string } | null;
    muteahhit: { adSoyad: string } | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Bölümün paylaştırılmış toplam oranı (%). 100'e ulaşınca paylaşım tamamdır. */
const bolumPaylasimOrani = (b: Bolum): number => b.paylasimlar.reduce((s, p) => s + p.oranYuzde, 0);

/** Ortak/müteahhidin hak ettiği payın (payTL) ne kadarını bölümlerden aldığı (%); payı hesaplanmamışsa null. */
const payTamamlanmaOrani = (k: { payTL: number | null; aldigiPayTL: number | null }): number | null =>
    k.payTL && k.payTL > 0 ? ((k.aldigiPayTL ?? 0) / k.payTL) * 100 : null;
// Bölümler bütün parçalar halinde dağıtıldığı için tam eşitlik nadirdir; %99,5 ve üstü tamamlanmış sayılır.
const PAY_TAMAM_ESIGI = 99.5;

/** İnce ilerleme çubuğu + yüzde. */
const OranCubugu: React.FC<{ oran: number; tamamEsigi?: number }> = ({ oran, tamamEsigi = 100 }) => {
    const tamam = oran >= tamamEsigi;
    const renk = tamam ? '#16a34a' : oran > 0 ? '#d97706' : '#cbd5e1';
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 96 }}>
            <div style={{ flex: 1, height: 5, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(100, oran)}%`, height: '100%', background: renk }} />
            </div>
            <span style={{ fontSize: 11.5, fontWeight: 700, color: tamam ? '#16a34a' : oran > 0 ? '#b45309' : '#9ca3af', whiteSpace: 'nowrap' }}>
                %{oran.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}
            </span>
        </div>
    );
};

const TamamIsareti: React.FC<{ title: string }> = ({ title }) => (
    <i className="fas fa-check-circle" style={{ color: '#16a34a', marginLeft: 6 }} title={title}></i>
);

/** Görünümlerde bölüm rengi: paylaşımına göre kime düştüğü. Paylaşımı olmayanlar "Diğer" (gri) görünür. */
const BOLUM_SAHIP_RENK: Record<string, { bg: string; border: string; text: string }> = {
    'Müteahhit': { bg: '#3b82f6', border: '#2563eb', text: '#fff' },
    'Arsa Sahibi': { bg: '#22c55e', border: '#16a34a', text: '#fff' },
    'Ortak Paylaşım': { bg: '#f59e0b', border: '#d97706', text: '#fff' },
};
const bolumSahipDurumu = (b: Bolum): string => {
    if (b.paylasimlar.length === 0) return 'Paylaşılmadı';
    const muteahhit = b.paylasimlar.some(p => p.muteahhitID != null);
    const ortak = b.paylasimlar.some(p => p.ortakID != null);
    return muteahhit && ortak ? 'Ortak Paylaşım' : muteahhit ? 'Müteahhit' : 'Arsa Sahibi';
};

function fmt(val: number | null | undefined) {
    if (val == null) return '–';
    return val.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Sekmeler = iş akışının 7 adımı (bkz. KatKarsiligAdimRehberi)
type TabKey = AdimKey;

// ─── Öneri Tipleri ────────────────────────────────────────────────────────────

interface OneriKisi {
    tip: 'Ortak' | 'Muteahhit';
    id: number;
    adSoyad: string;
    payTL: number;
    kalanOncesi: number;
    oneriOran: number;
    oneriTL: number;
    kalanSonra: number;
    durum: 'tamamlandi' | 'devam';
}

interface Atanamamis {
    tip: string;
    id: number;
    adSoyad: string;
    payTL: number;
    kalanTL: number;
}

interface MevcutPaylasimOneri {
    paylasimID: number;
    adSoyad: string;
    tip: string;
    oranYuzde: number;
    payTL: number;
}

interface BolumOneri {
    bolumID: number;
    bolumAdi: string;
    bolumTipi: string | null;
    fiyat: number;
    dagitilanOran: number;
    kalanOran: number;
    kalanFiyat: number;
    atanmayanFiyat: number;
    mevcutPaylasimlar: MevcutPaylasimOneri[];
    oneriler: OneriKisi[];
}

interface PaylasimOnerileri {
    arsaId: number;
    toplamKalanTL: number;
    bolumler: BolumOneri[];
    atanamamis: Atanamamis[];
}

interface OrtakDuzenleSatir {
    ortakID: number;
    adSoyad: string;
    telefon: string;
    eposta: string;
    tcKimlik: string;
    hisseMetre: string;
    not: string;
    dirty: boolean;
}

interface TopluDuzenleSatir {
    bolumID: number;
    bolumAdi: string;
    bolumTipi: string;
    turu: string;
    blokID: string;
    katID: string;
    cephe1ID: string;
    cephe2ID: string;
    cephe3ID: string;
    cephe4ID: string;
    m2: string;
    fiyat: string;
    birimFiyat: string;
    dirty: boolean;
}

interface TopluBolumSatir {
    id: string;
    blokID: number;
    katID: number | null;
    bolumAdi: string;
    turu: string;
    m2: string;
    fiyat: string;
    birimFiyat: string;
    bolumTipi: string;
    cephe1ID: string;
    not: string;
    isDuplicate: boolean;
    silindi: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

const KatKarsiligArsaDetay: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const arsaId = Number(id);
    const navigate = useNavigate();

    const { t } = useTranslation();
    const [arsa, setArsa] = useState<Arsa | null>(null);
    // Excel şablonu ile toplu ortak / bölüm aktarımı
    const ortakExcelRef = useRef<HTMLInputElement>(null);
    const bolumExcelRef = useRef<HTMLInputElement>(null);
    const [excelYukleniyor, setExcelYukleniyor] = useState<'ortak' | 'bolum' | null>(null);
    const [gorunumlerAcik, setGorunumlerAcik] = useState(false);
    const [bolumIslemlerAcik, setBolumIslemlerAcik] = useState(false);
    // Adım 1: künye düzenleme
    const [kunyeAcik, setKunyeAcik] = useState(false);
    const [kunyeForm, setKunyeForm] = useState({ takipNo: '', ada: '', parsel: '', il: '', ilce: '', mahalle: '', aciklama: '' });
    const [kunyeKaydediliyor, setKunyeKaydediliyor] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<TabKey>('genel');

    // ─ Modal states ─
    const [muteahhitModal, setMuteahhitModal] = useState(false);
    const [muteahhitEdit, setMuteahhitEdit] = useState<Muteahhit | null>(null);
    const [muteahhitForm, setMuteahhitForm] = useState({ adSoyad: '', telefon: '', eposta: '', vergiNo: '', not: '', hisseMetre: '', payYuzde: '' });

    const [ortakModal, setOrtakModal] = useState(false);
    const [ortakEdit, setOrtakEdit] = useState<ArsaOrtak | null>(null);
    const [ortakForm, setOrtakForm] = useState({ adSoyad: '', telefon: '', eposta: '', tcKimlik: '', hisseMetre: '', not: '' });
    const [ortakEkleAktif, setOrtakEkleAktif] = useState(false);
    const [ortakEkleForm, setOrtakEkleForm] = useState({ adSoyad: '', telefon: '', eposta: '', tcKimlik: '', hisseMetre: '', not: '' });
    const [ortakEkleSaving, setOrtakEkleSaving] = useState(false);
    const [ortakEkleError, setOrtakEkleError] = useState('');
    const [ortakDuzenleMod, setOrtakDuzenleMod] = useState(false);
    const [ortakDuzenleSatirlar, setOrtakDuzenleSatirlar] = useState<OrtakDuzenleSatir[]>([]);
    const [ortakDuzenleSaving, setOrtakDuzenleSaving] = useState(false);

    const [blokModal, setBlokModal] = useState(false);
    const [blokEdit, setBlokEdit] = useState<Blok | null>(null);
    const [blokForm, setBlokForm] = useState({ blokAdi: '', katSayisi: '', en: '', boy: '', aciklama: '', zemin: false, bodrum: false });

    // inline kat ekleme: blokID -> satır görünür mü
    const [inlineKatBlokId, setInlineKatBlokId] = useState<number | null>(null);
    const [selectedBlokId, setSelectedBlokId] = useState<number | null>(null);
    const [cizimKat, setCizimKat] = useState<{ blok: Blok; kat: Kat } | null>(null);
    const [inlineKatForm, setInlineKatForm] = useState({ katAdi: '', siraNo: '', en: '', boy: '' });
    const [inlineKatSaving, setInlineKatSaving] = useState(false);

    const [katModal, setKatModal] = useState(false);
    const [katBlokId, setKatBlokId] = useState<number | null>(null);
    const [katEdit, setKatEdit] = useState<Kat | null>(null);
    const [katForm, setKatForm] = useState({ katAdi: '', siraNo: '', en: '', boy: '' });

    const [cepheModal, setCepheModal] = useState(false);
    const [cepheEdit, setCepheEdit] = useState<Cephe | null>(null);
    const [cepheForm, setCepheForm] = useState({ cepheAdi: '', yon: '', aciklama: '' });

    const [topluDuzenleMod, setTopluDuzenleMod] = useState(false);
    const [topluDuzenleSatirlar, setTopluDuzenleSatirlar] = useState<TopluDuzenleSatir[]>([]);
    const [topluDuzenleSaving, setTopluDuzenleSaving] = useState(false);

    const [bolumModal, setBolumModal] = useState(false);
    const [bolumEdit, setBolumEdit] = useState<Bolum | null>(null);
    const [bolumForm, setBolumForm] = useState({
        blokID: '', katID: '', cephe1ID: '', cephe2ID: '', cephe3ID: '', cephe4ID: '',
        bolumAdi: '', bolumTipi: '', turu: '', m2: '', fiyat: '', birimFiyat: '', not: ''
    });
    const [turuListesi, setTuruListesi] = useState<string[]>([]);

    const [paylasimModal, setPaylasimModal] = useState(false);
    const [paylasimBolumId, setPaylasimBolumId] = useState<number | null>(null);
    const [paylasimEdit, setPaylasimEdit] = useState<Paylasim | null>(null);
    // Paylaşım modalında satır içi düzenlenen mevcut paylaşımlar: paylasimID → { oran, not }
    const [paylasimSatirDuzen, setPaylasimSatirDuzen] = useState<Record<number, { oran: string; not: string }>>({});
    // Tarafın aldığı daireler modalı ve orada düzenlenen oranlar (paylasimID → oran)
    const [tarafModal, setTarafModal] = useState<{ tip: 'ortak' | 'muteahhit'; id: number } | null>(null);
    const [tarafSatirDuzen, setTarafSatirDuzen] = useState<Record<number, string>>({});
    const [paylasimIslemde, setPaylasimIslemde] = useState<number | null>(null);
    // Dağıtım önerisinde uygulanan satırlar ("bolumID-tip-id")
    const [oneriUygulanan, setOneriUygulanan] = useState<Set<string>>(new Set());
    const [oneriUygulaniyor, setOneriUygulaniyor] = useState<string | null>(null);
    const [paylasimForm, setPaylasimForm] = useState({ ortakID: '', muteahhitID: '', oranYuzde: '', aciklamaNot: '' });

    const [oneriModal, setOneriModal] = useState(false);
    const [oneriData, setOneriData] = useState<PaylasimOnerileri | null>(null);
    const [oneriLoading, setOneriLoading] = useState(false);
    const [showIslemler, setShowIslemler] = useState(false);
    const [showIndir, setShowIndir] = useState(false);
    const [showBlokRaporModal, setShowBlokRaporModal] = useState(false);
    const [blokRaporBlokId, setBlokRaporBlokId] = useState<number | null>(null);
    const [showBlokRaporPayliModal, setShowBlokRaporPayliModal] = useState(false);
    const [blokRaporPayliBlokId, setBlokRaporPayliBlokId] = useState<number | null>(null);
    const [showVaziyetModal, setShowVaziyetModal] = useState(false);
    const [vaziyetBlokId, setVaziyetBlokId] = useState<number | null>(null);

    // ─ Bölüm görünüm & filtre durumu ─
    const [bolumView, setBolumView] = useState<'liste' | 'tablo' | 'vaziyet' | 'gorunum'>('liste');
    const [bolumFilterTip, setBolumFilterTip] = useState('');
    const [bolumFilterTuru, setBolumFilterTuru] = useState('');
    const [bolumFilterAdi, setBolumFilterAdi] = useState('');
    const [bolumFilterBlok, setBolumFilterBlok] = useState('');
    const [bolumFilterKat, setBolumFilterKat] = useState('');
    const [bolumPage, setBolumPage] = useState(1);
    const BOLUM_SAYFA_BOYUTU = 25;
    const [bolumTabloBlokId, setBolumTabloBlokId] = useState<number | null>(null);
    const [tabloGoster, setTabloGoster] = useState({ fiyat: true, cephe: true, ortaklar: true });
    const [draggingKisi, setDraggingKisi] = useState<{ tip: 'ortak' | 'muteahhit'; id: number; ad: string; kalanPay: number } | null>(null);
    const [hoveredBolumID, setHoveredBolumID] = useState<number | null>(null);
    const [hoveredPayKey, setHoveredPayKey] = useState<string | null>(null);
    const [planModalKat, setPlanModalKat] = useState<{ blok: Blok; kat: Kat } | null>(null);
    const [showCephelerModal, setShowCephelerModal] = useState(false);

    // ─ Vaziyet Görünümü (Bölümler sekmesi) ─
    const [vaziyetGorunumBlokId, setVaziyetGorunumBlokId] = useState<number | null>(null);
    const [vaziyetGorunumKatIdler, setVaziyetGorunumKatIdler] = useState<Set<number>>(new Set());
    const [vaziyetKatFiltreAcik, setVaziyetKatFiltreAcik] = useState(false);

    const [saving, setSaving] = useState(false);
    const [modalError, setModalError] = useState('');

    // ─ Toplu bölüm ekleme wizard ─
    const [topluBolumModal, setTopluBolumModal] = useState(false);
    const [topluWizard, setTopluWizard] = useState({
        blokID: '', basKatSiraNo: '1', kattakiDaireSayisi: '4',
        baslamaDaireNo: '1', eklenecekSayi: '8', turu: '', bolumTipi: '', m2: '', fiyat: '',
    });
    const [topluListesi, setTopluListesi] = useState<TopluBolumSatir[]>([]);
    const [topluSaving, setTopluSaving] = useState(false);

    // ─ Fetch ─
    const fetchArsa = useCallback(async (sessiz: boolean = false) => {
        if (!sessiz) setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/KatKarsiligArsa/${arsaId}`);
            const data = res.data;
            // API null döndürebileceğinden collection'lara varsayılan değer ver
            data.muteahhitler = data.muteahhitler ?? [];
            data.ortaklar = data.ortaklar ?? [];
            data.bloklar = (data.bloklar ?? []).map((b: Blok) => ({ ...b, katlar: b.katlar ?? [] }));
            data.cepheler = data.cepheler ?? [];
            data.bolumler = (data.bolumler ?? []).map((b: Bolum) => ({ ...b, paylasimlar: b.paylasimlar ?? [] }));
            setArsa(data);
        } catch {
            alert(t('msg.islemHatasi'));
        } finally {
            if (!sessiz) setLoading(false);
        }
    }, [arsaId]);
    /** Açık modallar kapanmadan veriyi yeniler. */
    const arsaYenile = () => fetchArsa(true);

    const kunyeyiAc = () => {
        if (!arsa) return;
        setKunyeForm({
            takipNo: arsa.takipNo ?? '', ada: arsa.ada ?? '', parsel: arsa.parsel ?? '', il: arsa.il ?? '',
            ilce: arsa.ilce ?? '', mahalle: arsa.mahalle ?? '', aciklama: arsa.aciklama ?? '',
        });
        setKunyeAcik(true);
    };

    const kunyeyiKaydet = async () => {
        if (!arsa) return;
        if (!kunyeForm.takipNo.trim()) { alert('Takip no boş olamaz.'); return; }
        setKunyeKaydediliyor(true);
        try {
            const bos = (v: string) => (v.trim() === '' ? null : v.trim());
            await axiosInstance.put(`/api/KatKarsiligArsa/${arsaId}`, {
                takipNo: kunyeForm.takipNo.trim(), ada: bos(kunyeForm.ada), parsel: bos(kunyeForm.parsel),
                il: bos(kunyeForm.il), ilce: bos(kunyeForm.ilce), mahalle: bos(kunyeForm.mahalle),
                aciklama: bos(kunyeForm.aciklama),
                // Arsa m² tarafların hisse m²'sinden hesaplanır; mevcut değer korunur
                arsaMetrekare: arsa.arsaMetrekare, aktifMi: arsa.aktifMi,
            });
            setKunyeAcik(false);
            await fetchArsa();
        } catch (err: any) {
            alert(typeof err?.response?.data === 'string' ? err.response.data : (err?.response?.data?.message || t('msg.islemHatasi')));
        } finally {
            setKunyeKaydediliyor(false);
        }
    };

    const handleExcelYukle = async (tur: 'ortak' | 'bolum', e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !arsa) return;
        setExcelYukleniyor(tur);
        try {
            const { satirlar, hatalar } = tur === 'ortak' ? await ortakExcelOku(file, arsa) : await bolumExcelOku(file, arsa);
            if (hatalar.length > 0) {
                alert(`Excel dosyasında ${hatalar.length} hatalı satır var, hiçbir kayıt aktarılmadı:\n\n`
                    + hatalar.slice(0, 15).join('\n') + (hatalar.length > 15 ? `\n… ve ${hatalar.length - 15} satır daha` : ''));
                return;
            }
            if (satirlar.length === 0) { alert('Excel dosyasında aktarılacak satır bulunamadı.'); return; }
            // ID'si dolu satırlar mevcut kaydı günceller (ortak satırında ortakID, bölüm satırında bolumID)
            const guncel = (satirlar as { ortakID?: number | null; bolumID?: number | null }[])
                .filter(x => (x.ortakID ?? x.bolumID ?? null) !== null).length;
            const ad = tur === 'ortak' ? 'ortak' : 'bölüm';
            if (!window.confirm(`${satirlar.length - guncel} yeni ${ad} eklenecek, ${guncel} ${ad} güncellenecek. Devam edilsin mi?`)) return;
            const res = await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/${tur === 'ortak' ? 'ortaklar' : 'bolumler'}/toplu`, satirlar);
            await fetchArsa();
            alert(`Aktarım tamamlandı: ${res.data?.eklenen ?? 0} eklendi, ${res.data?.guncellenen ?? 0} güncellendi.`);
        } catch (err: any) {
            alert(err?.response?.data?.message || 'Excel aktarımı sırasında hata oluştu.');
        } finally {
            setExcelYukleniyor(null);
        }
    };

    const excelButonlari = (tur: 'ortak' | 'bolum') => (
        <>
            <input ref={tur === 'ortak' ? ortakExcelRef : bolumExcelRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                onChange={e => handleExcelYukle(tur, e)} />
            <button className="kka-btn-secondary" title="Mevcut kayıtlarla birlikte Excel şablonunu indir"
                onClick={() => arsa && (tur === 'ortak' ? ortakSablonIndir(arsa) : bolumSablonIndir(arsa))}>
                <i className="fas fa-file-excel" style={{ marginRight: 6, color: '#1d6f42' }}></i>Şablon İndir
            </button>
            <button className="kka-btn-secondary" title="Doldurulmuş Excel şablonunu yükle" disabled={excelYukleniyor !== null}
                onClick={() => (tur === 'ortak' ? ortakExcelRef : bolumExcelRef).current?.click()}>
                <i className={excelYukleniyor === tur ? 'fas fa-spinner fa-spin' : 'fas fa-file-upload'} style={{ marginRight: 6, color: '#1d6f42' }}></i>Excel'den Yükle
            </button>
        </>
    );

    useEffect(() => { fetchArsa(); }, [fetchArsa]);

    useEffect(() => {
        axiosInstance.get<string[]>('/api/KatKarsiligArsa/bolumler/turu-listesi')
            .then(res => setTuruListesi(res.data))
            .catch(() => {});
    }, []);

    // ─── Müteahhit ────────────────────────────────────────────────────────────

    const openMuteahhitCreate = () => {
        setMuteahhitEdit(null);
        setMuteahhitForm({ adSoyad: '', telefon: '', eposta: '', vergiNo: '', not: '', hisseMetre: '', payYuzde: '' });
        setModalError('');
        setMuteahhitModal(true);
    };
    const openMuteahhitEdit = (m: Muteahhit) => {
        setMuteahhitEdit(m);
        setMuteahhitForm({ adSoyad: m.adSoyad, telefon: m.telefon ?? '', eposta: m.eposta ?? '', vergiNo: m.vergiNo ?? '', not: m.not ?? '', hisseMetre: m.hisseMetre !== null ? String(m.hisseMetre) : '', payYuzde: String(m.payYuzde) });
        setModalError('');
        setMuteahhitModal(true);
    };
    const saveMuteahhit = async () => {
        if (!muteahhitForm.adSoyad.trim()) { setModalError('Ad Soyad zorunludur.'); return; }
        setSaving(true); setModalError('');
        try {
            const payload = { ...muteahhitForm, hisseMetre: muteahhitForm.hisseMetre !== '' ? parseFloat(muteahhitForm.hisseMetre) : null, payYuzde: parseFloat(muteahhitForm.payYuzde) || 0 };
            if (muteahhitEdit) {
                await axiosInstance.put(`/api/KatKarsiligArsa/${arsaId}/muteahhitler/${muteahhitEdit.muteahhitID}`, payload);
            } else {
                await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/muteahhitler`, payload);
            }
            setMuteahhitModal(false);
            fetchArsa();
        } catch (e: any) { setModalError(e?.response?.data || 'Hata'); }
        finally { setSaving(false); }
    };
    const deleteMuteahhit = async (mId: number) => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        await axiosInstance.delete(`/api/KatKarsiligArsa/${arsaId}/muteahhitler/${mId}`);
        fetchArsa();
    };

    // ─── Ortak ────────────────────────────────────────────────────────────────

    const openOrtakCreate = () => {
        setOrtakEdit(null);
        setOrtakForm({ adSoyad: '', telefon: '', eposta: '', tcKimlik: '', hisseMetre: '', not: '' });
        setModalError('');
        setOrtakModal(true);
    };
    const openOrtakEdit = (o: ArsaOrtak) => {
        setOrtakEdit(o);
        setOrtakForm({ adSoyad: o.adSoyad, telefon: o.telefon ?? '', eposta: o.eposta ?? '', tcKimlik: o.tcKimlik ?? '', hisseMetre: o.hisseMetre !== null ? String(o.hisseMetre) : '', not: o.not ?? '' });
        setModalError('');
        setOrtakModal(true);
    };
    const saveOrtak = async () => {
        if (!ortakForm.adSoyad.trim()) { setModalError('Ad Soyad zorunludur.'); return; }
        setSaving(true); setModalError('');
        try {
            const payload = { ...ortakForm, hisseMetre: ortakForm.hisseMetre !== '' ? parseFloat(ortakForm.hisseMetre) : null };
            if (ortakEdit) {
                await axiosInstance.put(`/api/KatKarsiligArsa/${arsaId}/ortaklar/${ortakEdit.ortakID}`, payload);
            } else {
                await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/ortaklar`, payload);
            }
            setOrtakModal(false);
            fetchArsa();
        } catch (e: any) { setModalError(e?.response?.data || 'Hata'); }
        finally { setSaving(false); }
    };
    const deleteOrtak = async (oId: number) => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        await axiosInstance.delete(`/api/KatKarsiligArsa/${arsaId}/ortaklar/${oId}`);
        fetchArsa();
    };

    const saveOrtakInline = async () => {
        if (!ortakEkleForm.adSoyad.trim()) { setOrtakEkleError('Ad Soyad zorunludur.'); return; }
        setOrtakEkleSaving(true); setOrtakEkleError('');
        try {
            await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/ortaklar`, {
                ...ortakEkleForm,
                hisseMetre: ortakEkleForm.hisseMetre !== '' ? parseFloat(ortakEkleForm.hisseMetre) : null,
            });
            setOrtakEkleAktif(false);
            setOrtakEkleForm({ adSoyad: '', telefon: '', eposta: '', tcKimlik: '', hisseMetre: '', not: '' });
            fetchArsa();
        } catch (e: any) { setOrtakEkleError(t('msg.islemHatasi')); }
        finally { setOrtakEkleSaving(false); }
    };

    const openOrtakDuzenle = () => {
        if (!arsa) return;
        setOrtakDuzenleSatirlar(arsa.ortaklar.map(o => ({
            ortakID: o.ortakID,
            adSoyad: o.adSoyad,
            telefon: o.telefon ?? '',
            eposta: o.eposta ?? '',
            tcKimlik: o.tcKimlik ?? '',
            hisseMetre: o.hisseMetre !== null ? String(o.hisseMetre) : '',
            not: o.not ?? '',
            dirty: false,
        })));
        setOrtakDuzenleMod(true);
    };

    const updateOrtakDuzenleSatir = (ortakID: number, field: keyof OrtakDuzenleSatir, value: string) => {
        setOrtakDuzenleSatirlar(prev => prev.map(s =>
            s.ortakID === ortakID ? { ...s, [field]: value, dirty: true } : s
        ));
    };

    const saveOrtakDuzenle = async () => {
        const degisenler = ortakDuzenleSatirlar.filter(s => s.dirty);
        if (degisenler.length === 0) { setOrtakDuzenleMod(false); return; }
        setOrtakDuzenleSaving(true);
        try {
            for (const s of degisenler) {
                await axiosInstance.put(`/api/KatKarsiligArsa/${arsaId}/ortaklar/${s.ortakID}`, {
                    adSoyad: s.adSoyad, telefon: s.telefon || null, eposta: s.eposta || null,
                    tcKimlik: s.tcKimlik || null,
                    hisseMetre: s.hisseMetre !== '' ? parseFloat(s.hisseMetre) : null,
                    not: s.not || null,
                });
            }
            setOrtakDuzenleMod(false);
            fetchArsa();
        } catch { alert(t('msg.islemHatasi')); }
        finally { setOrtakDuzenleSaving(false); }
    };

    // ─── Blok ─────────────────────────────────────────────────────────────────

    const openBlokCreate = () => {
        setBlokEdit(null);
        setBlokForm({ blokAdi: '', katSayisi: '', en: '', boy: '', aciklama: '', zemin: false, bodrum: false });
        setModalError('');
        setBlokModal(true);
    };
    const openBlokEdit = (b: Blok) => {
        setBlokEdit(b);
        setBlokForm({ blokAdi: b.blokAdi, katSayisi: b.katSayisi !== null ? String(b.katSayisi) : '', en: b.en !== null ? String(b.en) : '', boy: b.boy !== null ? String(b.boy) : '', aciklama: b.aciklama ?? '', zemin: false, bodrum: false });
        setModalError('');
        setBlokModal(true);
    };
    const saveBlok = async () => {
        if (!blokForm.blokAdi.trim()) { setModalError(t('msg.zorunluAlanlar')); return; }
        setSaving(true); setModalError('');
        try {
            const payload = {
                blokAdi: blokForm.blokAdi,
                katSayisi: blokForm.katSayisi !== '' ? parseInt(blokForm.katSayisi) : null,
                en: blokForm.en !== '' ? parseInt(blokForm.en) : null,
                boy: blokForm.boy !== '' ? parseInt(blokForm.boy) : null,
                aciklama: blokForm.aciklama,
            };
            let blokID: number;
            if (blokEdit) {
                await axiosInstance.put(`/api/KatKarsiligArsa/${arsaId}/bloklar/${blokEdit.blokID}`, payload);
                blokID = blokEdit.blokID;
            } else {
                const res = await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/bloklar`, payload);
                blokID = res.data?.BlokID ?? res.data?.blokID ?? res.data?.id;

                // Yeni blok ise katları otomatik oluştur
                const katSayisi = blokForm.katSayisi !== '' ? parseInt(blokForm.katSayisi) : 0;
                if (blokID && (katSayisi > 0 || blokForm.bodrum || blokForm.zemin)) {
                    // Sıra numarası: bodrum=-1, zemin=0, 1.kat=1, 2.kat=2 ...
                    // SıraNo en küçükten büyüğe: bodrum → zemin → 1.kat → 2.kat ...
                    const katlar: { katAdi: string; siraNo: number }[] = [];
                    let sira = 1;
                    if (blokForm.bodrum) katlar.push({ katAdi: 'Bodrum', siraNo: sira++ });
                    if (blokForm.zemin)  katlar.push({ katAdi: 'Zemin Kat', siraNo: sira++ });
                    for (let i = 1; i <= katSayisi; i++) {
                        katlar.push({ katAdi: `${i}. Kat`, siraNo: sira++ });
                    }
                    for (const kat of katlar) {
                        await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/bloklar/${blokID}/katlar`, {
                            katAdi: kat.katAdi,
                            siraNo: kat.siraNo,
                            en: null,
                            boy: null,
                        });
                    }
                }
            }
            setBlokModal(false);
            fetchArsa();
        } catch (e: any) { setModalError(e?.response?.data || 'Hata'); }
        finally { setSaving(false); }
    };
    const deleteBlok = async (bId: number) => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        await axiosInstance.delete(`/api/KatKarsiligArsa/${arsaId}/bloklar/${bId}`);
        fetchArsa();
    };

    // ─── Kat ──────────────────────────────────────────────────────────────────

    const openInlineKatAdd = (blok: Blok) => {
        const sortedKatlar = [...blok.katlar].sort((a, b) => a.siraNo - b.siraNo);
        const lastKat = sortedKatlar[sortedKatlar.length - 1];
        const nextSiraNo = lastKat ? lastKat.siraNo + 1 : 1;
        const nextKatAdi = `${nextSiraNo}. Kat`;
        setInlineKatForm({
            katAdi: nextKatAdi,
            siraNo: String(nextSiraNo),
            en: blok.en !== null ? String(blok.en) : '',
            boy: blok.boy !== null ? String(blok.boy) : '',
        });
        setInlineKatBlokId(blok.blokID);
    };

    const saveInlineKat = async (blokId: number) => {
        if (!inlineKatForm.katAdi.trim()) return;
        setInlineKatSaving(true);
        try {
            const payload = {
                katAdi: inlineKatForm.katAdi,
                siraNo: parseInt(inlineKatForm.siraNo) || 0,
                en: inlineKatForm.en !== '' ? parseInt(inlineKatForm.en) : null,
                boy: inlineKatForm.boy !== '' ? parseInt(inlineKatForm.boy) : null,
            };
            await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/bloklar/${blokId}/katlar`, payload);
            setInlineKatBlokId(null);
            fetchArsa();
        } catch { }
        finally { setInlineKatSaving(false); }
    };

    const openKatEdit = (blokId: number, k: Kat) => {
        setKatBlokId(blokId);
        setKatEdit(k);
        setKatForm({ katAdi: k.katAdi, siraNo: String(k.siraNo), en: k.en !== null ? String(k.en) : '', boy: k.boy !== null ? String(k.boy) : '' });
        setModalError('');
        setKatModal(true);
    };
    const saveKat = async () => {
        if (!katForm.katAdi.trim() || katBlokId === null) { setModalError(t('msg.zorunluAlanlar')); return; }
        setSaving(true); setModalError('');
        try {
            const payload = { katAdi: katForm.katAdi, siraNo: parseInt(katForm.siraNo) || 0, en: katForm.en !== '' ? parseInt(katForm.en) : null, boy: katForm.boy !== '' ? parseInt(katForm.boy) : null };
            await axiosInstance.put(`/api/KatKarsiligArsa/${arsaId}/bloklar/${katBlokId}/katlar/${katEdit!.katID}`, payload);
            setKatModal(false);
            fetchArsa();
        } catch (e: any) { setModalError(e?.response?.data || 'Hata'); }
        finally { setSaving(false); }
    };
    const deleteKat = async (blokId: number, kId: number) => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        await axiosInstance.delete(`/api/KatKarsiligArsa/${arsaId}/bloklar/${blokId}/katlar/${kId}`);
        fetchArsa();
    };

    // ─── Cephe ────────────────────────────────────────────────────────────────

    const openCepheCreate = () => {
        setCepheEdit(null);
        setCepheForm({ cepheAdi: '', yon: '', aciklama: '' });
        setModalError('');
        setCepheModal(true);
    };
    const openCepheEdit = (c: Cephe) => {
        setCepheEdit(c);
        setCepheForm({ cepheAdi: c.cepheAdi, yon: c.yon ?? '', aciklama: c.aciklama ?? '' });
        setModalError('');
        setCepheModal(true);
    };
    const saveCephe = async () => {
        if (!cepheForm.cepheAdi.trim()) { setModalError(t('msg.zorunluAlanlar')); return; }
        setSaving(true); setModalError('');
        try {
            if (cepheEdit) {
                await axiosInstance.put(`/api/KatKarsiligArsa/${arsaId}/cepheler/${cepheEdit.cepheID}`, cepheForm);
            } else {
                await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/cepheler`, cepheForm);
            }
            setCepheModal(false);
            fetchArsa();
        } catch (e: any) { setModalError(e?.response?.data || 'Hata'); }
        finally { setSaving(false); }
    };
    const deleteCephe = async (cId: number) => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        await axiosInstance.delete(`/api/KatKarsiligArsa/${arsaId}/cepheler/${cId}`);
        fetchArsa();
    };

    // ─── Bölüm ────────────────────────────────────────────────────────────────

    const openBolumCreate = () => {
        setBolumEdit(null);
        setBolumForm({ blokID: '', katID: '', cephe1ID: '', cephe2ID: '', cephe3ID: '', cephe4ID: '', bolumAdi: '', bolumTipi: '', turu: '', m2: '', fiyat: '', birimFiyat: '', not: '' });
        setModalError('');
        setBolumModal(true);
    };
    const openBolumEdit = (b: Bolum) => {
        setBolumEdit(b);
        setBolumForm({
            blokID: b.blokID !== null ? String(b.blokID) : '',
            katID: b.katID !== null ? String(b.katID) : '',
            cephe1ID: b.cephe1ID !== null ? String(b.cephe1ID) : '',
            cephe2ID: b.cephe2ID !== null ? String(b.cephe2ID) : '',
            cephe3ID: b.cephe3ID !== null ? String(b.cephe3ID) : '',
            cephe4ID: b.cephe4ID !== null ? String(b.cephe4ID) : '',
            bolumAdi: b.bolumAdi,
            bolumTipi: b.bolumTipi ?? '',
            turu: b.turu ?? '',
            m2: String(b.m2),
            fiyat: String(b.fiyat),
            birimFiyat: b.m2 > 0 ? String(Math.round((b.fiyat / b.m2) * 100) / 100) : String(b.birimFiyat),
            not: b.not ?? ''
        });
        setModalError('');
        setBolumModal(true);
    };
    const saveBolum = async () => {
        if (!bolumForm.bolumAdi.trim()) { setModalError(t('msg.zorunluAlanlar')); return; }
        setSaving(true); setModalError('');
        try {
            const payload = {
                blokID: bolumForm.blokID !== '' ? parseInt(bolumForm.blokID) : null,
                katID: bolumForm.katID !== '' ? parseInt(bolumForm.katID) : null,
                cephe1ID: bolumForm.cephe1ID !== '' ? parseInt(bolumForm.cephe1ID) : null,
                cephe2ID: bolumForm.cephe2ID !== '' ? parseInt(bolumForm.cephe2ID) : null,
                cephe3ID: bolumForm.cephe3ID !== '' ? parseInt(bolumForm.cephe3ID) : null,
                cephe4ID: bolumForm.cephe4ID !== '' ? parseInt(bolumForm.cephe4ID) : null,
                bolumAdi: bolumForm.bolumAdi,
                bolumTipi: bolumForm.bolumTipi || null,
                turu: bolumForm.turu || null,
                m2: parseFloat(bolumForm.m2) || 0,
                fiyat: parseFloat(bolumForm.fiyat) || 0,
                birimFiyat: parseFloat(bolumForm.birimFiyat) || 0,
                not: bolumForm.not || null,
            };
            if (bolumEdit) {
                await axiosInstance.put(`/api/KatKarsiligArsa/${arsaId}/bolumler/${bolumEdit.bolumID}`, payload);
            } else {
                await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/bolumler`, payload);
            }
            setBolumModal(false);
            fetchArsa();
        } catch (e: any) { setModalError(e?.response?.data || 'Hata'); }
        finally { setSaving(false); }
    };
    const deleteBolum = async (bId: number) => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        await axiosInstance.delete(`/api/KatKarsiligArsa/${arsaId}/bolumler/${bId}`);
        fetchArsa();
    };

    // ─── Toplu Düzenleme ──────────────────────────────────────────────────────

    const openTopluDuzenle = () => {
        if (!arsa) return;
        const satirlar: TopluDuzenleSatir[] = arsa.bolumler.map(b => ({
            bolumID: b.bolumID,
            bolumAdi: b.bolumAdi,
            bolumTipi: b.bolumTipi ?? '',
            turu: b.turu ?? '',
            blokID: b.blokID !== null ? String(b.blokID) : '',
            katID: b.katID !== null ? String(b.katID) : '',
            cephe1ID: b.cephe1ID !== null ? String(b.cephe1ID) : '',
            cephe2ID: b.cephe2ID !== null ? String(b.cephe2ID) : '',
            cephe3ID: b.cephe3ID !== null ? String(b.cephe3ID) : '',
            cephe4ID: b.cephe4ID !== null ? String(b.cephe4ID) : '',
            m2: b.m2 ? String(b.m2) : '',
            fiyat: b.fiyat ? String(b.fiyat) : '',
            birimFiyat: b.birimFiyat ? String(b.birimFiyat) : '',
            dirty: false,
        }));
        setTopluDuzenleSatirlar(satirlar);
        setTopluDuzenleMod(true);
    };

    const updateDuzenleSatir = (bolumID: number, field: keyof TopluDuzenleSatir, value: string | boolean) => {
        setTopluDuzenleSatirlar(prev => prev.map(s => {
            if (s.bolumID !== bolumID) return s;
            const updated = { ...s, [field]: value, dirty: true };
            if (field === 'blokID') updated.katID = '';
            if ((field === 'm2' || field === 'fiyat') && updated.m2 && updated.fiyat) {
                const m2n = parseFloat(updated.m2);
                const fn = parseFloat(updated.fiyat);
                if (m2n > 0) updated.birimFiyat = String(Math.round(fn / m2n));
            }
            return updated;
        }));
    };

    const saveTopluDuzenle = async () => {
        if (!arsa) return;
        const degisenler = topluDuzenleSatirlar.filter(s => s.dirty);
        if (degisenler.length === 0) { setTopluDuzenleMod(false); return; }
        setTopluDuzenleSaving(true);
        try {
            for (const s of degisenler) {
                await axiosInstance.put(`/api/KatKarsiligArsa/${arsaId}/bolumler/${s.bolumID}`, {
                    blokID: s.blokID !== '' ? parseInt(s.blokID) : null,
                    katID: s.katID !== '' ? parseInt(s.katID) : null,
                    cephe1ID: s.cephe1ID !== '' ? parseInt(s.cephe1ID) : null,
                    cephe2ID: s.cephe2ID !== '' ? parseInt(s.cephe2ID) : null,
                    cephe3ID: s.cephe3ID !== '' ? parseInt(s.cephe3ID) : null,
                    cephe4ID: s.cephe4ID !== '' ? parseInt(s.cephe4ID) : null,
                    bolumAdi: s.bolumAdi,
                    bolumTipi: s.bolumTipi || null,
                    turu: s.turu || null,
                    m2: parseFloat(s.m2) || 0,
                    fiyat: parseFloat(s.fiyat) || 0,
                    birimFiyat: parseFloat(s.birimFiyat) || 0,
                });
            }
            setTopluDuzenleMod(false);
            fetchArsa();
        } catch {
            alert(t('msg.islemHatasi'));
        } finally {
            setTopluDuzenleSaving(false);
        }
    };

    // ─── Toplu Bölüm Ekleme Wizard ────────────────────────────────────────────

    const fmtBinlik = (val: string) => {
        if (val === '' || val === null) return '';
        const n = parseFloat(val);
        if (isNaN(n)) return val;
        return n.toLocaleString('tr-TR', { maximumFractionDigits: 2 });
    };
    const parseBinlik = (display: string) => display.replace(/\./g, '').replace(',', '.');

    const openTopluBolumModal = () => {
        setTopluWizard({ blokID: '', basKatSiraNo: '1', kattakiDaireSayisi: '4', baslamaDaireNo: '1', eklenecekSayi: '8', turu: '', bolumTipi: '', m2: '', fiyat: '' });
        setTopluListesi([]);
        setTopluBolumModal(true);
    };

    const generateTopluListesi = () => {
        if (!arsa) return;
        const blokID = parseInt(topluWizard.blokID);
        if (!blokID) { alert(t('msg.islemHatasi')); return; }
        const blok = arsa.bloklar.find(b => b.blokID === blokID);
        if (!blok) return;

        const sortedKatlar = [...blok.katlar].sort((a, b) => a.siraNo - b.siraNo);
        const basKat = parseInt(topluWizard.basKatSiraNo) || 1;
        const perFloor = Math.max(1, parseInt(topluWizard.kattakiDaireSayisi) || 1);
        const total = Math.max(1, parseInt(topluWizard.eklenecekSayi) || 1);
        const startNo = parseInt(topluWizard.baslamaDaireNo) || 1;

        // Mevcut bölüm adları (aynı blokta)
        const mevcutAdlar = new Set(
            arsa.bolumler.filter(b => b.blokID === blokID).map(b => b.bolumAdi.trim().toLowerCase())
        );

        const fiyat = topluWizard.fiyat;
        const m2Str = topluWizard.m2;
        const birimFiyat = (parseFloat(fiyat) > 0 && parseFloat(m2Str) > 0)
            ? String(Math.round((parseFloat(fiyat) / parseFloat(m2Str)) * 100) / 100)
            : '';

        const liste: TopluBolumSatir[] = [];
        for (let i = 0; i < total; i++) {
            const daireNo = startNo + i;
            const katSiraNo = basKat + Math.floor(i / perFloor);
            const kat = sortedKatlar.find(k => k.siraNo === katSiraNo) ?? sortedKatlar[sortedKatlar.length - 1] ?? null;
            const bolumAdi = String(daireNo);
            liste.push({
                id: `t-${i}`,
                blokID,
                katID: kat?.katID ?? null,
                bolumAdi,
                turu: topluWizard.turu,
                m2: m2Str,
                fiyat,
                birimFiyat,
                bolumTipi: topluWizard.bolumTipi,
                cephe1ID: '',
                not: '',
                isDuplicate: mevcutAdlar.has(bolumAdi.trim().toLowerCase()),
                silindi: false,
            });
        }
        setTopluListesi(liste);
    };

    const updateTopluSatir = (id: string, field: keyof TopluBolumSatir, value: string | number | boolean | null) => {
        setTopluListesi(prev => prev.map(s => {
            if (s.id !== id) return s;
            const updated = { ...s, [field]: value };
            // birim fiyat otomatik hesapla
            if (field === 'm2' || field === 'fiyat') {
                const m2 = parseFloat(field === 'm2' ? String(value) : updated.m2);
                const f = parseFloat(field === 'fiyat' ? String(value) : updated.fiyat);
                updated.birimFiyat = (m2 > 0 && f > 0) ? String(Math.round((f / m2) * 100) / 100) : updated.birimFiyat;
            }
            return updated;
        }));
    };

    const saveTopluBolumler = async () => {
        if (!arsa) return;
        const eklenecekler = topluListesi.filter(s => !s.silindi);
        if (eklenecekler.length === 0) { alert(t('msg.islemHatasi')); return; }
        setTopluSaving(true);
        try {
            for (const s of eklenecekler) {
                await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/bolumler`, {
                    blokID: s.blokID || null,
                    katID: s.katID || null,
                    cephe1ID: s.cephe1ID ? parseInt(s.cephe1ID) : null,
                    cephe2ID: null, cephe3ID: null, cephe4ID: null,
                    bolumAdi: s.bolumAdi,
                    bolumTipi: s.bolumTipi || null,
                    turu: s.turu || null,
                    m2: parseFloat(s.m2) || 0,
                    fiyat: parseFloat(s.fiyat) || 0,
                    birimFiyat: parseFloat(s.birimFiyat) || 0,
                    not: s.not || null,
                });
            }
            setTopluBolumModal(false);
            fetchArsa();
        } catch (e: any) {
            alert(t('msg.islemHatasi'));
        } finally {
            setTopluSaving(false);
        }
    };

    // ─── Paylaşım ─────────────────────────────────────────────────────────────

    const openPaylasimCreate = (bolumId: number, prefill?: { tip: 'ortak' | 'muteahhit'; id: number }) => {
        setPaylasimBolumId(bolumId);
        setPaylasimEdit(null);
        setPaylasimForm({
            ortakID: prefill?.tip === 'ortak' ? String(prefill.id) : '',
            muteahhitID: prefill?.tip === 'muteahhit' ? String(prefill.id) : '',
            oranYuzde: '',
            aciklamaNot: '',
        });
        setModalError('');
        setPaylasimSatirDuzen({});
        setPaylasimModal(true);
    };
    // Paylaşım modalı her zaman bölümün mevcut paylaşımlarını (düzenle/sil) ve yeni ekleme formunu birlikte gösterir;
    // "düzenle" ile açılınca ilgili satır doğrudan düzenleme modunda gelir.
    const openPaylasimEdit = (bolumId: number, p: Paylasim) => {
        openPaylasimCreate(bolumId);
        setPaylasimSatirDuzen({ [p.paylasimID]: { oran: String(p.oranYuzde), not: p.aciklamaNot ?? '' } });
    };

    const hataMetni = (e: any) =>
        typeof e?.response?.data === 'string' ? e.response.data : (e?.response?.data?.message || t('msg.islemHatasi'));

    /** Mevcut bir paylaşımın oranını / notunu günceller. */
    const paylasimGuncelle = async (bolumId: number, p: Paylasim, oran: number, not: string | null): Promise<boolean> => {
        setPaylasimIslemde(p.paylasimID);
        try {
            await axiosInstance.put(`/api/KatKarsiligArsa/${arsaId}/bolumler/${bolumId}/paylasimlar/${p.paylasimID}`, {
                ortakID: p.ortakID, muteahhitID: p.muteahhitID, oranYuzde: oran, aciklamaNot: not,
            });
            await arsaYenile();
            return true;
        } catch (e: any) {
            alert(hataMetni(e));
            return false;
        } finally {
            setPaylasimIslemde(null);
        }
    };

    /** Paylaşımı iptal eder (siler); bölümün o kısmı yeniden dağıtılabilir hale gelir. */
    const paylasimIptal = async (bolumId: number, p: Paylasim, kisiAd: string, bolumAd: string): Promise<boolean> => {
        if (!window.confirm(`${kisiAd} kişisinin ${bolumAd} bölümündeki %${p.oranYuzde} paylaşımı iptal edilsin mi?`)) return false;
        setPaylasimIslemde(p.paylasimID);
        try {
            await axiosInstance.delete(`/api/KatKarsiligArsa/${arsaId}/bolumler/${bolumId}/paylasimlar/${p.paylasimID}`);
            await arsaYenile();
            return true;
        } catch (e: any) {
            alert(hataMetni(e));
            return false;
        } finally {
            setPaylasimIslemde(null);
        }
    };

    /** Dağıtım önerisindeki bir satırı paylaşım olarak kaydeder. */
    const oneriUygula = async (b: BolumOneri, o: OneriKisi): Promise<boolean> => {
        const anahtar = `${b.bolumID}-${o.tip}-${o.id}`;
        setOneriUygulaniyor(anahtar);
        try {
            await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/bolumler/${b.bolumID}/paylasimlar`, {
                ortakID: o.tip === 'Ortak' ? o.id : null,
                muteahhitID: o.tip === 'Ortak' ? null : o.id,
                oranYuzde: Math.round(o.oneriOran * 10000) / 10000,
                aciklamaNot: 'Dağıtım önerisinden uygulandı',
            });
            setOneriUygulanan(prev => new Set(prev).add(anahtar));
            await arsaYenile();
            return true;
        } catch (e: any) {
            alert(`${b.bolumAdi} – ${o.adSoyad}: ${hataMetni(e)}`);
            return false;
        } finally {
            setOneriUygulaniyor(null);
        }
    };

    const bolumOnerileriniUygula = async (b: BolumOneri) => {
        const bekleyen = b.oneriler.filter(o => !oneriUygulanan.has(`${b.bolumID}-${o.tip}-${o.id}`));
        if (bekleyen.length === 0) return;
        if (!window.confirm(`${b.bolumAdi} için ${bekleyen.length} öneri paylaşım olarak kaydedilsin mi?`)) return;
        for (const o of bekleyen) {
            if (!(await oneriUygula(b, o))) break;
        }
    };
    const savePaylasim = async () => {
        if (paylasimBolumId === null) return;
        const oran = parseFloat(paylasimForm.oranYuzde);
        if (isNaN(oran) || oran <= 0) {
            setModalError(t('kka.paylasimOraniHatasi'));
            return;
        }
        setSaving(true); setModalError('');
        try {
            const payload = {
                ortakID: paylasimForm.ortakID !== '' ? parseInt(paylasimForm.ortakID) : null,
                muteahhitID: paylasimForm.muteahhitID !== '' ? parseInt(paylasimForm.muteahhitID) : null,
                oranYuzde: parseFloat(paylasimForm.oranYuzde) || 0,
                aciklamaNot: paylasimForm.aciklamaNot || null,
            };
            await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/bolumler/${paylasimBolumId}/paylasimlar`, payload);
            setPaylasimForm({ ortakID: '', muteahhitID: '', oranYuzde: '', aciklamaNot: '' });
            await arsaYenile();
        } catch (e: any) { setModalError(hataMetni(e)); }
        finally { setSaving(false); }
    };
    const deletePaylasim = async (bolumId: number, pId: number) => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        await axiosInstance.delete(`/api/KatKarsiligArsa/${arsaId}/bolumler/${bolumId}/paylasimlar/${pId}`);
        fetchArsa();
    };

    const fetchOneri = async () => {
        setOneriLoading(true);
        try {
            const res = await axiosInstance.get(`/api/KatKarsiligArsa/${arsaId}/paylasim-onerileri`);
            setOneriData(res.data);
            setOneriUygulanan(new Set());
            setOneriModal(true);
        } catch {
            alert(t('msg.islemHatasi'));
        } finally {
            setOneriLoading(false);
        }
    };

    // ── Ortak rapor başlığı oluştur ──────────────────────────────────────────
    const pdfHeader = (doc: jsPDF, baslik: string) => {
        if (!arsa) return 36;
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 32;
        let y = 36;

        // Başlık bandı
        doc.setFillColor(26, 35, 64);
        doc.rect(0, 0, pageWidth, 52, 'F');
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text(tr2pdf(baslik), margin, 32);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(new Date().toLocaleDateString('tr-TR'), pageWidth - margin, 32, { align: 'right' });
        doc.setTextColor(0);
        y = 64;

        // Arsa bilgi kutusu
        doc.setFillColor(245, 247, 252);
        doc.setDrawColor(200, 212, 235);
        doc.roundedRect(margin, y, pageWidth - margin * 2, 46, 4, 4, 'FD');

        const firmalar = arsa.muteahhitler.length > 0 ? arsa.muteahhitler.map(m => tr2pdf(m.adSoyad)).join(' / ') : '';
        const adaParsel = `Ada: ${arsa.ada ?? '-'}  Parsel: ${arsa.parsel ?? '-'}`;
        const toplamM2 = arsa.arsaMetrekare != null ? `Toplam: ${fmt(arsa.arsaMetrekare)} m2` : '';

        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'bold');
        if (firmalar) doc.text(tr2pdf(`Firma: ${firmalar}`), margin + 10, y + 14);
        doc.setFont('helvetica', 'normal');
        doc.text(tr2pdf(adaParsel), margin + 10, y + 26);
        doc.text(tr2pdf(`Takip No: ${arsa.takipNo}`), margin + 10, y + 38);
        if (toplamM2) doc.text(toplamM2, pageWidth - margin - 10, y + 26, { align: 'right' });
        y += 56;

        // Turuncu ayırıcı çizgi
        doc.setDrawColor(230, 126, 34);
        doc.setLineWidth(1.5);
        doc.line(margin, y, pageWidth - margin, y);
        doc.setLineWidth(0.5);
        doc.setDrawColor(0);
        y += 12;
        return y;
    };

    const pdfFooter = (doc: jsPDF) => {
        const pageCount = (doc.internal as any).getNumberOfPages ? (doc.internal as any).getNumberOfPages() : 1;
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFillColor(26, 35, 64);
            doc.rect(0, pageHeight - 24, pageWidth, 24, 'F');
            doc.setFontSize(7.5);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(200, 210, 230);
            doc.text('Yapi CRM Sistemi Kullanilarak olusturulmustur', 32, pageHeight - 9);
            doc.text(`Sayfa ${i} / ${pageCount}`, pageWidth - 32, pageHeight - 9, { align: 'right' });
            doc.setTextColor(0);
        }
    };

    const exportOrtaklarPdf = () => {
        if (!arsa) return;
        const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
        const margin = 32;
        const y = pdfHeader(doc, 'Arsa Ortaklari Raporu');

        autoTable(doc, {
            startY: y,
            margin: { left: margin, right: margin },
            head: [['Ad Soyad', 'TC Kimlik', 'Telefon', 'Hisse m\u00b2', 'Hisse (%)', 'Pay (TL)', 'Aldigi Pay (TL)', 'Not']],
            body: arsa.ortaklar.map(o => [
                tr2pdf(o.adSoyad),
                o.tcKimlik ?? '-',
                o.telefon ?? '-',
                o.hisseMetre != null ? `${o.hisseMetre.toLocaleString('tr-TR')} m2` : '-',
                `%${o.hisseOrani.toLocaleString('tr-TR')}`,
                o.payTL != null ? `${fmt(o.payTL)} TL` : '-',
                o.aldigiPayTL != null ? `${fmt(o.aldigiPayTL)} TL` : '-',
                tr2pdf(o.not),
            ]),
            headStyles: { fillColor: [26, 35, 64], textColor: 255, fontStyle: 'bold', fontSize: 8 },
            alternateRowStyles: { fillColor: [245, 247, 252] },
            bodyStyles: { fontSize: 8 },
            didDrawPage: () => { /* footers drawn after */ },
        });

        pdfFooter(doc);
        const takip = arsa.takipNo ? `_${arsa.takipNo}` : '';
        doc.save(`arsa-ortaklari${takip}-${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    const exportGayrimenkullerPdf = () => {
        if (!arsa) return;
        const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
        const margin = 32;
        const y = pdfHeader(doc, 'Gayrimenkuller Raporu');

        autoTable(doc, {
            startY: y,
            margin: { left: margin, right: margin },
            head: [['Bolum Adi', 'Tip', 'Turu', 'Blok', 'Kat', 'Cepheler', 'm\u00b2', 'Fiyat (TL)', 'Birim Fiyat (TL)']],
            body: arsa.bolumler.map(b => {
                const cepheler = [b.cephe1, b.cephe2, b.cephe3, b.cephe4]
                    .filter(Boolean).map(c => tr2pdf(c!.cepheAdi)).join(', ') || '-';
                return [
                    tr2pdf(b.bolumAdi),
                    tr2pdf(b.bolumTipi),
                    tr2pdf(b.turu),
                    b.blok ? tr2pdf(b.blok.blokAdi) : '-',
                    b.kat ? tr2pdf(b.kat.katAdi) : '-',
                    cepheler,
                    `${b.m2.toLocaleString('tr-TR')} m2`,
                    `${fmt(b.fiyat)} TL`,
                    `${fmt(b.birimFiyat)} TL`,
                ];
            }),
            headStyles: { fillColor: [26, 35, 64], textColor: 255, fontStyle: 'bold', fontSize: 8 },
            alternateRowStyles: { fillColor: [245, 247, 252] },
            bodyStyles: { fontSize: 8 },
        });

        pdfFooter(doc);
        const takip = arsa.takipNo ? `_${arsa.takipNo}` : '';
        doc.save(`gayrimenkuller${takip}-${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    const exportPaylasimlarPdf = () => {
        if (!arsa) return;
        const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 32;
        let y = pdfHeader(doc, 'Bolum Paylasim Listesi');

        arsa.bolumler.forEach((b, idx) => {
            // Sayfaya sığmıyorsa yeni sayfa
            if (y > pageHeight - 80) { doc.addPage(); y = 44; }

            const cepheler = [b.cephe1, b.cephe2, b.cephe3, b.cephe4]
                .filter(Boolean).map(c => tr2pdf(c!.cepheAdi)).join(', ') || '-';
            const toplamOran = b.paylasimlar.reduce((acc, p) => acc + p.oranYuzde, 0);
            const tamamlandi = toplamOran >= 100;

            // Bölüm başlık bandı — başlık + meta ikinci satır
            const bandColor: [number, number, number] = tamamlandi ? [39, 174, 96] : [26, 35, 64];
            const metaParts: string[] = [];
            if (b.blok) metaParts.push(`Blok: ${tr2pdf(b.blok.blokAdi)}`);
            if (b.kat)  metaParts.push(`Kat: ${tr2pdf(b.kat.katAdi)}`);
            if (cepheler !== '-') metaParts.push(`Cephe: ${cepheler}`);
            metaParts.push(`${b.m2.toLocaleString('tr-TR')} m2`);
            metaParts.push(`${fmt(b.fiyat)} TL`);
            const bandH = metaParts.length > 0 ? 44 : 28;

            doc.setFillColor(...bandColor);
            doc.roundedRect(margin, y, pageWidth - margin * 2, bandH, 3, 3, 'F');

            // Başlık satırı
            doc.setFontSize(9.5);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(255, 255, 255);
            const bolumBaslik = tr2pdf(`${idx + 1}. ${b.bolumAdi}${b.bolumTipi ? ' - ' + b.bolumTipi : ''}`);
            doc.text(bolumBaslik, margin + 8, y + 17);
            const durumText = tamamlandi ? 'Tamamlandi' : `%${toplamOran.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} Dagitildi`;
            doc.setFontSize(8);
            doc.text(durumText, pageWidth - margin - 8, y + 17, { align: 'right' });

            // Meta satırı (band içinde, hafif saydam)
            if (metaParts.length > 0) {
                doc.setFontSize(7.5);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(tamamlandi ? 210 : 180, tamamlandi ? 240 : 200, tamamlandi ? 225 : 235);
                doc.text(metaParts.join('   |   '), margin + 8, y + 33);
            }

            doc.setTextColor(0);
            y += bandH + 6;

            // Paylaşım tablosu
            if (b.paylasimlar.length > 0) {
                autoTable(doc, {
                    startY: y,
                    margin: { left: margin + 8, right: margin },
                    head: [['Kisi / Kurum', 'Tur', 'Oran (%)', 'Pay (TL)', 'Not']],
                    body: b.paylasimlar.map(p => [
                        tr2pdf(p.ortak?.adSoyad ?? p.muteahhit?.adSoyad ?? '-'),
                        p.ortak ? 'Arsa Ortagi' : p.muteahhit ? 'Muteahhit' : '-',
                        `%${p.oranYuzde.toLocaleString('tr-TR')}`,
                        `${fmt(b.fiyat * p.oranYuzde / 100)} TL`,
                        tr2pdf(p.aciklamaNot),
                    ]),
                    headStyles: { fillColor: [230, 126, 34], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
                    alternateRowStyles: { fillColor: tamamlandi ? [242, 250, 245] : [245, 247, 252] },
                    bodyStyles: { fontSize: 7.8 },
                    foot: [['', 'TOPLAM', `%${toplamOran.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`, `${fmt(b.fiyat * toplamOran / 100)} TL`, '']],
                    footStyles: { fillColor: [240, 242, 248], textColor: [26, 35, 64], fontStyle: 'bold', fontSize: 7.8 },
                });
                y = (doc as any).lastAutoTable.finalY + 6;
            } else {
                doc.setFontSize(7.8);
                doc.setFont('helvetica', 'italic');
                doc.setTextColor(160);
                doc.text('Henuz paylasim yapilmamis.', margin + 8, y + 8);
                doc.setTextColor(0);
                doc.setFont('helvetica', 'normal');
                y += 16;
            }

            y += 10;
        });

        pdfFooter(doc);
        const takip = arsa.takipNo ? `_${arsa.takipNo}` : '';
        doc.save(`bolum-paylasimlar${takip}-${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    const exportBlokGorunumPdf = (blokId: number) => {
        if (!arsa) return;
        const blok = arsa.bloklar.find(b => b.blokID === blokId);
        if (!blok) return;
        const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
        const pageWidth  = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 28;
        let y = pdfHeader(doc, tr2pdf(`Blok Gorunum Raporu - ${blok.blokAdi}`));

        const katlar = [...blok.katlar].sort((a, b) => b.siraNo - a.siraNo);
        if (katlar.length === 0) {
            doc.setFontSize(9); doc.setTextColor(150);
            doc.text('Bu blokta kat tanimli degil.', margin, y + 14);
            pdfFooter(doc);
            doc.save(`blok-gorunum_${tr2pdf(blok.blokAdi)}-${new Date().toISOString().slice(0,10)}.pdf`);
            return;
        }

        // Sütun genişlikleri: kat etiketi sabit, geri kalanı bölümlere bölünür
        const katLblW = 70;
        const contentW = pageWidth - margin * 2 - katLblW;
        const rowH = 36;  // her kat satırının yüksekliği
        const cellGap = 5;

        katlar.forEach(kat => {
            const bolumlerInKat = arsa.bolumler.filter(b => b.katID === kat.katID);

            // Sayfa taşma kontrolü
            if (y + rowH + 4 > pageHeight - 30) { doc.addPage(); y = 36; }

            // Kat etiketi (lacivert)
            doc.setFillColor(26, 35, 64);
            doc.roundedRect(margin, y, katLblW, rowH, 2, 2, 'F');
            doc.setFontSize(8.5);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(255, 255, 255);
            doc.text(tr2pdf(kat.katAdi), margin + katLblW / 2, y + rowH / 2 + 3, { align: 'center' });
            doc.setTextColor(0);

            if (bolumlerInKat.length === 0) {
                // Boş kat
                doc.setFillColor(248, 249, 252);
                doc.setDrawColor(220, 226, 240);
                doc.rect(margin + katLblW + cellGap, y, contentW - cellGap, rowH, 'FD');
                doc.setFontSize(7.5); doc.setTextColor(180);
                doc.text('–', margin + katLblW + cellGap + 10, y + rowH / 2 + 2.5);
                doc.setTextColor(0);
            } else {
                // Bölüm hücreleri — eşit genişlikte yan yana
                const cellCount = bolumlerInKat.length;
                const cellW = (contentW - cellGap - (cellCount - 1) * 3) / cellCount;
                bolumlerInKat.forEach((b, ci) => {
                    const cx = margin + katLblW + cellGap + ci * (cellW + 3);
                    // Hücre çerçevesi
                    doc.setFillColor(252, 253, 255);
                    doc.setDrawColor(200, 210, 230);
                    doc.roundedRect(cx, y, cellW, rowH, 2, 2, 'FD');
                    // Bölüm adı
                    doc.setFontSize(7.5);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(26, 35, 64);
                    const name = tr2pdf(b.bolumAdi);
                    doc.text(name, cx + cellW / 2, y + 11, { align: 'center', maxWidth: cellW - 4 });
                    // Tip
                    if (b.bolumTipi) {
                        doc.setFontSize(6.5);
                        doc.setFont('helvetica', 'normal');
                        doc.setTextColor(120);
                        doc.text(tr2pdf(b.bolumTipi), cx + cellW / 2, y + 20, { align: 'center', maxWidth: cellW - 4 });
                    }
                    // m2
                    doc.setFontSize(6.8);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(80);
                    doc.text(`${b.m2.toLocaleString('tr-TR')} m2`, cx + cellW / 2, y + rowH - 7, { align: 'center', maxWidth: cellW - 4 });
                    doc.setTextColor(0);
                });
            }

            y += rowH + 4;
        });

        pdfFooter(doc);
        doc.save(`blok-gorunum_${tr2pdf(blok.blokAdi)}-${new Date().toISOString().slice(0,10)}.pdf`);
    };

    const exportBlokGorunumPayliPdf = (blokId: number) => {
        if (!arsa) return;
        const blok = arsa.bloklar.find(b => b.blokID === blokId);
        if (!blok) return;
        const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
        const pageWidth  = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin  = 28;
        let y = pdfHeader(doc, tr2pdf(`Blok Paylasim Raporu - ${blok.blokAdi}`));

        const katlar = [...blok.katlar].sort((a, b) => b.siraNo - a.siraNo);
        if (katlar.length === 0) {
            doc.setFontSize(9); doc.setTextColor(150);
            doc.text('Bu blokta kat tanimli degil.', margin, y + 14);
            pdfFooter(doc);
            doc.save(`blok-paylasim_${tr2pdf(blok.blokAdi)}-${new Date().toISOString().slice(0,10)}.pdf`);
            return;
        }

        const katLblW = 70;
        const contentW = pageWidth - margin * 2 - katLblW;
        const cellGapX = 4;
        const rowGapY  = 5;
        const topPad = 7, bottomPad = 6;
        const lineH  = 9;

        // Computes required height (in pt) for a single cell
        const getCellHeight = (b: Bolum): number => {
            const hasCephe = [b.cephe1, b.cephe2, b.cephe3, b.cephe4].some(c => c !== null);
            const payCount = b.paylasimlar.length;
            const hasPay   = payCount > 0;
            let h = topPad + bottomPad;
            h += lineH;             // bolümAdı
            h += lineH;             // tip + m²
            if (hasCephe) h += lineH; // cepheler satırı
            if (hasPay)   h += 6;   // ayraç çizgisi boşluğu
            h += payCount * lineH;  // her paylaşım satırı
            if (hasPay)   h += lineH; // toplam satırı
            return h;
        };

        katlar.forEach(kat => {
            const bolumlerInKat = arsa.bolumler.filter(b => b.katID === kat.katID);

            const maxCellH = bolumlerInKat.length > 0
                ? Math.max(...bolumlerInKat.map(getCellHeight))
                : topPad + lineH + bottomPad;
            const rowH = Math.max(38, maxCellH);

            // Sayfa taşma kontrolü
            if (y + rowH + rowGapY > pageHeight - 30) { doc.addPage(); y = 36; }

            // Kat etiketi (lacivert)
            doc.setFillColor(26, 35, 64);
            doc.roundedRect(margin, y, katLblW, rowH, 2, 2, 'F');
            doc.setFontSize(8.5); doc.setFont('helvetica', 'bold');
            doc.setTextColor(255, 255, 255);
            doc.text(tr2pdf(kat.katAdi), margin + katLblW / 2, y + rowH / 2 + 3, { align: 'center' });
            doc.setTextColor(0);

            if (bolumlerInKat.length === 0) {
                doc.setFillColor(248, 249, 252);
                doc.setDrawColor(220, 226, 240);
                doc.rect(margin + katLblW + cellGapX, y, contentW - cellGapX, rowH, 'FD');
                doc.setFontSize(7.5); doc.setTextColor(180);
                doc.text('\u2013', margin + katLblW + cellGapX + 10, y + rowH / 2 + 2.5);
                doc.setTextColor(0);
            } else {
                const cellCount = bolumlerInKat.length;
                const cellW = (contentW - cellGapX - (cellCount - 1) * 3) / cellCount;

                bolumlerInKat.forEach((b, ci) => {
                    const cx = margin + katLblW + cellGapX + ci * (cellW + 3);
                    const cepheler = [b.cephe1, b.cephe2, b.cephe3, b.cephe4]
                        .filter(c => c !== null)
                        .map(c => c!.cepheAdi);
                    const toplamOran = b.paylasimlar.reduce((s, p) => s + p.oranYuzde, 0);
                    const tamamlandi = toplamOran >= 100;
                    const hasPay = b.paylasimlar.length > 0;

                    // Hücre arka planı
                    if (tamamlandi) {
                        doc.setFillColor(232, 249, 240);
                        doc.setDrawColor(39, 174, 96);
                    } else if (hasPay) {
                        doc.setFillColor(255, 251, 245);
                        doc.setDrawColor(230, 160, 60);
                    } else {
                        doc.setFillColor(252, 253, 255);
                        doc.setDrawColor(200, 210, 230);
                    }
                    doc.setLineWidth(tamamlandi ? 1.2 : 0.5);
                    doc.roundedRect(cx, y, cellW, rowH, 2, 2, 'FD');
                    doc.setLineWidth(0.4);

                    let ly = y + topPad;

                    // Bölüm adı
                    doc.setFontSize(8); doc.setFont('helvetica', 'bold');
                    doc.setTextColor(
                        tamamlandi ? 21 : 26,
                        tamamlandi ? 115 : 35,
                        tamamlandi ? 55  : 64
                    );
                    doc.text(tr2pdf(b.bolumAdi), cx + cellW / 2, ly + 7, { align: 'center', maxWidth: cellW - 4 });
                    ly += lineH;

                    // Tip + m²
                    doc.setFontSize(6.5); doc.setFont('helvetica', 'normal');
                    doc.setTextColor(100);
                    const tipM2 = [b.bolumTipi ? tr2pdf(b.bolumTipi) : null, `${b.m2.toLocaleString('tr-TR')} m2`]
                        .filter(Boolean).join('  |  ');
                    doc.text(tipM2, cx + cellW / 2, ly + 7, { align: 'center', maxWidth: cellW - 4 });
                    ly += lineH;

                    // Cepheler
                    if (cepheler.length > 0) {
                        doc.setFontSize(6.5);
                        doc.setTextColor(tamamlandi ? 39 : 100, tamamlandi ? 160 : 120, tamamlandi ? 90 : 155);
                        doc.text(tr2pdf(cepheler.join(', ')), cx + cellW / 2, ly + 7, { align: 'center', maxWidth: cellW - 4 });
                        ly += lineH;
                    }

                    // Paylaşımlar
                    if (hasPay) {
                        // Ayraç çizgisi
                        doc.setDrawColor(tamamlandi ? 39 : 200, tamamlandi ? 174 : 210, tamamlandi ? 96 : 230);
                        doc.line(cx + 5, ly + 3, cx + cellW - 5, ly + 3);
                        ly += 6;

                        b.paylasimlar.forEach(p => {
                            const kisi = tr2pdf(p.ortak?.adSoyad ?? p.muteahhit?.adSoyad ?? '?');
                            doc.setFontSize(6.5); doc.setFont('helvetica', 'normal');
                            doc.setTextColor(
                                tamamlandi ? 28  : 55,
                                tamamlandi ? 130 : 70,
                                tamamlandi ? 65  : 100
                            );
                            doc.text(`${kisi}  %${p.oranYuzde}`, cx + cellW / 2, ly + 7, { align: 'center', maxWidth: cellW - 4 });
                            ly += lineH;
                        });

                        // Toplam satırı
                        doc.setFontSize(6.5); doc.setFont('helvetica', 'bold');
                        doc.setTextColor(
                            tamamlandi ? 39  : 100,
                            tamamlandi ? 174 : 100,
                            tamamlandi ? 96  : 100
                        );
                        doc.text(
                            `Toplam: %${toplamOran.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`,
                            cx + cellW / 2, ly + 7,
                            { align: 'center', maxWidth: cellW - 4 }
                        );
                    }

                    doc.setTextColor(0);
                });
            }

            y += rowH + rowGapY;
        });

        pdfFooter(doc);
        doc.save(`blok-paylasim_${tr2pdf(blok.blokAdi)}-${new Date().toISOString().slice(0,10)}.pdf`);
    };

    const exportVaziyetPlanPdf = async (blokId: number) => {
        if (!arsa) return;
        const blok = arsa.bloklar.find(b => b.blokID === blokId);
        if (!blok) return;

        const katlar = [...blok.katlar].sort((a, b) => b.siraNo - a.siraNo);

        type AnyShape = {
            id: number; tool: string;
            x1: number; y1: number; x2: number; y2: number;
            color: string; filled: boolean; lineWidth: number;
            text?: string; fontSize?: number; fontBold?: boolean; fontItalic?: boolean;
            imageData?: string;
        };

        // Tüm katların çizimlerini paralel çek
        const shapesMap = new Map<number, AnyShape[]>();
        await Promise.all(katlar.map(async kat => {
            try {
                const res = await axiosInstance.get<AnyShape[]>(`/api/KatCizim/${kat.katID}`);
                shapesMap.set(kat.katID, res.data ?? []);
            } catch { shapesMap.set(kat.katID, []); }
        }));

        // Pusula görselini base64 olarak yükle
        const loadImgBase64 = (src: string): Promise<string> => new Promise(resolve => {
            const img = new Image();
            img.onload = () => {
                const c = document.createElement('canvas');
                c.width = img.width; c.height = img.height;
                c.getContext('2d')!.drawImage(img, 0, 0);
                resolve(c.toDataURL('image/png'));
            };
            img.onerror = () => resolve('');
            img.crossOrigin = 'anonymous';
            img.src = src;
        });
        const compassBase64 = await loadImgBase64(yonlerImg);

        // �?ekilleri ve pin'leri doğrudan PDF koordinatlarına çizer.
        // �?ekiller [0,1] normalize koordinatlarda saklanır;
        // Pin'ler doğrudan yüzde bazlı → dx + xVal*dw, dy + yVal*dh
        const renderInPdf = (
            shapes: AnyShape[], bolumlerInKat: Bolum[],
            dx: number, dy: number, dw: number, dh: number
        ) => {
            // �?ekil koordinatları [0,1] normalize — doğrudan dw/dh ile çarp
            // lineWidth/fontSize için referans ölçek (stil amaçlı)
            const REF = 800;
            const sx = dw / REF;
            const sy = dh / REF;

            // Beyaz zemin
            doc.setFillColor(255, 255, 255);
            doc.rect(dx, dy, dw, dh, 'F');

            // Izgara
            doc.setDrawColor(232, 237, 244); doc.setLineWidth(0.25);
            const GSX = 24 * sx;
            const GSY = 24 * sy;
            for (let gx = dx; gx <= dx + dw + 0.5; gx += GSX) doc.line(gx, dy, gx, dy + dh);
            for (let gy = dy; gy <= dy + dh + 0.5; gy += GSY) doc.line(dx, gy, dx + dw, gy);

            // �?ekiller
            shapes.forEach(s => {
                if (s.tool === 'image') {
                    // Resim şekillerini PNG olarak ekle
                    if (s.imageData) {
                        try {
                            doc.addImage(s.imageData, 'PNG',
                                dx + Math.min(s.x1, s.x2) * dw,
                                dy + Math.min(s.y1, s.y2) * dh,
                                Math.abs(s.x2 - s.x1) * dw,
                                Math.abs(s.y2 - s.y1) * dh
                            );
                        } catch { /* ignore */ }
                    }
                    return;
                }
                const x1 = dx + s.x1 * dw;
                const y1 = dy + s.y1 * dh;
                const x2 = dx + s.x2 * dw;
                const y2 = dy + s.y2 * dh;
                const w  = Math.abs(x2 - x1);
                const h  = Math.abs(y2 - y1);
                const lx = Math.min(x1, x2);
                const ly = Math.min(y1, y2);
                const lw = Math.max(s.lineWidth * Math.min(sx, sy) * 0.5, 0.4);

                doc.setDrawColor(s.color);
                doc.setFillColor(s.color);
                doc.setLineWidth(lw);

                if (s.tool === 'rect') {
                    doc.rect(lx, ly, w, h, s.filled ? 'F' : 'S');
                } else if (s.tool === 'oval') {
                    doc.ellipse(lx + w / 2, ly + h / 2, w / 2, h / 2, s.filled ? 'F' : 'S');
                } else if (s.tool === 'line') {
                    doc.line(x1, y1, x2, y2);
                } else if (s.tool === 'text' && s.text) {
                    const fs = Math.max((s.fontSize ?? 14) * Math.min(sx, sy), 5);
                    doc.setFontSize(fs);
                    doc.setFont('helvetica', s.fontBold ? 'bold' : (s.fontItalic ? 'italic' : 'normal'));
                    doc.text(tr2pdf(s.text), x1, y1);
                }
            });

            doc.setLineWidth(0.4);
            doc.setFont('helvetica', 'normal');

            // Cephe etiketleri (yön bazlı)
            arsa.cepheler.forEach(c => {
                const yon = (c.yon ?? '').trim().toLowerCase();
                if (!yon) return;
                const lbl = tr2pdf(c.cepheAdi);
                doc.setFontSize(6.5); doc.setFont('helvetica', 'bold');
                const approxW = lbl.length * 4 + 10;
                const lblH    = 11;
                let bx = 0, by = 0, valid = true;
                if      (yon === 'kuzey' || yon === 'k')                             { bx = dx + dw / 2 - approxW / 2; by = dy + 2; }
                else if (yon === 'güney' || yon === 'g' || yon === 'guney')          { bx = dx + dw / 2 - approxW / 2; by = dy + dh - lblH - 2; }
                else if (yon === 'batı'  || yon === 'b' || yon === 'bati')           { bx = dx + 2;              by = dy + dh / 2 - lblH / 2; }
                else if (yon === 'doğu'  || yon === 'd' || yon === 'dogu')           { bx = dx + dw - approxW - 2; by = dy + dh / 2 - lblH / 2; }
                else { valid = false; }
                if (!valid) return;
                doc.setFillColor(230, 126, 34); doc.setDrawColor(180, 80, 20); doc.setLineWidth(0.5);
                doc.roundedRect(bx, by, approxW, lblH, 2, 2, 'F');
                doc.setTextColor(255, 255, 255);
                doc.text(lbl, bx + 5, by + lblH - 3);
                doc.setTextColor(0);
            });

            // Bölüm pin'leri — xVal/yVal doğrudan drawing alanına uygulanır
            bolumlerInKat.forEach(b => {
                const px = dx + (b.xVal ?? 0.1) * dw;
                const py = dy + (b.yVal ?? 0.1) * dh;
                const toplamOran = b.paylasimlar.reduce((s, p) => s + p.oranYuzde, 0);
                const tamamlandi = toplamOran >= 100;
                const pinW = 34; const pinH = 13;
                if (tamamlandi) { doc.setFillColor(39, 174, 96); doc.setDrawColor(21, 115, 55); }
                else             { doc.setFillColor(230, 126, 34); doc.setDrawColor(180, 80, 20); }
                doc.setLineWidth(0.7);
                doc.roundedRect(px - pinW / 2, py - pinH / 2, pinW, pinH, 2, 2, 'FD');
                doc.setFontSize(6.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255);
                const lbl = b.bolumAdi.length > 5 ? b.bolumAdi.substring(0, 5) : b.bolumAdi;
                doc.text(tr2pdf(lbl), px, py + 2, { align: 'center' });
                doc.setTextColor(0);
            });

            // Pusula — sağ üst
            if (compassBase64) {
                const CS = Math.min(38, dw * 0.08);
                doc.addImage(compassBase64, 'PNG', dx + dw - CS - 5, dy + 5, CS, CS);
            }

            // Dış kenarlık
            doc.setDrawColor(180, 190, 220); doc.setLineWidth(0.8);
            doc.rect(dx, dy, dw, dh);
            doc.setLineWidth(0.4);
        };

        // ── PDF oluştur ───────────────────────────────────────────────────
        const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
        const pageW  = doc.internal.pageSize.getWidth();
        const pageH  = doc.internal.pageSize.getHeight();
        const margin = 24;

        for (let ki = 0; ki < katlar.length; ki++) {
            const kat = katlar[ki];
            if (ki > 0) doc.addPage();

            let y = pdfHeader(doc, tr2pdf(`${blok.blokAdi} - Vaziyet Planlari`));

            // Kat etiketi bandı
            doc.setFillColor(230, 126, 34);
            doc.roundedRect(margin, y, 200, 22, 2, 2, 'F');
            doc.setFontSize(10); doc.setFont('helvetica', 'bold');
            doc.setTextColor(255, 255, 255);
            doc.text(tr2pdf(kat.katAdi), margin + 10, y + 15);
            doc.setTextColor(0);
            y += 30;

            const usableH  = pageH - y - margin - 26;
            const contentW = pageW - margin * 2;

            // En/boy oranı — KatCizimPanel ile aynı öncelik sırası
            const ratioVal = kat.en && kat.boy && kat.boy > 0
                ? kat.en / kat.boy
                : blok.en && blok.boy && blok.boy > 0
                    ? blok.en / blok.boy
                    : 4 / 3;

            // Çizim alanı: sayfanın yarısı genişlik, oran korunur
            // Yükseklik aşılırsa KatCizimPanel gibi GENİ�?Lİ�?İ küçült
            let drawingW = Math.floor(pageW / 2);
            let drawingH = Math.round(drawingW / ratioVal);
            if (drawingH > usableH) {
                drawingH = Math.floor(usableH);
                drawingW = Math.round(drawingH * ratioVal);
            }

            const infoW = pageW - margin - (margin + drawingW + 10) - 2;
            const infoX = margin + drawingW + 10;
            const bolumlerInKat = arsa.bolumler.filter(b => b.katID === kat.katID);
            const shapes = shapesMap.get(kat.katID) ?? [];

            renderInPdf(shapes, bolumlerInKat, margin, y, drawingW, drawingH);

            // ── Sağ panel: bölüm bilgi kartları ─────────────────────────
            const lineH  = 9;
            const cardPad = 5;

            if (bolumlerInKat.length === 0) {
                doc.setFontSize(7.5); doc.setTextColor(160);
                doc.text('Bu katta bolum tanimli degil.', infoX + cardPad, y + 14);
                doc.setTextColor(0);
            }

            let iy = y;
            bolumlerInKat.forEach(b => {
                const toplamOran = b.paylasimlar.reduce((s, p) => s + p.oranYuzde, 0);
                const tamamlandi = toplamOran >= 100;
                const hasPay     = b.paylasimlar.length > 0;
                const cepheler   = [b.cephe1, b.cephe2, b.cephe3, b.cephe4]
                    .filter(c => c !== null).map(c => c!.cepheAdi);

                let cardH = cardPad + lineH + lineH
                           + (cepheler.length > 0 ? lineH : 0)
                           + (hasPay ? 5 + b.paylasimlar.length * lineH + lineH : 0)
                           + cardPad;

                if (iy + cardH > y + drawingH) return;

                if (tamamlandi) { doc.setFillColor(232, 249, 240); doc.setDrawColor(39, 174, 96); }
                else if (hasPay){ doc.setFillColor(255, 251, 245); doc.setDrawColor(230, 160, 60); }
                else             { doc.setFillColor(252, 253, 255); doc.setDrawColor(200, 210, 230); }
                doc.setLineWidth(tamamlandi ? 1 : 0.4);
                doc.roundedRect(infoX, iy, infoW, cardH, 2, 2, 'FD');
                doc.setLineWidth(0.4);

                let ly = iy + cardPad;

                doc.setFontSize(8); doc.setFont('helvetica', 'bold');
                doc.setTextColor(tamamlandi ? 21 : 26, tamamlandi ? 115 : 35, tamamlandi ? 55 : 64);
                doc.text(tr2pdf(b.bolumAdi), infoX + cardPad, ly + 7, { maxWidth: infoW - cardPad * 2 });
                ly += lineH;

                doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(100);
                const tipM2 = [b.bolumTipi ? tr2pdf(b.bolumTipi) : null, `${b.m2.toLocaleString('tr-TR')} m2`]
                    .filter(Boolean).join('  |  ');
                doc.text(tipM2, infoX + cardPad, ly + 7, { maxWidth: infoW - cardPad * 2 });
                ly += lineH;

                if (cepheler.length > 0) {
                    doc.setFontSize(6.5); doc.setTextColor(80, 110, 160);
                    doc.text(tr2pdf(cepheler.join(', ')), infoX + cardPad, ly + 7, { maxWidth: infoW - cardPad * 2 });
                    ly += lineH;
                }

                if (hasPay) {
                    doc.setDrawColor(tamamlandi ? 39 : 200, tamamlandi ? 174 : 210, tamamlandi ? 96 : 230);
                    doc.line(infoX + 4, ly + 2, infoX + infoW - 4, ly + 2);
                    ly += 5;
                    b.paylasimlar.forEach(p => {
                        const kisi = tr2pdf(p.ortak?.adSoyad ?? p.muteahhit?.adSoyad ?? '?');
                        doc.setFontSize(6.5); doc.setFont('helvetica', 'normal');
                        doc.setTextColor(tamamlandi ? 28 : 55, tamamlandi ? 130 : 70, tamamlandi ? 65 : 100);
                        doc.text(`${kisi}  %${p.oranYuzde}`, infoX + cardPad, ly + 7, { maxWidth: infoW - cardPad * 2 });
                        ly += lineH;
                    });
                    doc.setFontSize(6.5); doc.setFont('helvetica', 'bold');
                    doc.setTextColor(tamamlandi ? 39 : 100, tamamlandi ? 174 : 100, tamamlandi ? 96 : 100);
                    doc.text(
                        `Toplam: %${toplamOran.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`,
                        infoX + cardPad, ly + 7, { maxWidth: infoW - cardPad * 2 }
                    );
                }

                doc.setTextColor(0);
                iy += cardH + 4;
            });
        }

        pdfFooter(doc);
        const takip = arsa.takipNo ? `_${arsa.takipNo}` : '';
        doc.save(`vaziyet-plan_${tr2pdf(blok.blokAdi)}${takip}-${new Date().toISOString().slice(0,10)}.pdf`);
    };

    // jsPDF helvetica fontu Turkce karakter desteklemiyor; ASCII karsiligi kullanilir
    const tr2pdf = (s: string | null | undefined): string => (s ?? '')
        .replace(/\u011f/g, 'g').replace(/\u011e/g, 'G')
        .replace(/\u00fc/g, 'u').replace(/\u00dc/g, 'U')
        .replace(/\u015f/g, 's').replace(/\u015e/g, 'S')
        .replace(/\u0131/g, 'i').replace(/\u0130/g, 'I')
        .replace(/\u00f6/g, 'o').replace(/\u00d6/g, 'O')
        .replace(/\u00e7/g, 'c').replace(/\u00c7/g, 'C');

    const exportOneriPdf = () => {
        if (!oneriData || !arsa) return;
        const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 32;
        let y = 36;

        // ── Ana başlık ──────────────────────────────────────────────────────
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('Akilli Dagitim Onerileri', margin, y);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`Tarih: ${new Date().toLocaleDateString('tr-TR')}`, pageWidth - margin, y, { align: 'right' });
        y += 18;

        // ── Arsa bilgi kutusu ────────────────────────────────────────────────
        // Muteahhitler adlarını firma olarak kullan
        const firmalar = arsa.muteahhitler.map(m => tr2pdf(m.adSoyad)).join(' / ');
        const lokasyon = [arsa.mahalle, arsa.ilce, arsa.il].filter(Boolean).map(tr2pdf).join(' / ');
        const adaParsel = `Ada: ${arsa.ada ?? '-'}  Parsel: ${arsa.parsel ?? '-'}`;
        const m2Bilgi = arsa.arsaMetrekare != null ? `Toplam Alan: ${fmt(arsa.arsaMetrekare)} m2` : '';
        const degerBilgi = arsa.arsaTutar != null ? `Toplam Deger: ${fmt(arsa.arsaTutar)} TL` : '';
        const toplamKalan = `Toplam Kalan Dagitilacak: ${fmt(oneriData.toplamKalanTL)} TL`;

        doc.setFillColor(240, 244, 255);
        doc.setDrawColor(180, 190, 220);
        doc.roundedRect(margin, y, pageWidth - margin * 2, 52, 4, 4, 'FD');

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        if (firmalar) doc.text(`Firma: ${firmalar}`, margin + 10, y + 14);
        doc.setFont('helvetica', 'normal');
        doc.text(adaParsel, margin + 10, y + 26);
        if (lokasyon) doc.text(lokasyon, margin + 10, y + 38);
        const sag = pageWidth - margin - 10;
        if (m2Bilgi) doc.text(m2Bilgi, sag, y + 14, { align: 'right' });
        if (degerBilgi) doc.text(degerBilgi, sag, y + 26, { align: 'right' });
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 80, 180);
        doc.text(toplamKalan, sag, y + 38, { align: 'right' });
        doc.setTextColor(0);
        doc.setFont('helvetica', 'normal');

        y += 62;
        doc.setDrawColor(180);
        doc.line(margin, y, pageWidth - margin, y);
        y += 14;

        // ── Bölümler ────────────────────────────────────────────────────────
        oneriData.bolumler.forEach(b => {
            if (b.kalanOran <= 0 && b.oneriler.length === 0) return;

            if (y > doc.internal.pageSize.getHeight() - 70) { doc.addPage(); y = 36; }

            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text(tr2pdf(`${b.bolumAdi}${b.bolumTipi ? ' - ' + b.bolumTipi : ''}`), margin, y);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            const bolumBilgi = `Fiyat: ${fmt(b.fiyat)} TL   Dagitilan: %${b.dagitilanOran.toFixed(2)}   Kalan: %${b.kalanOran.toFixed(2)}${b.atanmayanFiyat > 0 ? '   Atanamayan: ' + fmt(b.atanmayanFiyat) + ' TL' : ''}`;
            doc.text(bolumBilgi, margin, y + 12);
            y += 26;

            // Mevcut paylaşımlar
            if (b.mevcutPaylasimlar.length > 0) {
                autoTable(doc, {
                    startY: y,
                    margin: { left: margin, right: margin },
                    head: [['Ad Soyad', 'Tur', 'Oran (%)', 'Pay (TL)']],
                    body: b.mevcutPaylasimlar.map(p => [
                        tr2pdf(p.adSoyad),
                        p.tip === 'Ortak' ? 'Arsa Ortagi' : 'Muteahhit',
                        `%${p.oranYuzde}`,
                        `${fmt(p.payTL)} TL`
                    ]),
                    headStyles: { fillColor: [200, 210, 230], textColor: 30, fontStyle: 'bold', fontSize: 7.5 },
                    bodyStyles: { fontSize: 7.5 },
                    tableWidth: 'auto'
                });
                y = (doc as any).lastAutoTable.finalY + 8;
            }

            // Öneriler
            if (b.oneriler.length > 0) {
                autoTable(doc, {
                    startY: y,
                    margin: { left: margin, right: margin },
                    head: [['Ad Soyad', 'Tur', 'Toplam Hak (TL)', 'Onceki Kalan (TL)', 'Oneri Oran', 'Oneri TL', 'Sonraki Kalan (TL)', 'Durum']],
                    body: b.oneriler.map(o => [
                        tr2pdf(o.adSoyad),
                        o.tip === 'Ortak' ? 'Arsa Ortagi' : 'Muteahhit',
                        `${fmt(o.payTL)} TL`,
                        `${fmt(o.kalanOncesi)} TL`,
                        `%${o.oneriOran.toFixed(2)}`,
                        `${fmt(o.oneriTL)} TL`,
                        `${fmt(o.kalanSonra)} TL`,
                        o.durum === 'tamamlandi' ? 'Tamamlandi' : `${fmt(o.kalanSonra)} TL devam`
                    ]),
                    headStyles: { fillColor: [26, 35, 64], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
                    bodyStyles: { fontSize: 7.5 },
                    columnStyles: { 4: { fontStyle: 'bold' }, 5: { fontStyle: 'bold' } }
                });
                y = (doc as any).lastAutoTable.finalY + 16;
            }
        });

        // Atanamayanlar
        if (oneriData.atanamamis && oneriData.atanamamis.length > 0) {
            if (y > doc.internal.pageSize.getHeight() - 80) { doc.addPage(); y = 36; }
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text(`Dagitilamamis Kisiler (${oneriData.atanamamis.length})`, margin, y);
            y += 14;
            autoTable(doc, {
                startY: y,
                margin: { left: margin, right: margin },
                head: [['Ad Soyad', 'Tur', 'Toplam Hak (TL)', 'Dagitilamamis (TL)']],
                body: oneriData.atanamamis.map(a => [
                    tr2pdf(a.adSoyad),
                    a.tip === 'Ortak' ? 'Arsa Ortagi' : 'Muteahhit',
                    `${fmt(a.payTL)} TL`,
                    `${fmt(a.kalanTL)} TL`
                ]),
                headStyles: { fillColor: [192, 57, 43], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
                bodyStyles: { fontSize: 7.5 }
            });
        }

        const takip = arsa.takipNo ? `_${arsa.takipNo}` : '';
        doc.save(`dagitim-onerileri${takip}-${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    // ─── Yardımcılar ──────────────────────────────────────────────────────────

    // Paylaşılan alan: paylasimlar oranlarından hesapla
    const bolumPaylasılanAlan = (b: Bolum): number => {
        const toplamOran = b.paylasimlar.reduce((acc, p) => acc + p.oranYuzde, 0);
        return b.m2 * Math.min(toplamOran, 100) / 100;
    };

    // Sürükle-bırak sırasında bir bölümün bırakma hedefi olarak pasif (bırakılamaz) olup olmadığı
    const bolumDropPasifMi = (b: Bolum): boolean => {
        if (!draggingKisi) return true;
        const toplamOran = b.paylasimlar.reduce((acc, p) => acc + p.oranYuzde, 0);
        const tamDolu = toplamOran >= 100;
        const butceDisi = draggingKisi.kalanPay * 1.05 < b.fiyat;
        return tamDolu || butceDisi;
    };

    // Cepheler sekmesi ile Bölümler sekmesindeki "Cepheler" modalı arasında paylaşılan içerik
    const renderCephelerIcerik = () => {
        if (!arsa) return null;
        return (
            <>
                <div className="kka-section-header">
                    <h4>{t('kka.cepheler', 'Cepheler')}</h4>
                    <button className="kka-btn-primary" onClick={openCepheCreate}>+ Cephe Ekle</button>
                </div>
                {arsa.cepheler.length === 0 ? (
                    <div className="kka-empty">{t('kka.henuzCepheEklenmemis')}</div>
                ) : (
                    <table className="kka-sub-table">
                        <thead><tr><th>{t('kka.cepheAdi', 'Cephe Adı')}</th><th>{t('kka.yon', 'Yön')}</th><th>{t('label.aciklama')}</th><th></th></tr></thead>
                        <tbody>
                            {arsa.cepheler.map(c => (
                                <tr key={c.cepheID}>
                                    <td><strong>{c.cepheAdi}</strong></td>
                                    <td>{c.yon ?? '–'}</td>
                                    <td>{c.aciklama ?? '–'}</td>
                                    <td>
                                        <div className="kka-td-actions">
                                            <button className="kka-icon-btn edit" onClick={() => openCepheEdit(c)}><i className="fas fa-pen" style={{ color: '#1a2340' }}></i></button>
                                            <button className="kka-icon-btn del" onClick={() => deleteCephe(c.cepheID)}><i className="fas fa-trash" style={{ color: '#1a2340' }}></i></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </>
        );
    };

    // Tablo & Vaziyet Görünümü'nde paylaşılan "Sürükle & Bırak" ortak/müteahhit yan paneli
    const renderSurukleBirakPaneli = () => {
        if (!arsa) return null;
        const ortakKalanlar = arsa.ortaklar.map(o => ({ ...o, kalanPay: (o.payTL ?? 0) - (o.aldigiPayTL ?? 0) }));
        const muteahhitKalanlar = arsa.muteahhitler.map(m => ({ ...m, kalanPay: (m.payTL ?? 0) - (m.aldigiPayTL ?? 0) }));
        if (ortakKalanlar.length === 0 && muteahhitKalanlar.length === 0) return null;
        return (
            <div style={{ background: '#f4f6fb', border: '1px solid #e2e6f0', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ background: '#1a2340', padding: '9px 12px', fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <i className="fas fa-hand-paper" style={{ color: '#cd6906' }}></i> Sürükle & Bırak
                </div>
                <div style={{ padding: '8px 10px', maxHeight: 520, overflowY: 'auto' }}>
                    {ortakKalanlar.length > 0 && (
                        <>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>Ortaklar</div>
                            {ortakKalanlar.map(o => (
                                <div
                                    key={o.ortakID}
                                    draggable
                                    onDragStart={() => setDraggingKisi({ tip: 'ortak', id: o.ortakID, ad: o.adSoyad, kalanPay: o.kalanPay })}
                                    onDragEnd={() => setDraggingKisi(null)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', marginBottom: 4, background: '#fff', border: '1.5px solid #d1fae5', borderRadius: 7, cursor: 'grab', userSelect: 'none', transition: 'box-shadow 0.15s' }}
                                    title={`Sürükle: ${o.adSoyad}`}
                                >
                                    <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#dcfce7', color: '#15803d', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>O</span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: '#1a2340', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.adSoyad}</div>
                                        <div style={{ fontSize: 10, color: o.kalanPay > 0 ? '#15803d' : '#9ca3af' }}>{fmt(o.kalanPay)} ₺</div>
                                    </div>
                                    <i className="fas fa-grip-vertical" style={{ color: '#d1d5db', fontSize: 10 }}></i>
                                </div>
                            ))}
                        </>
                    )}
                    {muteahhitKalanlar.length > 0 && (
                        <>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 8, marginBottom: 5 }}>Müteahhitler</div>
                            {muteahhitKalanlar.map(m => (
                                <div
                                    key={m.muteahhitID}
                                    draggable
                                    onDragStart={() => setDraggingKisi({ tip: 'muteahhit', id: m.muteahhitID, ad: m.adSoyad, kalanPay: m.kalanPay })}
                                    onDragEnd={() => setDraggingKisi(null)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', marginBottom: 4, background: '#fff', border: '1.5px solid #fef08a', borderRadius: 7, cursor: 'grab', userSelect: 'none', transition: 'box-shadow 0.15s' }}
                                    title={`Sürükle: ${m.adSoyad}`}
                                >
                                    <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#fef9c3', color: '#854d0e', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>M</span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: '#1a2340', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.adSoyad}</div>
                                        <div style={{ fontSize: 10, color: m.kalanPay > 0 ? '#854d0e' : '#9ca3af' }}>{fmt(m.kalanPay)} ₺</div>
                                    </div>
                                    <i className="fas fa-grip-vertical" style={{ color: '#d1d5db', fontSize: 10 }}></i>
                                </div>
                            ))}
                        </>
                    )}
                    {draggingKisi && (
                        <div style={{ marginTop: 10, padding: '7px 10px', background: '#fffbeb', border: '1px dashed #cd6906', borderRadius: 7, fontSize: 11, color: '#92400e', textAlign: 'center' }}>
                            <i className="fas fa-info-circle" style={{ marginRight: 5 }}></i>
                            <strong>{draggingKisi.ad}</strong> sürükleniyor<br/>
                            <span style={{ color: '#9ca3af' }}>Gri daireler bırakılamaz</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Seçilen bloğa ait katlar
    const katsForBlok = (blokId: string) => {
        if (!arsa || !blokId) return [];
        const blok = arsa.bloklar.find(b => b.blokID === parseInt(blokId));
        return blok?.katlar ?? [];
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    if (loading) return <div className="kka-detay-page"><div className="kka-loading">{t('msg.yukleniyor')}</div></div>;
    if (!arsa) return <div className="kka-detay-page"><div className="kka-loading">{t('msg.yukleniyor')}rsa bulunamadı.</div></div>;

    // ── İş akışı: her adımın kontrol listesi (yardım panelinde ve adım çubuğunda kullanılır) ──
    const paraFmt = (n: number) => n.toLocaleString('tr-TR', { maximumFractionDigits: 0 }) + ' ₺';
    const git = (k: TabKey) => { setActiveTab(k); window.scrollTo({ top: 0, behavior: 'smooth' }); };
    const taraflar = [
        ...arsa.ortaklar.map(o => ({ ad: o.adSoyad, tur: 'Arsa Ortağı', yuzde: o.hisseOrani, pay: o.payTL, aldigi: o.aldigiPayTL })),
        ...arsa.muteahhitler.map(m => ({ ad: m.adSoyad, tur: 'Müteahhit', yuzde: m.toplamPayYuzde, pay: m.payTL, aldigi: m.aldigiPayTL })),
    ];
    const tarafDurumu = (t2: { pay: number | null; aldigi: number | null }): 'tamam' | 'eksik' | 'fazla' | 'yok' => {
        if (!t2.pay || t2.pay <= 0) return 'yok';
        const oran = ((t2.aldigi ?? 0) / t2.pay) * 100;
        if (oran > 100.5) return 'fazla';
        return oran >= PAY_TAMAM_ESIGI ? 'tamam' : 'eksik';
    };
    const adimKontrolleri = (k: TabKey): KontrolMaddesi[] => {
        const m: KontrolMaddesi[] = [];
        const bolumSayisi = arsa.bolumler.length;
        const eksikPaylasim = arsa.bolumler.filter(b => bolumPaylasimOrani(b) < 100);
        switch (k) {
            case 'genel': {
                const eksik = [!arsa.ada && 'ada', !arsa.parsel && 'parsel', !arsa.il && 'il', !arsa.ilce && 'ilçe'].filter(Boolean);
                if (eksik.length) m.push({ durum: 'uyari', metin: `Künyede eksik bilgi: ${eksik.join(', ')}. Raporlarda boş görünür.`, eylem: { etiket: 'Künyeyi düzenle', onClick: kunyeyiAc } });
                else m.push({ durum: 'tamam', metin: 'Künye bilgileri (ada, parsel, il, ilçe) girilmiş.' });
                break;
            }
            case 'taraflar': {
                if (arsa.ortaklar.length === 0) m.push({ durum: 'hata', metin: 'Henüz arsa ortağı eklenmedi. Daire ataması yapılabilmesi için en az bir hak sahibi gerekir.' });
                if (arsa.muteahhitler.length === 0) m.push({ durum: 'uyari', metin: 'Müteahhit eklenmedi. Müteahhidin payı tanımlanmadan dağıtım tamamlanamaz.' });
                const hissesiz = [...arsa.ortaklar, ...arsa.muteahhitler].filter(x => !x.hisseMetre).length;
                if (hissesiz > 0) m.push({ durum: 'uyari', metin: `${hissesiz} tarafın hisse m²'si girilmemiş; bu tarafların hak ediş oranı 0 hesaplanır.` });
                const mutYuzde = arsa.muteahhitler.reduce((x, y) => x + y.payYuzde, 0);
                if (mutYuzde > 100) m.push({ durum: 'hata', metin: `Müteahhitlerin sabit pay yüzdesi toplamı %${mutYuzde} — %100'ü aşamaz.` });
                if (m.length === 0) m.push({ durum: 'tamam', metin: `${arsa.ortaklar.length} ortak ve ${arsa.muteahhitler.length} müteahhit tanımlı; hak ediş oranları hesaplandı.` });
                if (arsa.arsaMetrekare) m.push({ durum: 'bilgi', metin: `Hisselerden hesaplanan arsa alanı: ${arsa.arsaMetrekare.toLocaleString('tr-TR')} m².` });
                break;
            }
            case 'bloklar': {
                if (arsa.bloklar.length === 0) { m.push({ durum: 'hata', metin: 'Henüz blok eklenmedi. "+ Blok Ekle" ile başlayın.' }); break; }
                const katsiz = arsa.bloklar.filter(b => b.katlar.length === 0);
                if (katsiz.length) m.push({ durum: 'uyari', metin: `Katı olmayan blok: ${katsiz.map(b => b.blokAdi).join(', ')}.` });
                const uyumsuz = arsa.bloklar.filter(b => b.katSayisi !== null && b.katlar.length > 0 && b.katlar.length !== b.katSayisi);
                if (uyumsuz.length) m.push({ durum: 'uyari', metin: `Kat sayısı ile eklenen kat adedi uyuşmuyor: ${uyumsuz.map(b => `${b.blokAdi} (${b.katlar.length}/${b.katSayisi})`).join(', ')}.` });
                const ebatsiz = arsa.bloklar.flatMap(b => b.katlar.filter(kt => !(kt.en ?? b.en) || !(kt.boy ?? b.boy)));
                if (ebatsiz.length) m.push({ durum: 'bilgi', metin: `${ebatsiz.length} katın ebadı (en × boy) girilmemiş; kat planı 4:3 oranında çizilir.` });
                if (!katsiz.length && !uyumsuz.length) m.push({ durum: 'tamam', metin: `${arsa.bloklar.length} blok ve ${arsa.bloklar.reduce((x, b) => x + b.katlar.length, 0)} kat tanımlı.` });
                break;
            }
            case 'bolumler': {
                if (arsa.bloklar.length === 0) m.push({ durum: 'uyari', metin: 'Önce blok ve katları oluşturun; bölümler bir kata bağlanır.', eylem: { etiket: 'Adım 3', onClick: () => git('bloklar') } });
                if (bolumSayisi === 0) { m.push({ durum: 'hata', metin: 'Henüz bölüm eklenmedi. "İşlemler" menüsünden ekleyin.' }); break; }
                const katsiz = arsa.bolumler.filter(b => !b.katID).length;
                if (katsiz) m.push({ durum: 'uyari', metin: `${katsiz} bölümün blok/katı seçilmemiş; kat planında yerleştirilemez.` });
                const fiyatsiz = arsa.bolumler.filter(b => !b.fiyat).length;
                if (fiyatsiz) m.push({ durum: 'uyari', metin: `${fiyatsiz} bölümün fiyatı girilmemiş; toplam değer ve hak edişler eksik hesaplanır.` });
                const m2siz = arsa.bolumler.filter(b => !b.m2).length;
                if (m2siz) m.push({ durum: 'bilgi', metin: `${m2siz} bölümün m²'si girilmemiş.` });
                if (!katsiz && !fiyatsiz) m.push({ durum: 'tamam', metin: `${bolumSayisi} bölüm fiyatlandırıldı — toplam değer ${paraFmt(arsa.arsaTutar ?? 0)}.` });
                break;
            }
            case 'yerlesim': {
                if (bolumSayisi === 0) { m.push({ durum: 'uyari', metin: 'Yerleştirilecek bölüm yok.', eylem: { etiket: 'Adım 4', onClick: () => git('bolumler') } }); break; }
                const yerlesmemis = arsa.bolumler.filter(b => b.katID && (b.xVal === null || b.yVal === null)).length;
                if (yerlesmemis) m.push({ durum: 'uyari', metin: `${yerlesmemis} bölüm kat planında henüz yerleştirilmedi (planın sol üst köşesinde bekliyor).` });
                const katsiz = arsa.bolumler.filter(b => !b.katID).length;
                if (katsiz) m.push({ durum: 'bilgi', metin: `${katsiz} bölümün katı olmadığı için kat planında görünmez.`, eylem: { etiket: 'Adım 4', onClick: () => git('bolumler') } });
                if (!yerlesmemis) m.push({ durum: 'tamam', metin: 'Katı olan tüm bölümler kat planında yerleştirildi.' });
                m.push({ durum: 'bilgi', metin: 'Bina fotoğrafı üzerinde yerleşim için Görünümler\'i açın (isteğe bağlı).', eylem: { etiket: 'Görünümler', onClick: () => setGorunumlerAcik(true) } });
                break;
            }
            case 'paylasim': {
                if (taraflar.length === 0) m.push({ durum: 'hata', metin: 'Atama yapılacak hak sahibi yok.', eylem: { etiket: 'Adım 2', onClick: () => git('taraflar') } });
                if (bolumSayisi === 0) { m.push({ durum: 'hata', metin: 'Atanacak bölüm yok.', eylem: { etiket: 'Adım 4', onClick: () => git('bolumler') } }); break; }
                if (eksikPaylasim.length) m.push({ durum: 'uyari', metin: `${eksikPaylasim.length} / ${bolumSayisi} bölümün paylaşımı tamamlanmadı.` });
                else m.push({ durum: 'tamam', metin: `${bolumSayisi} bölümün tamamı hak sahiplerine atandı.` });
                break;
            }
            case 'kontrol': {
                if (bolumSayisi === 0 || taraflar.length === 0) { m.push({ durum: 'uyari', metin: 'Kontrol için önce taraflar, bölümler ve atamalar tamamlanmalı.' }); break; }
                if (eksikPaylasim.length) m.push({ durum: 'uyari', metin: `${eksikPaylasim.length} bölüm henüz tam paylaştırılmadı.`, eylem: { etiket: 'Adım 6', onClick: () => git('paylasim') } });
                const eksik = taraflar.filter(x => tarafDurumu(x) === 'eksik');
                const fazla = taraflar.filter(x => tarafDurumu(x) === 'fazla');
                if (fazla.length) m.push({ durum: 'hata', metin: `Hak ettiğinden fazla pay alan: ${fazla.map(x => x.ad).join(', ')}.` });
                if (eksik.length) m.push({ durum: 'uyari', metin: `Hak ettiği payı henüz tam almayan: ${eksik.map(x => x.ad).join(', ')}.` });
                if (!eksikPaylasim.length && !eksik.length && !fazla.length) m.push({ durum: 'tamam', metin: 'Tüm bölümler paylaştırıldı ve her taraf hak ettiği payı aldı — dağıtım kesinleşmeye hazır.' });
                break;
            }
        }
        return m;
    };
    const adimDurumlari = Object.fromEntries(ADIMLAR.map(a => [a.key, adimDurumu(adimKontrolleri(a.key))])) as Record<TabKey, 'tamam' | 'uyari' | 'bos'>;
    const adimEtiketEki: Partial<Record<TabKey, string>> = {
        taraflar: `${arsa.ortaklar.length + arsa.muteahhitler.length} taraf`,
        bloklar: `${arsa.bloklar.length} blok`,
        bolumler: `${arsa.bolumler.length} bölüm`,
        paylasim: `${arsa.bolumler.filter(b => bolumPaylasimOrani(b) >= 100).length}/${arsa.bolumler.length}`,
    };
    // Adım 4'te yalnızca liste; Adım 6'da tablo / vaziyet / görünüm (sürükle-bırak)
    const bolumGorunumu: 'liste' | 'tablo' | 'vaziyet' | 'gorunum' =
        activeTab === 'bolumler' ? 'liste' : (bolumView === 'liste' ? 'tablo' : bolumView);
    const yerlesimModu = activeTab === 'yerlesim';

    return (
        <div className="kka-detay-page">
            {/* Sayfa Başlığı */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>
                        {t('kka.katKarsiligSozlesmesi', 'Kat Karşılığı Sözleşmesi')}
                        {(arsa.ada || arsa.parsel) && (
                            <span style={{ color: '#64748b', fontWeight: 500 }}>
                                {' – '}
                                {[arsa.ada ? `${t('kka.ada', 'Ada')}: ${arsa.ada}` : null, arsa.parsel ? `${t('kka.parsel', 'Parsel')}: ${arsa.parsel}` : null].filter(Boolean).join(' | ')}
                            </span>
                        )}
                    </h1>
                    <p style={{ fontSize: '13.5px', color: '#64748b', margin: 0 }}>
                        {[arsa.takipNo, arsa.il, arsa.ilce, arsa.mahalle].filter(Boolean).join(' · ')}
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div className="kka-indir-wrap">
                        <button className="kka-indir-btn" style={{ background: '#1a2340', color: '#fff' }} onClick={() => setShowIslemler(v => !v)}>
                            <i className="fas fa-cog" style={{ marginRight: 6 }}></i>{t('label.islemler')}
                            <i className="fas fa-caret-down" style={{ marginLeft: 6, fontSize: '0.75rem' }}></i>
                        </button>
                        {showIslemler && (
                            <div className="kka-indir-menu" onMouseLeave={() => setShowIslemler(false)}>
                                <button className="kka-indir-item" onClick={() => { setShowIslemler(false); setActiveTab('genel'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                                    <i className="fas fa-info-circle" style={{ marginRight: 8, color: '#1a2340' }}></i>{t('kka.detaylar', 'Detaylar')}
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="kka-indir-wrap">
                        <button className="kka-indir-btn" onClick={() => setShowIndir(v => !v)}>
                            <i className="fas fa-file-pdf" style={{ marginRight: 6 }}></i>{t('btn.indir')}
                            <i className="fas fa-caret-down" style={{ marginLeft: 6, fontSize: '0.75rem' }}></i>
                        </button>
                        {showIndir && (
                            <div className="kka-indir-menu" onMouseLeave={() => setShowIndir(false)}>
                                <button className="kka-indir-item" onClick={() => { setShowIndir(false); exportOrtaklarPdf(); }}>
                                    <i className="fas fa-users" style={{ marginRight: 8, color: '#cd6906' }}></i>{t('kka.arsaOrtaklariRaporu', 'Arsa Ortağı Raporu')}
                                </button>
                                <button className="kka-indir-item" onClick={() => { setShowIndir(false); exportGayrimenkullerPdf(); }}>
                                    <i className="fas fa-building" style={{ marginRight: 8, color: '#cd6906' }}></i>{t('kka.gayrimenkullerRaporu', 'Gayrimenkuller Raporu')}
                                </button>
                                <button className="kka-indir-item" onClick={() => { setShowIndir(false); exportPaylasimlarPdf(); }}>
                                    <i className="fas fa-list-alt" style={{ marginRight: 8, color: '#cd6906' }}></i>{t('kka.bolumPaylasimListesi', 'Bölüm Paylaşım Listesi')}
                                </button>
                                <button className="kka-indir-item" onClick={() => { setShowIndir(false); setBlokRaporBlokId(null); setShowBlokRaporModal(true); }}>
                                    <i className="fas fa-th-large" style={{ marginRight: 8, color: '#cd6906' }}></i>{t('kka.blokGorunumRaporu', 'Blok Görünüm Raporu')}
                                </button>
                                <button className="kka-indir-item" onClick={() => { setShowIndir(false); setBlokRaporPayliBlokId(null); setShowBlokRaporPayliModal(true); }}>
                                    <i className="fas fa-th-large" style={{ marginRight: 8, color: '#27ae60' }}></i>{t('kka.blokGorunumRaporuPayli', 'Blok Görünüm Raporu (Paylaşımlı)')}
                                </button>
                                <button className="kka-indir-item" onClick={() => { setShowIndir(false); setVaziyetBlokId(null); setShowVaziyetModal(true); }}>
                                    <i className="fas fa-map-marked-alt" style={{ marginRight: 8, color: '#8e44ad' }}></i>{t('kka.vaziyetPlanlari', 'Vaziyet Planları')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* İş akışı adımları + o adımın yardım paneli */}
            <AdimCubugu aktif={activeTab} durumlar={adimDurumlari} etiketEki={adimEtiketEki} onSec={git} />
            <AdimRehberi adim={activeTab} maddeler={adimKontrolleri(activeTab)} onGit={git} />

            {/* ── Genel Tab ── */}
            {activeTab === 'genel' && (
                <div className="kka-tab-section">
                    {/* Özet Kartları */}
                    <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                        {/* Arsa Alanı */}
                        <div style={{ background: '#f3f4f6', borderRadius: 8, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 160, cursor: 'default' }}>
                            <span style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <i className="fas fa-ruler-combined" style={{ fontSize: 15, color: '#64748b' }} />
                            </span>
                            <span style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', lineHeight: 1, letterSpacing: '-0.04em', flexShrink: 0 }}>
                                {arsa.arsaMetrekare !== null ? arsa.arsaMetrekare.toLocaleString('tr-TR') : '—'}
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', letterSpacing: '0.3px', lineHeight: 1.3 }}>
                                {t('kka.arsaM2', 'Arsa m²')}
                            </span>
                        </div>

                        {/* Bölüm Adedi */}
                        <div style={{ background: '#f3f4f6', borderRadius: 8, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 160, cursor: 'default' }}>
                            <span style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <i className="fas fa-th-large" style={{ fontSize: 15, color: '#64748b' }} />
                            </span>
                            <span style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', lineHeight: 1, letterSpacing: '-0.04em', flexShrink: 0 }}>
                                {arsa.bolumler.length}
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', letterSpacing: '0.3px', lineHeight: 1.3 }}>
                                {t('kka.bolumAdedi', 'Bölüm Adedi')}
                            </span>
                        </div>

                        {/* Toplam Değer */}
                        <div style={{ background: '#f3f4f6', borderRadius: 8, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 200, cursor: 'default' }}>
                            <span style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <span style={{ fontSize: 18, fontWeight: 800, color: '#64748b' }}>₺</span>
                            </span>
                            <span style={{ fontSize: 18, fontWeight: 800, color: '#1e293b', lineHeight: 1, letterSpacing: '-0.03em', flexShrink: 0 }}>
                                {arsa.arsaTutar !== null
                                    ? arsa.arsaTutar.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ₺'
                                    : '—'}
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', letterSpacing: '0.3px', lineHeight: 1.3 }}>
                                {t('kka.toplamDeger', 'Toplam Değer')}
                            </span>
                        </div>

                        {/* Paylaşılma Durumu */}
                        {(() => {
                            const toplamBolum = arsa.bolumler.length;
                            const paylasimliSayi = arsa.bolumler.filter(b => b.paylasimlar.reduce((s, p) => s + p.oranYuzde, 0) >= 100).length;
                            const paylasimOrani = toplamBolum > 0 ? Math.round((paylasimliSayi / toplamBolum) * 100) : 0;
                            return (
                                <div style={{ background: '#f3f4f6', borderRadius: 8, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 180, cursor: 'default' }}>
                                    <span style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <i className="fas fa-chart-pie" style={{ fontSize: 15, color: '#64748b' }} />
                                    </span>
                                    <div style={{ flexShrink: 0 }}>
                                        <div style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', lineHeight: 1, letterSpacing: '-0.04em' }}>%{paylasimOrani}</div>
                                        <div style={{ background: '#e5e7eb', borderRadius: 4, height: 4, width: 60, overflow: 'hidden', marginTop: 5 }}>
                                            <div style={{ width: `${paylasimOrani}%`, background: '#cd6906', height: '100%', borderRadius: 4 }} />
                                        </div>
                                    </div>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', letterSpacing: '0.3px', lineHeight: 1.3 }}>
                                        {t('kka.paylasılma', 'Paylaşılma')}<br /><span style={{ color: '#9ca3af', fontWeight: 400 }}>{paylasimliSayi}/{toplamBolum} {t('kka.bolum', 'bölüm')}</span>
                                    </span>
                                </div>
                            );
                        })()}
                    </div>

                    {/* Metro kutucuklar — ilgili sekmeye geçiş */}
                    {(() => {
                        const tamamBolum = arsa.bolumler.filter(b => bolumPaylasimOrani(b) >= 100).length;
                        const tamamOrtak = arsa.ortaklar.filter(o => (payTamamlanmaOrani(o) ?? 0) >= PAY_TAMAM_ESIGI).length;
                        const paylasimYuzde = arsa.bolumler.length > 0 ? Math.round((tamamBolum / arsa.bolumler.length) * 100) : 0;
                        const kutular: { key: TabKey; baslik: string; ikon: string; renk: string; deger: string; alt: string }[] = [
                            { key: 'bolumler', baslik: t('kka.bolumler', 'Bölümler'), ikon: 'fa-th-large', renk: '#1a2340', deger: String(arsa.bolumler.length), alt: `${tamamBolum} bölümün paylaşımı tamam` },
                            { key: 'taraflar', baslik: t('kka.muteahhitler', 'Müteahhitler'), ikon: 'fa-hard-hat', renk: '#cd6906', deger: String(arsa.muteahhitler.length), alt: 'müteahhit' },
                            { key: 'taraflar', baslik: t('kka.arsaOrtak', 'Arsa Ortakları'), ikon: 'fa-users', renk: '#0f766e', deger: String(arsa.ortaklar.length), alt: `${tamamOrtak} ortağın paylaşımı tamam` },
                            { key: 'paylasim', baslik: t('kka.paylasimlar', 'Paylaşımlar'), ikon: 'fa-chart-pie', renk: '#7c3aed', deger: `%${paylasimYuzde}`, alt: `${tamamBolum}/${arsa.bolumler.length} bölüm paylaşıldı` },
                        ];
                        return (
                            <div className="kka-metro">
                                {kutular.map(k => (
                                    <button key={k.baslik} className="kka-metro-kutu" style={{ background: k.renk }} onClick={() => setActiveTab(k.key)}>
                                        <i className={`fas ${k.ikon} kka-metro-ikon`}></i>
                                        <span className="kka-metro-deger">{k.deger}</span>
                                        <span className="kka-metro-baslik">{k.baslik} <i className="fas fa-arrow-right"></i></span>
                                        <span className="kka-metro-alt">{k.alt}</span>
                                    </button>
                                ))}
                            </div>
                        );
                    })()}
                    <div className="kka-section-header">
                        <h4>{t('kka.arsaBilgileri', 'Arsa Bilgileri')}</h4>
                        {!kunyeAcik && (
                            <button className="kka-btn-secondary" onClick={kunyeyiAc}>
                                <i className="fas fa-pen" style={{ marginRight: 6 }}></i>Künyeyi Düzenle
                            </button>
                        )}
                    </div>
                    {kunyeAcik && (
                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                                {([
                                    ['takipNo', 'Takip No *'], ['ada', 'Ada'], ['parsel', 'Parsel'],
                                    ['il', 'İl'], ['ilce', 'İlçe'], ['mahalle', 'Mahalle'],
                                ] as [keyof typeof kunyeForm, string][]).map(([alan, etiket]) => (
                                    <label key={alan} style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#475569' }}>
                                        {etiket}
                                        <input className="kka-input" value={kunyeForm[alan]} onChange={e => setKunyeForm(f => ({ ...f, [alan]: e.target.value }))} />
                                    </label>
                                ))}
                            </div>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#475569', marginTop: 10 }}>
                                Açıklama
                                <textarea className="kka-input" rows={2} value={kunyeForm.aciklama} onChange={e => setKunyeForm(f => ({ ...f, aciklama: e.target.value }))} />
                            </label>
                            <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 8 }}>
                                <i className="fas fa-info-circle" style={{ marginRight: 5 }}></i>
                                Arsa m² ve toplam değer burada girilmez; taraf hisselerinden (Adım 2) ve bölüm fiyatlarından (Adım 4) hesaplanır.
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
                                <button className="kka-btn-secondary" onClick={() => setKunyeAcik(false)} disabled={kunyeKaydediliyor}>{t('btn.iptal')}</button>
                                <button className="kka-btn-primary" onClick={kunyeyiKaydet} disabled={kunyeKaydediliyor}>
                                    {kunyeKaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="kka-info-grid">
                        <div className="kka-info-item"><label>{t('kka.takipNo', 'Takip No')}</label><span>{arsa.takipNo}</span></div>
                        <div className="kka-info-item"><label>{t('kka.ada', 'Ada')}</label><span>{arsa.ada ?? '–'}</span></div>
                        <div className="kka-info-item"><label>{t('kka.parsel', 'Parsel')}</label><span>{arsa.parsel ?? '–'}</span></div>
                        <div className="kka-info-item"><label>{t('label.il', 'İl')}</label><span>{arsa.il ?? '–'}</span></div>
                        <div className="kka-info-item"><label>{t('label.ilce', 'İlçe')}</label><span>{arsa.ilce ?? '–'}</span></div>
                        <div className="kka-info-item"><label>{t('kka.mahalle', 'Mahalle')}</label><span>{arsa.mahalle ?? '–'}</span></div>
                        <div className="kka-info-item"><label>{t('kka.alanM2', 'Alan (m²)')}</label><span>{arsa.arsaMetrekare !== null ? `${arsa.arsaMetrekare.toLocaleString()} m²` : '–'}</span></div>
                        <div className="kka-info-item"><label>{t('kka.toplamTutar', 'Toplam Tutar')}</label><span>{arsa.arsaTutar !== null ? `${arsa.arsaTutar.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺` : '–'}</span></div>
                        <div className="kka-info-item"><label>{t('label.durum', 'Durum')}</label><span><span className={`kka-badge ${arsa.aktifMi ? 'aktif' : 'pasif'}`}>{arsa.aktifMi ? t('durum.aktif', 'Aktif') : t('durum.pasif', 'Pasif')}</span></span></div>
                    </div>
                    {arsa.aciklama && (
                        <div style={{ marginTop: 16, padding: '12px 16px', background: '#f8f9fb', borderRadius: 8, fontSize: '0.9rem', color: '#444' }}>
                            {arsa.aciklama}
                        </div>
                    )}
                </div>
            )}

            {/* ── Ortaklar Tab ── */}
            {activeTab === 'taraflar' && (() => {
                const iSt: React.CSSProperties = { padding: '4px 8px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', outline: 'none', width: '100%', boxSizing: 'border-box' };
                const iDirty: React.CSSProperties = { ...iSt, border: '1px solid #cd6906', background: '#fffbf5' };
                const hisseOranHesapla = (m2: string) => {
                    if (!m2 || !arsa.arsaMetrekare || arsa.arsaMetrekare <= 0) return null;
                    return ((parseFloat(m2) / arsa.arsaMetrekare) * 100).toFixed(4);
                };
                const dirtyCount = ortakDuzenleSatirlar.filter(s => s.dirty).length;
                return (
                <div className="kka-tab-section">
                    {/* Header */}
                    <div className="kka-section-header">
                        <h4>{t('kka.arsaOrtak')}</h4>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            {ortakDuzenleMod ? (
                                <>
                                    <button className="kka-btn-secondary" onClick={() => setOrtakDuzenleMod(false)} disabled={ortakDuzenleSaving}>
                                        <i className="fas fa-times" style={{ marginRight: 6 }}></i>{t('btn.iptal')}
                                    </button>
                                    <button
                                        className="kka-btn-primary"
                                        onClick={saveOrtakDuzenle}
                                        disabled={ortakDuzenleSaving || dirtyCount === 0}
                                        style={{ opacity: dirtyCount === 0 ? 0.5 : 1 }}
                                    >
                                        {ortakDuzenleSaving
                                            ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }}></i>{t('msg.kaydediliyor', 'Kaydediliyor...')} </>
                                            : <><i className="fas fa-save" style={{ marginRight: 6 }}></i>{t('btn.kaydet', 'Kaydet')} ({dirtyCount})</>}
                                    </button>
                                </>
                            ) : (
                                <>
                                    {excelButonlari('ortak')}
                                    {arsa.ortaklar.length > 0 && (
                                        <button className="kka-btn-secondary" onClick={openOrtakDuzenle} title={t('btn.topluDuzenle')}>
                                            <i className="fas fa-table" style={{ marginRight: 6 }}></i>{t('btn.topluDuzenle')}
                                        </button>
                                    )}
                                    <button
                                        className="kka-btn-primary"
                                        onClick={() => { setOrtakEkleAktif(a => !a); setOrtakEkleError(''); setOrtakEkleForm({ adSoyad: '', telefon: '', eposta: '', tcKimlik: '', hisseMetre: '', not: '' }); }}
                                    >
                                        {ortakEkleAktif
                                            ? <><i className="fas fa-times" style={{ marginRight: 6 }}></i>{t('btn.iptal')}</>
                                            : <><i className="fas fa-plus" style={{ marginRight: 6 }}></i>{t('kka.ortakEkle', 'Ortak Ekle')}</>}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Tablo */}
                    {(arsa.ortaklar.length > 0 || ortakEkleAktif) ? (
                        <table className="kka-sub-table">
                            <thead>
                                <tr>
                                    <th>{t('label.adSoyad')}</th>
                                    <th>{t('kka.tcKimlik', 'TC Kimlik')}</th>
                                    <th>{t('label.telefon')}</th>
                                    <th>{t('label.eposta', 'E-Posta')}</th>
                                    <th>{t('kka.hisseM2', 'Hisse m²')}</th>
                                    <th>{t('kka.hisseOrani', 'Hisse (%)')}</th>
                                    {!ortakDuzenleMod && <th>{t('kka.payTL', 'Pay (TL)')}</th>}
                                    {!ortakDuzenleMod && <th>{t('kka.aldigiPay')}</th>}
                                    {!ortakDuzenleMod && <th style={{ minWidth: 120 }}>Tamamlanma</th>}
                                    <th>{t('label.not', 'Not')}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {arsa.ortaklar.map(o => {
                                    const oPays = arsa.bolumler
                                        .filter(b => b.paylasimlar.some(p => p.ortakID === o.ortakID))
                                        .map(b => ({ b, p: b.paylasimlar.find(p => p.ortakID === o.ortakID)! }));

                                    if (ortakDuzenleMod) {
                                        const ds = ortakDuzenleSatirlar.find(s => s.ortakID === o.ortakID);
                                        if (!ds) return null;
                                        const inp = ds.dirty ? iDirty : iSt;
                                        const oran = hisseOranHesapla(ds.hisseMetre);
                                        return (
                                            <tr key={o.ortakID} style={{ background: ds.dirty ? '#fffbf5' : undefined }}>
                                                <td style={{ minWidth: 130 }}>
                                                    <input style={inp} value={ds.adSoyad} onChange={e => updateOrtakDuzenleSatir(o.ortakID, 'adSoyad', e.target.value)} />
                                                </td>
                                                <td style={{ minWidth: 110 }}>
                                                    <input style={inp} value={ds.tcKimlik} maxLength={11} onChange={e => updateOrtakDuzenleSatir(o.ortakID, 'tcKimlik', e.target.value)} />
                                                </td>
                                                <td style={{ minWidth: 110 }}>
                                                    <input style={inp} value={ds.telefon} onChange={e => updateOrtakDuzenleSatir(o.ortakID, 'telefon', e.target.value)} />
                                                </td>
                                                <td style={{ minWidth: 140 }}>
                                                    <input style={inp} value={ds.eposta} onChange={e => updateOrtakDuzenleSatir(o.ortakID, 'eposta', e.target.value)} />
                                                </td>
                                                <td style={{ minWidth: 90 }}>
                                                    <input style={inp} type="number" value={ds.hisseMetre} onChange={e => updateOrtakDuzenleSatir(o.ortakID, 'hisseMetre', e.target.value)} />
                                                </td>
                                                <td style={{ color: '#6b7280', fontSize: 12, whiteSpace: 'nowrap' }}>
                                                    {oran ? `%${oran}` : '–'}
                                                </td>
                                                <td style={{ minWidth: 120 }}>
                                                    <input style={inp} value={ds.not} onChange={e => updateOrtakDuzenleSatir(o.ortakID, 'not', e.target.value)} />
                                                </td>
                                                <td>
                                                    <button
                                                        className="kka-icon-btn"
                                                        title={t('btn.sifirla')}
                                                        style={{ opacity: ds.dirty ? 1 : 0.2, pointerEvents: ds.dirty ? 'auto' : 'none' }}
                                                        onClick={() => setOrtakDuzenleSatirlar(prev => prev.map(s => s.ortakID !== o.ortakID ? s : {
                                                            ...s, adSoyad: o.adSoyad, telefon: o.telefon ?? '', eposta: o.eposta ?? '',
                                                            tcKimlik: o.tcKimlik ?? '', hisseMetre: o.hisseMetre !== null ? String(o.hisseMetre) : '',
                                                            not: o.not ?? '', dirty: false,
                                                        }))}
                                                    >
                                                        <i className="fas fa-undo" style={{ color: '#6b7280' }}></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    }

                                    // Normal görünüm
                                    return (
                                        <tr key={o.ortakID}>
                                            <td>
                                                <strong>{o.adSoyad}</strong>
                                                {(payTamamlanmaOrani(o) ?? 0) >= PAY_TAMAM_ESIGI && <TamamIsareti title="Paylaşımı tamamlandı" />}
                                                <button className="kka-daire-btn" onClick={() => { setTarafSatirDuzen({}); setTarafModal({ tip: 'ortak', id: o.ortakID }); }}
                                                    title="Bu kişinin paylaşımdan aldığı daireler">
                                                    <i className="fas fa-home"></i> {oPays.length}
                                                </button>
                                                {oPays.length > 0 && (
                                                    <div className="kka-pay-list">
                                                        {oPays.map(({ b, p }) => {
                                                            const key = `o-${o.ortakID}-${b.bolumID}`;
                                                            const cepheler = [b.cephe1, b.cephe2, b.cephe3, b.cephe4].filter(Boolean).map(c => c!.cepheAdi).join(', ');
                                                            return (
                                                                <span key={key} className="kka-pay-chip"
                                                                    onMouseEnter={() => setHoveredPayKey(key)}
                                                                    onMouseLeave={() => setHoveredPayKey(null)}>
                                                                    {b.bolumAdi}: %{p.oranYuzde}
                                                                    {hoveredPayKey === key && (
                                                                        <div className="kka-pay-tooltip">
                                                                            <div className="kka-pay-tooltip-title">{b.bolumAdi}{b.bolumTipi ? ` – ${b.bolumTipi}` : ''}</div>
                                                                            {b.blok && <div className="kka-pay-tooltip-row"><span>Blok</span><span>{b.blok.blokAdi}</span></div>}
                                                                            {b.kat && <div className="kka-pay-tooltip-row"><span>Kat</span><span>{b.kat.katAdi}</span></div>}
                                                                            {cepheler && <div className="kka-pay-tooltip-row"><span>Cephe</span><span>{cepheler}</span></div>}
                                                                            <div className="kka-pay-tooltip-row"><span>M²</span><span>{b.m2.toLocaleString('tr-TR')} m²</span></div>
                                                                            <div className="kka-pay-tooltip-row"><span>Fiyat</span><span>{fmt(b.fiyat)} ₺</span></div>
                                                                            <div className="kka-pay-tooltip-row kka-pay-tooltip-oran"><span>Oran</span><span>%{p.oranYuzde}</span></div>
                                                                            <div className="kka-pay-tooltip-row kka-pay-tooltip-tl"><span>Pay (TL)</span><span>{fmt(b.fiyat * p.oranYuzde / 100)} ₺</span></div>
                                                                        </div>
                                                                    )}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </td>
                                            <td>{o.tcKimlik ?? '–'}</td>
                                            <td>{o.telefon ?? '–'}</td>
                                            <td>{o.eposta ?? '–'}</td>
                                            <td>{o.hisseMetre !== null ? `${o.hisseMetre.toLocaleString('tr-TR')} m²` : '–'}</td>
                                            <td>%{o.hisseOrani.toLocaleString('tr-TR')}</td>
                                            <td>{fmt(o.payTL)} ₺</td>
                                            <td>{fmt(o.aldigiPayTL)} ₺</td>
                                            <td>
                                                {payTamamlanmaOrani(o) === null
                                                    ? <span style={{ color: '#bbb' }}>–</span>
                                                    : <OranCubugu oran={payTamamlanmaOrani(o)!} tamamEsigi={PAY_TAMAM_ESIGI} />}
                                            </td>
                                            <td>{o.not ?? '–'}</td>
                                            <td>
                                                <div className="kka-td-actions">
                                                    <button className="kka-icon-btn edit" onClick={() => openOrtakEdit(o)}><i className="fas fa-pen" style={{ color: '#1a2340' }}></i></button>
                                                    <button className="kka-icon-btn del" onClick={() => deleteOrtak(o.ortakID)}><i className="fas fa-trash" style={{ color: '#1a2340' }}></i></button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}

                                {/* ── Inline Ortak Ekle Satırı ── */}
                                {ortakEkleAktif && !ortakDuzenleMod && (() => {
                                    const oran = hisseOranHesapla(ortakEkleForm.hisseMetre);
                                    return (
                                        <>
                                            {ortakEkleError && (
                                                <tr>
                                                    <td colSpan={11} style={{ padding: '4px 12px', color: '#dc2626', fontSize: 12 }}>
                                                        {ortakEkleError}
                                                    </td>
                                                </tr>
                                            )}
                                            <tr style={{ background: '#f0fdf4', borderTop: '2px solid #86efac' }}>
                                                <td style={{ minWidth: 130 }}>
                                                    <input
                                                        style={{ ...iSt, border: '1px solid #86efac' }}
                                                        placeholder="Ad Soyad *"
                                                        value={ortakEkleForm.adSoyad}
                                                        onChange={e => setOrtakEkleForm(f => ({ ...f, adSoyad: e.target.value }))}
                                                        autoFocus
                                                        onKeyDown={e => e.key === 'Enter' && saveOrtakInline()}
                                                    />
                                                </td>
                                                <td style={{ minWidth: 110 }}>
                                                    <input style={{ ...iSt, border: '1px solid #86efac' }} placeholder="TC Kimlik"
                                                        maxLength={11} value={ortakEkleForm.tcKimlik}
                                                        onChange={e => setOrtakEkleForm(f => ({ ...f, tcKimlik: e.target.value }))} />
                                                </td>
                                                <td style={{ minWidth: 110 }}>
                                                    <input style={{ ...iSt, border: '1px solid #86efac' }} placeholder={t('label.telefon')}
                                                        value={ortakEkleForm.telefon}
                                                        onChange={e => setOrtakEkleForm(f => ({ ...f, telefon: e.target.value }))} />
                                                </td>
                                                <td style={{ minWidth: 140 }}>
                                                    <input style={{ ...iSt, border: '1px solid #86efac' }} placeholder={t('label.eposta')}
                                                        value={ortakEkleForm.eposta}
                                                        onChange={e => setOrtakEkleForm(f => ({ ...f, eposta: e.target.value }))} />
                                                </td>
                                                <td style={{ minWidth: 90 }}>
                                                    <input style={{ ...iSt, border: '1px solid #86efac' }} type="number"
                                                        placeholder={arsa.arsaMetrekare ? `/ ${arsa.arsaMetrekare}` : 'm²'}
                                                        value={ortakEkleForm.hisseMetre}
                                                        onChange={e => setOrtakEkleForm(f => ({ ...f, hisseMetre: e.target.value }))} />
                                                </td>
                                                <td style={{ color: '#16a34a', fontSize: 12, whiteSpace: 'nowrap' }}>
                                                    {oran ? `%${oran}` : '–'}
                                                </td>
                                                <td>–</td>
                                                <td>–</td>
                                                <td>–</td>
                                                <td style={{ minWidth: 120 }}>
                                                    <input style={{ ...iSt, border: '1px solid #86efac' }} placeholder="Not"
                                                        value={ortakEkleForm.not}
                                                        onChange={e => setOrtakEkleForm(f => ({ ...f, not: e.target.value }))} />
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: 4 }}>
                                                        <button
                                                            className="kka-btn-primary"
                                                            onClick={saveOrtakInline}
                                                            disabled={ortakEkleSaving}
                                                            style={{ padding: '4px 10px', fontSize: 12 }}
                                                        >
                                                            {ortakEkleSaving ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-check"></i>}
                                                        </button>
                                                        <button
                                                            className="kka-btn-secondary"
                                                            onClick={() => { setOrtakEkleAktif(false); setOrtakEkleError(''); }}
                                                            style={{ padding: '4px 8px', fontSize: 12 }}
                                                        >
                                                            <i className="fas fa-times"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        </>
                                    );
                                })()}
                            </tbody>
                        </table>
                    ) : (
                        <div className="kka-empty">{t('kka.henuzOrtakEklenmemis')}</div>
                    )}
                </div>
                );
            })()}

            {/* ── Müteahhitler Tab ── */}
            {activeTab === 'taraflar' && (
                <div className="kka-tab-section">
                    <div className="kka-section-header">
                        <h4>{t('kka.muteahhitler')}</h4>
                        <button className="kka-btn-primary" onClick={openMuteahhitCreate}>{t('kka.muteahhitEkle', 'Müteahhit Ekle')}</button>
                    </div>
                    {arsa.muteahhitler.length === 0 ? (
                        <div className="kka-empty">{t('kka.henuzMuteahhitEklenmemis')}</div>
                    ) : (
                        <table className="kka-sub-table">
                            <thead><tr><th>{t('label.adSoyad')}</th><th>{t('label.telefon')}</th><th>{t('label.eposta', 'E-Posta')}</th><th>{t('kka.vergiNo', 'Vergi No')}</th><th>{t('kka.hisseM2', 'Hisse m²')}</th><th>{t('kka.payYuzde', 'Pay (%)')}</th><th>{t('kka.toplamPayYuzde', 'Toplam Pay (%)')}</th><th>{t('kka.payTL', 'Pay (TL)')}</th><th>{t('kka.aldigiPay')}</th><th>{t('label.not', 'Not')}</th><th></th></tr></thead>
                            <tbody>
                                {arsa.muteahhitler.map(m => {
                                    const mPays = arsa.bolumler
                                        .filter(b => b.paylasimlar.some(p => p.muteahhitID === m.muteahhitID))
                                        .map(b => ({ b, p: b.paylasimlar.find(p => p.muteahhitID === m.muteahhitID)! }));
                                    return (
                                    <tr key={m.muteahhitID}>
                                        <td>
                                            <strong>{m.adSoyad}</strong>
                                            <button className="kka-daire-btn" onClick={() => { setTarafSatirDuzen({}); setTarafModal({ tip: 'muteahhit', id: m.muteahhitID }); }}
                                                title="Bu müteahhidin paylaşımdan aldığı daireler">
                                                <i className="fas fa-home"></i> {mPays.length}
                                            </button>
                                            {mPays.length > 0 && (
                                                <div className="kka-pay-list">
                                                    {mPays.map(({ b, p }) => {
                                                        const key = `m-${m.muteahhitID}-${b.bolumID}`;
                                                        const cepheler = [b.cephe1, b.cephe2, b.cephe3, b.cephe4].filter(Boolean).map(c => c!.cepheAdi).join(', ');
                                                        return (
                                                            <span
                                                                key={key}
                                                                className="kka-pay-chip"
                                                                onMouseEnter={() => setHoveredPayKey(key)}
                                                                onMouseLeave={() => setHoveredPayKey(null)}
                                                            >
                                                                {b.bolumAdi}: %{p.oranYuzde}
                                                                {hoveredPayKey === key && (
                                                                    <div className="kka-pay-tooltip">
                                                                        <div className="kka-pay-tooltip-title">{b.bolumAdi}{b.bolumTipi ? ` – ${b.bolumTipi}` : ''}</div>
                                                                        {b.blok && <div className="kka-pay-tooltip-row"><span>{t('label.blok', 'Blok')}</span><span>{b.blok.blokAdi}</span></div>}
                                                                        {b.kat && <div className="kka-pay-tooltip-row"><span>{t('label.kat', 'Kat')}</span><span>{b.kat.katAdi}</span></div>}
                                                                        {cepheler && <div className="kka-pay-tooltip-row"><span>{t('kka.cephe', 'Cephe')}</span><span>{cepheler}</span></div>}
                                                                        <div className="kka-pay-tooltip-row"><span>m²</span><span>{b.m2.toLocaleString()} m²</span></div>
                                                                        <div className="kka-pay-tooltip-row"><span>{t('kka.fiyat', 'Fiyat')}</span><span>{fmt(b.fiyat)} ₺</span></div>
                                                                        <div className="kka-pay-tooltip-row kka-pay-tooltip-oran"><span>{t('kka.oran', 'Oran')}</span><span>%{p.oranYuzde}</span></div>
                                                                        <div className="kka-pay-tooltip-row kka-pay-tooltip-tl"><span>{t('kka.payTL', 'Pay (TL)')}</span><span>{fmt(b.fiyat * p.oranYuzde / 100)} ₺</span></div>
                                                                    </div>
                                                                )}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </td>
                                        <td>{m.telefon ?? '–'}</td>
                                        <td>{m.eposta ?? '–'}</td>
                                        <td>{m.vergiNo ?? '–'}</td>
                                        <td>{m.hisseMetre !== null ? `${m.hisseMetre.toLocaleString('tr-TR')} m²` : '–'}</td>
                                        <td>%{m.payYuzde.toLocaleString('tr-TR')}</td>
                                        <td>%{m.toplamPayYuzde.toLocaleString('tr-TR')}</td>
                                        <td>{fmt(m.payTL)} ₺</td>
                                        <td>{fmt(m.aldigiPayTL)} ₺</td>
                                        <td>{m.not ?? '–'}</td>
                                        <td>
                                            <div className="kka-td-actions">
                                                <button className="kka-icon-btn edit" title={t('btn.duzenle')} onClick={() => openMuteahhitEdit(m)}><i className="fas fa-pen" style={{ color: '#1a2340' }}></i></button>
                                                <button className="kka-icon-btn del" title={t('btn.sil')} onClick={() => deleteMuteahhit(m.muteahhitID)}><i className="fas fa-trash" style={{ color: '#1a2340' }}></i></button>
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* ── Bloklar & Katlar Tab ── */}
            {(activeTab === 'bloklar' || activeTab === 'yerlesim') && (
                <div className="kka-tab-section">
                    <div className="kka-section-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <h4 style={{ margin: 0 }}>{yerlesimModu ? 'Bölüm Yerleşimi' : 'Bloklar & Katlar'}</h4>
                            <select
                                className="kka-input"
                                style={{ minWidth: 180, padding: '5px 10px', fontSize: '0.9rem' }}
                                value={selectedBlokId ?? ''}
                                onChange={e => {
                                    setSelectedBlokId(e.target.value ? Number(e.target.value) : null);
                                    setInlineKatBlokId(null);
                                }}
                            >
                                <option value="">{t('kka.blokSecin')}</option>
                                {arsa.bloklar.map(b => (
                                    <option key={b.blokID} value={b.blokID}>
                                        {b.blokAdi}{b.katSayisi ? ` (${b.katSayisi} kat)` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {yerlesimModu ? (
                                <button className="kka-btn-primary" onClick={() => setGorunumlerAcik(true)}
                                    title="Bina fotoğrafı üzerine bölümleri sürükle bırak ile yerleştirin">
                                    <i className="fas fa-layer-group" style={{ marginRight: 6 }}></i>Görünümler (Fotoğraf Üzerinde Yerleşim)
                                </button>
                            ) : (
                                <button className="kka-btn-primary" onClick={openBlokCreate}>+ Blok Ekle</button>
                            )}
                        </div>
                    </div>
                    {gorunumlerAcik && (
                        <ProjeGorunumleri
                            projeID={arsaId}
                            apiYolu="/api/KatKarsiligGorunumleri"
                            sahipAlani="arsaID"
                            durumRenkleri={BOLUM_SAHIP_RENK}
                            daireler={arsa.bolumler.map(b => ({
                                daireID: b.bolumID,
                                daireNo: b.bolumAdi,
                                blokID: b.blokID ?? 0,
                                katID: b.katID ?? 0,
                                cepheID: b.cephe1ID ?? 0,
                                manzaraID: 0,
                                daireTipID: 0,
                                daireDurumu: bolumSahipDurumu(b),
                                listeFiyati: b.fiyat || undefined,
                                netM2: b.m2 || undefined,
                                aktifMi: true,
                            }))}
                            bloklar={arsa.bloklar.map(b => ({ id: b.blokID, ad: b.blokAdi }))}
                            katlar={arsa.bloklar.flatMap(b => b.katlar.map(k => ({ id: k.katID, ad: arsa.bloklar.length > 1 ? `${b.blokAdi} / ${k.katAdi}` : k.katAdi })))}
                            cepheler={arsa.cepheler.map(c => ({ id: c.cepheID, ad: c.cepheAdi }))}
                            manzaralar={[]}
                            onClose={() => setGorunumlerAcik(false)}
                        />
                    )}

                    {arsa.bloklar.length === 0 ? (
                        <div className="kka-empty">{t('kka.henuzBlokEklenmemis')}</div>
                    ) : !selectedBlokId ? (
                        <div className="kka-empty">{t('kka.blokSecilmedi')}</div>
                    ) : (() => {
                        const b = arsa.bloklar.find(x => x.blokID === selectedBlokId);
                        if (!b) return null;
                        const katAdet = b.katlar.length;
                        const beklenen = b.katSayisi;
                        const uyari = beklenen !== null && katAdet !== beklenen
                            ? (katAdet < beklenen ? `${beklenen - katAdet} kat eksik` : `${katAdet - beklenen} kat fazla`)
                            : null;
                        return (
                            <div style={{ display: 'flex', gap: 0, alignItems: 'flex-start' }}>
                                {/* Sol 1/4: blok kartı */}
                                <div style={{ width: '25%', minWidth: 220 }}>
                                <div className="kka-blok-card" style={{ margin: 0 }}>
                                    <div className="kka-blok-card-header">
                                        <span>
                                            {b.blokAdi}{b.katSayisi ? ` (${b.katSayisi} kat)` : ''}
                                            {b.en || b.boy ? <span style={{ fontSize: '0.75rem', color: '#aaa', marginLeft: 6 }}>{b.en ?? '?'}×{b.boy ?? '?'}</span> : null}
                                        </span>
                                        <div className="kka-blok-card-actions">
                                            {uyari && <span style={{ fontSize: '0.75rem', color: '#cd6906', fontWeight: 600, marginRight: 6 }}><i className="fas fa-exclamation-triangle" style={{ marginRight: 4 }}></i>{uyari}</span>}
                                            {!yerlesimModu && <button className="kka-icon-btn edit" onClick={() => openBlokEdit(b)}><i className="fas fa-pen"></i></button>}
                                            {!yerlesimModu && <button className="kka-icon-btn del" onClick={() => deleteBlok(b.blokID)}><i className="fas fa-trash"></i></button>}
                                        </div>
                                    </div>
                                    <div className="kka-blok-card-body">
                                        {b.katlar.length === 0 ? (
                                            <p style={{ fontSize: '0.83rem', color: '#bbb', margin: '6px 0' }}>{t('kka.henuzKatYok')}</p>
                                        ) : (
                                            b.katlar.sort((a, c) => a.siraNo - c.siraNo).map(k => (
                                                <div className="kka-kat-row" key={k.katID} style={{ background: cizimKat?.kat.katID === k.katID ? '#eef3ff' : undefined }}>
                                                    <span>
                                                        {k.katAdi}{(k.en || k.boy) ? <span style={{ fontSize: '0.72rem', color: '#bbb', marginLeft: 4 }}>{k.en ?? '?'}×{k.boy ?? '?'}</span> : null}
                                                        {yerlesimModu && (() => {
                                                            const katBolum = arsa.bolumler.filter(x => x.katID === k.katID);
                                                            const bekleyen = katBolum.filter(x => x.xVal === null || x.yVal === null).length;
                                                            return katBolum.length > 0 && (
                                                                <span style={{ fontSize: '0.7rem', marginLeft: 6, fontWeight: 700, color: bekleyen ? '#b45309' : '#16a34a' }}
                                                                    title={bekleyen ? `${bekleyen} bölüm yerleştirilmedi` : 'Tüm bölümler yerleştirildi'}>
                                                                    {bekleyen ? `${katBolum.length - bekleyen}/${katBolum.length}` : <i className="fas fa-check"></i>}
                                                                </span>
                                                            );
                                                        })()}
                                                    </span>
                                                    <div className="kka-kat-row-actions">
                                                        {!yerlesimModu && <button className="kka-icon-btn edit" title={t('btn.duzenle')} onClick={() => openKatEdit(b.blokID, k)}><i className="fas fa-pen"></i></button>}
                                                        <button
                                                            className="kka-icon-btn"
                                                            title={yerlesimModu ? 'Bölümleri bu katın planına yerleştir' : t('kka.cizimYap')}
                                                            style={{ color: cizimKat?.kat.katID === k.katID ? '#3b6bb5' : '#aaa', fontSize: '1rem' }}
                                                            onClick={() => setCizimKat(cizimKat?.kat.katID === k.katID ? null : { blok: b, kat: k })}
                                                        ><i className="fas fa-map-marker-alt"></i></button>
                                                        {!yerlesimModu && <button className="kka-icon-btn del" title={t('btn.sil')} onClick={() => deleteKat(b.blokID, k.katID)}><i className="fas fa-trash"></i></button>}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                        {/* Inline kat ekleme satırı */}
                                        {yerlesimModu ? null : inlineKatBlokId === b.blokID ? (
                                            <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                                <input
                                                    style={{ flex: 2, minWidth: 80, fontSize: '0.82rem', padding: '4px 7px', border: '1px solid #dce', borderRadius: 5 }}
                                                    placeholder={t('kka.katAdi')}
                                                    value={inlineKatForm.katAdi}
                                                    onChange={e => setInlineKatForm(f => ({ ...f, katAdi: e.target.value }))}
                                                    autoFocus
                                                />
                                                <input
                                                    style={{ width: 52, fontSize: '0.82rem', padding: '4px 7px', border: '1px solid #dce', borderRadius: 5 }}
                                                    type="number" placeholder={t('label.ara')}
                                                    value={inlineKatForm.siraNo}
                                                    onChange={e => setInlineKatForm(f => ({ ...f, siraNo: e.target.value }))}
                                                />
                                                <input
                                                    style={{ width: 52, fontSize: '0.82rem', padding: '4px 7px', border: '1px solid #dce', borderRadius: 5 }}
                                                    type="number" placeholder="En"
                                                    value={inlineKatForm.en}
                                                    onChange={e => setInlineKatForm(f => ({ ...f, en: e.target.value }))}
                                                />
                                                <input
                                                    style={{ width: 52, fontSize: '0.82rem', padding: '4px 7px', border: '1px solid #dce', borderRadius: 5 }}
                                                    type="number" placeholder="Boy"
                                                    value={inlineKatForm.boy}
                                                    onChange={e => setInlineKatForm(f => ({ ...f, boy: e.target.value }))}
                                                />
                                                <button
                                                    style={{ fontSize: '0.8rem', padding: '4px 10px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer' }}
                                                    onClick={() => saveInlineKat(b.blokID)}
                                                    disabled={inlineKatSaving}
                                                >{inlineKatSaving ? '...' : 'Ekle'}</button>
                                                <button
                                                    style={{ fontSize: '0.8rem', padding: '4px 8px', background: '#eee', color: '#555', border: 'none', borderRadius: 5, cursor: 'pointer' }}
                                                    onClick={() => setInlineKatBlokId(null)}
                                                ><i className="fas fa-times"></i></button>
                                            </div>
                                        ) : (
                                            <button className="kka-add-kat-btn" onClick={() => openInlineKatAdd(b)}>+ Kat Ekle</button>
                                        )}
                                    </div>
                                </div>
                                </div>

                                {/* Sağ 3/4: çizim paneli */}
                                <div style={{ flex: 1, minWidth: 0, borderLeft: '2px solid #e8edf5', minHeight: 300 }}>
                                    {cizimKat ? (
                                        <KatCizimPanel
                                            key={cizimKat.kat.katID}
                                            katId={cizimKat.kat.katID}
                                            blokAdi={cizimKat.blok.blokAdi}
                                            katAdi={cizimKat.kat.katAdi}
                                            katEn={cizimKat.kat.en ?? cizimKat.blok.en}
                                            katBoy={cizimKat.kat.boy ?? cizimKat.blok.boy}
                                            blokKatlar={cizimKat.blok.katlar.map(k => ({ katID: k.katID, katAdi: k.katAdi }))}
                                            arsaId={arsaId}
                                            cepheler={arsa.cepheler.map(c => ({ cepheAdi: c.cepheAdi, yon: c.yon }))}
                                            bolumler={!yerlesimModu ? [] : arsa.bolumler
                                                .filter(b => b.katID === cizimKat.kat.katID)
                                                .map(b => ({
                                                    bolumID: b.bolumID,
                                                    bolumAdi: b.bolumAdi,
                                                    xVal: b.xVal,
                                                    yVal: b.yVal,
                                                    paylasimlar: b.paylasimlar.map(p => ({
                                                        adSoyad: p.ortak?.adSoyad ?? p.muteahhit?.adSoyad ?? '–',
                                                        oranYuzde: p.oranYuzde,
                                                    })),
                                                }))}
                                            onBolumKoordinatGuncellendi={(bolumID, xVal, yVal) => {
                                                setArsa(prev => {
                                                    if (!prev) return prev;
                                                    return {
                                                        ...prev,
                                                        bolumler: prev.bolumler.map(b =>
                                                            b.bolumID === bolumID ? { ...b, xVal, yVal } : b
                                                        )
                                                    };
                                                });
                                            }}
                                        />
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 260, color: '#bbb', fontSize: '0.88rem', flexDirection: 'column', gap: 8 }}>
                                            <i className="fas fa-map-marker-alt" style={{ fontSize: '2rem', color: '#1a2340' }}></i>
                                            {yerlesimModu
                                                ? <>Kat listesinden <i className="fas fa-map-marker-alt" style={{ color: '#1a2340' }}></i> ikonuna tıklayın; bölüm etiketlerini plan üzerinde doğru yerlerine sürükleyin</>
                                                : <>Kat listesinden <i className="fas fa-map-marker-alt" style={{ color: '#1a2340' }}></i> ikonuna tıklayarak kat planını çizin</>}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}
                </div>
            )}


            {/* ── Bölümler Tab ── */}
            {(activeTab === 'bolumler' || activeTab === 'paylasim') && (
                <div className="kka-tab-section">
                    {/* Header */}
                    <div className="kka-section-header">
                        <h4>{activeTab === 'paylasim' ? 'Daire Atama (Sürükle & Bırak)' : t('kka.bolumler')}</h4>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            {activeTab === 'paylasim' ? (
                            <>
                            {/* Atama ekranı seçimi */}
                            <div className="kka-view-toggle">
                                <button
                                    className={`kka-view-btn${bolumGorunumu === 'tablo' ? ' active' : ''}`}
                                    onClick={() => setBolumView('tablo')}
                                    title="Tablo — katlara göre bölüm hücreleri"
                                ><i className="fas fa-border-all"></i></button>
                                <button
                                    className={`kka-view-btn${bolumGorunumu === 'vaziyet' ? ' active' : ''}`}
                                    onClick={() => setBolumView('vaziyet')}
                                    title="Vaziyet — kat planları üzerinde"
                                ><i className="fas fa-map-marked-alt"></i></button>
                                <button
                                    className={`kka-view-btn${bolumGorunumu === 'gorunum' ? ' active' : ''}`}
                                    onClick={() => setBolumView('gorunum')}
                                    title="Görünüm — bina fotoğrafı üzerinde"
                                ><i className="fas fa-layer-group"></i></button>
                            </div>
                            <button className="kka-btn-secondary" onClick={fetchOneri} disabled={oneriLoading}
                                title="Hak edişlere göre otomatik atama önerisi hazırlar">
                                {oneriLoading
                                    ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }}></i>{t('msg.hesaplaniyor', 'Hesaplanıyor...')}</>
                                    : <><i className="fas fa-lightbulb" style={{ marginRight: 6, color: '#f59e0b' }}></i>{t('kka.dagitimOneriHazirla', 'Dağıtım Önerisi Hazırla')}</>}
                            </button>
                            </>
                            ) : (
                            <>
                            <button
                                className="kka-btn-secondary"
                                onClick={() => setShowCephelerModal(true)}
                                title={t('kka.cepheler', 'Cepheler')}
                            >
                                <i className="fas fa-compass" style={{ marginRight: 6 }}></i>{t('kka.cepheler', 'Cepheler')} ({arsa.cepheler.length})
                            </button>
                            {topluDuzenleMod ? (
                                <>
                                    <button
                                        className="kka-btn-secondary"
                                        onClick={() => setTopluDuzenleMod(false)}
                                        disabled={topluDuzenleSaving}
                                    >
                                        <i className="fas fa-times" style={{ marginRight: 6 }}></i>{t('btn.iptal')}
                                    </button>
                                    <button
                                        className="kka-btn-primary"
                                        onClick={saveTopluDuzenle}
                                        disabled={topluDuzenleSaving || topluDuzenleSatirlar.filter(s => s.dirty).length === 0}
                                        style={{ opacity: topluDuzenleSatirlar.filter(s => s.dirty).length === 0 ? 0.5 : 1 }}
                                    >
                                        {topluDuzenleSaving
                                            ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }}></i>{t('msg.kaydediliyor', 'Kaydediliyor...')} </>
                                            : <><i className="fas fa-save" style={{ marginRight: 6 }}></i>{t('btn.kaydet', 'Kaydet')} ({topluDuzenleSatirlar.filter(s => s.dirty).length})</>
                                        }
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        className="kka-btn-secondary"
                                        onClick={openTopluDuzenle}
                                        title={t('btn.topluDuzenle')}
                                    >
                                        <i className="fas fa-table" style={{ marginRight: 6 }}></i>{t('btn.topluDuzenle')}
                                    </button>
                                    <input ref={bolumExcelRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                                        onChange={e => handleExcelYukle('bolum', e)} />
                                    <div className="kka-indir-wrap">
                                        <button className="kka-btn-primary" onClick={() => setBolumIslemlerAcik(v => !v)}>
                                            <i className={excelYukleniyor === 'bolum' ? 'fas fa-spinner fa-spin' : 'fas fa-cog'} style={{ marginRight: 6 }}></i>{t('label.islemler', 'İşlemler')}
                                            <i className="fas fa-caret-down" style={{ marginLeft: 6, fontSize: '0.75rem' }}></i>
                                        </button>
                                        {bolumIslemlerAcik && (
                                            <div className="kka-indir-menu" onMouseLeave={() => setBolumIslemlerAcik(false)}>
                                                <button className="kka-indir-item" onClick={() => { setBolumIslemlerAcik(false); openBolumCreate(); }}>
                                                    <i className="fas fa-plus" style={{ marginRight: 8, color: '#cd6906' }}></i>Bölüm Ekle
                                                </button>
                                                <button className="kka-indir-item" onClick={() => { setBolumIslemlerAcik(false); openTopluBolumModal(); }}>
                                                    <i className="fas fa-hat-wizard" style={{ marginRight: 8, color: '#cd6906' }}></i>Toplu Ekle
                                                </button>
                                                <button className="kka-indir-item" onClick={() => { setBolumIslemlerAcik(false); bolumSablonIndir(arsa); }}>
                                                    <i className="fas fa-file-excel" style={{ marginRight: 8, color: '#1d6f42' }}></i>Şablon İndir
                                                </button>
                                                <button className="kka-indir-item" disabled={excelYukleniyor !== null}
                                                    onClick={() => { setBolumIslemlerAcik(false); bolumExcelRef.current?.click(); }}>
                                                    <i className="fas fa-file-upload" style={{ marginRight: 8, color: '#1d6f42' }}></i>Excel'den Yükle
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                            </>
                            )}
                        </div>
                    </div>

                    {arsa.bolumler.length === 0 ? (
                        <div className="kka-empty">Henüz bölüm eklenmemiş.</div>
                    ) : bolumGorunumu === 'liste' ? (
                        <>
                            {/* Filtreler */}
                            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                                <input
                                    className="kka-search-input"
                                    style={{ flex: '2 1 150px', minWidth: 120 }}
                                    placeholder={t('label.ara')}
                                    value={bolumFilterAdi}
                                    onChange={e => { setBolumFilterAdi(e.target.value); setBolumPage(1); }}
                                />
                                <select
                                    className="kka-filter-select"
                                    value={bolumFilterTip}
                                    onChange={e => { setBolumFilterTip(e.target.value); setBolumPage(1); }}
                                >
                                    <option value="">– Tüm Tipler –</option>
                                    {Array.from(new Set(arsa.bolumler.map(b => b.bolumTipi).filter((x): x is string => Boolean(x)))).map(item => (
                                        <option key={item} value={item}>{item}</option>
                                    ))}
                                </select>
                                <select
                                    className="kka-filter-select"
                                    value={bolumFilterTuru}
                                    onChange={e => { setBolumFilterTuru(e.target.value); setBolumPage(1); }}
                                >
                                    <option value="">– Tüm Türler –</option>
                                    {Array.from(new Set(arsa.bolumler.map(b => b.turu).filter((x): x is string => Boolean(x)))).map(item => (
                                        <option key={item} value={item}>{item}</option>
                                    ))}
                                </select>
                                <select
                                    className="kka-filter-select"
                                    value={bolumFilterBlok}
                                    onChange={e => { setBolumFilterBlok(e.target.value); setBolumFilterKat(''); setBolumPage(1); }}
                                >
                                    <option value="">– Tüm Bloklar –</option>
                                    {arsa.bloklar.map(b => (
                                        <option key={b.blokID} value={String(b.blokID)}>{b.blokAdi}</option>
                                    ))}
                                </select>
                                <select
                                    className="kka-filter-select"
                                    value={bolumFilterKat}
                                    onChange={e => { setBolumFilterKat(e.target.value); setBolumPage(1); }}
                                >
                                    <option value="">– Tüm Katlar –</option>
                                    {(bolumFilterBlok
                                        ? arsa.bloklar.find(b => String(b.blokID) === bolumFilterBlok)?.katlar ?? []
                                        : arsa.bloklar.flatMap(b => b.katlar)
                                    )
                                        .sort((a, c) => a.siraNo - c.siraNo)
                                        .map(k => (
                                            <option key={k.katID} value={String(k.katID)}>{k.katAdi}</option>
                                        ))}
                                </select>
                            </div>
                            {/* Tablo */}
                            {(() => {
                                const filtered = arsa.bolumler.filter(b => {
                                    if (bolumFilterAdi && !b.bolumAdi.toLowerCase().includes(bolumFilterAdi.toLowerCase())) return false;
                                    if (bolumFilterTip && b.bolumTipi !== bolumFilterTip) return false;
                                    if (bolumFilterTuru && b.turu !== bolumFilterTuru) return false;
                                    if (bolumFilterBlok && String(b.blokID) !== bolumFilterBlok) return false;
                                    if (bolumFilterKat && String(b.katID) !== bolumFilterKat) return false;
                                    return true;
                                });
                                if (filtered.length === 0) return <div className="kka-empty">Filtreye uygun bölüm bulunamadı.</div>;
                                const toplamSayfa = Math.ceil(filtered.length / BOLUM_SAYFA_BOYUTU);
                                const gecerliSayfa = Math.min(bolumPage, toplamSayfa);
                                const sayfadaki = topluDuzenleMod
                                    ? filtered
                                    : filtered.slice((gecerliSayfa - 1) * BOLUM_SAYFA_BOYUTU, gecerliSayfa * BOLUM_SAYFA_BOYUTU);
                                const editSel: React.CSSProperties = {
                                    padding: '3px 6px', fontSize: 12, border: '1px solid #d1d5db',
                                    borderRadius: 6, background: '#fff', outline: 'none', width: '100%',
                                };
                                const editInp: React.CSSProperties = {
                                    padding: '3px 7px', fontSize: 12, border: '1px solid #d1d5db',
                                    borderRadius: 6, background: '#fff', outline: 'none', width: '100%', boxSizing: 'border-box',
                                };
                                const editInpDirty: React.CSSProperties = {
                                    ...editInp, border: '1px solid #cd6906', background: '#fffbf5',
                                };
                                return (
                                    <>
                                    <table className="kka-sub-table">
                                        <thead>
                                            <tr>
                                                <th>{t('kka.bolumAdi', 'Bölüm Adı')}</th>
                                                <th>{t('label.tip')}</th>
                                                <th>{t('kka.turu', 'Türü')}</th>
                                                <th>{t('label.blok', 'Blok')}</th>
                                                <th>{t('label.kat', 'Kat')}</th>
                                                <th>{t('kka.cepheler', 'Cepheler')}</th>
                                                <th style={{ minWidth: 70 }}>m²</th>
                                                <th style={{ minWidth: 110 }}>{t('kka.fiyat', 'Fiyat')}</th>
                                                {!topluDuzenleMod && <th>{t('kka.birimFiyat', 'Birim Fiyat')}</th>}
                                                {!topluDuzenleMod && <th>{t('kka.paylasılanAlan', 'Paylaşılan Alan')}</th>}
                                                {!topluDuzenleMod && <th style={{ minWidth: 120 }}>Paylaşım</th>}
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sayfadaki.map(b => {
                                                const paylasılan = bolumPaylasılanAlan(b);
                                                const hasPaylasim = b.paylasimlar.length > 0;
                                                const ds = topluDuzenleMod
                                                    ? topluDuzenleSatirlar.find(s => s.bolumID === b.bolumID)
                                                    : null;
                                                if (topluDuzenleMod && !ds) return null;

                                                if (topluDuzenleMod && ds) {
                                                    const blokKatlar = ds.blokID
                                                        ? (arsa.bloklar.find(bl => bl.blokID === parseInt(ds.blokID))?.katlar ?? []).sort((a, c) => a.siraNo - c.siraNo)
                                                        : [];
                                                    const inp = ds.dirty ? editInpDirty : editInp;
                                                    const sel = ds.dirty ? { ...editSel, border: '1px solid #cd6906', background: '#fffbf5' } : editSel;
                                                    return (
                                                        <tr key={b.bolumID} style={{ background: ds.dirty ? '#fffbf5' : undefined }}>
                                                            <td style={{ minWidth: 80 }}>
                                                                <input style={inp} value={ds.bolumAdi}
                                                                    onChange={e => updateDuzenleSatir(b.bolumID, 'bolumAdi', e.target.value)} />
                                                            </td>
                                                            <td style={{ minWidth: 90 }}>
                                                                <input style={inp} value={ds.bolumTipi} placeholder="Daire…"
                                                                    onChange={e => updateDuzenleSatir(b.bolumID, 'bolumTipi', e.target.value)} />
                                                            </td>
                                                            <td style={{ minWidth: 90 }}>
                                                                <select style={sel} value={ds.turu}
                                                                    onChange={e => updateDuzenleSatir(b.bolumID, 'turu', e.target.value)}>
                                                                    <option value="">—</option>
                                                                    {turuListesi.map(item => <option key={item} value={item}>{item}</option>)}
                                                                </select>
                                                            </td>
                                                            <td style={{ minWidth: 90 }}>
                                                                <select style={sel} value={ds.blokID}
                                                                    onChange={e => updateDuzenleSatir(b.bolumID, 'blokID', e.target.value)}>
                                                                    <option value="">—</option>
                                                                    {arsa.bloklar.map(bl => <option key={bl.blokID} value={bl.blokID}>{bl.blokAdi}</option>)}
                                                                </select>
                                                            </td>
                                                            <td style={{ minWidth: 100 }}>
                                                                <select style={sel} value={ds.katID}
                                                                    onChange={e => updateDuzenleSatir(b.bolumID, 'katID', e.target.value)}>
                                                                    <option value="">—</option>
                                                                    {blokKatlar.map(k => <option key={k.katID} value={k.katID}>{k.katAdi}</option>)}
                                                                </select>
                                                            </td>
                                                            <td style={{ minWidth: 120 }}>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                                                    {[
                                                                        { field: 'cephe1ID' as keyof TopluDuzenleSatir, label: '1' },
                                                                        { field: 'cephe2ID' as keyof TopluDuzenleSatir, label: '2' },
                                                                    ].map(({ field, label }) => (
                                                                        <select key={field} style={{ ...sel, fontSize: 11 }} value={ds[field] as string}
                                                                            onChange={e => updateDuzenleSatir(b.bolumID, field, e.target.value)}>
                                                                            <option value="">C{label} —</option>
                                                                            {arsa.cepheler.map(c => <option key={c.cepheID} value={c.cepheID}>{c.cepheAdi}</option>)}
                                                                        </select>
                                                                    ))}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <input style={{ ...inp, width: 68 }} type="number" value={ds.m2}
                                                                    onChange={e => updateDuzenleSatir(b.bolumID, 'm2', e.target.value)} />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    style={{ ...inp, width: 100 }}
                                                                    type="text" inputMode="numeric"
                                                                    value={fmtBinlik(ds.fiyat)}
                                                                    onChange={e => {
                                                                        const raw = parseBinlik(e.target.value).replace(/[^\d.]/g, '');
                                                                        updateDuzenleSatir(b.bolumID, 'fiyat', raw);
                                                                    }}
                                                                />
                                                            </td>
                                                            <td>
                                                                <button
                                                                    className="kka-icon-btn del"
                                                                    title={t('btn.sifirla')}
                                                                    onClick={() => {
                                                                        const orig = arsa.bolumler.find(x => x.bolumID === b.bolumID);
                                                                        if (!orig) return;
                                                                        setTopluDuzenleSatirlar(prev => prev.map(s => s.bolumID !== b.bolumID ? s : ({
                                                                            ...s,
                                                                            bolumAdi: orig.bolumAdi, bolumTipi: orig.bolumTipi ?? '',
                                                                            turu: orig.turu ?? '', blokID: orig.blokID !== null ? String(orig.blokID) : '',
                                                                            katID: orig.katID !== null ? String(orig.katID) : '',
                                                                            cephe1ID: orig.cephe1ID !== null ? String(orig.cephe1ID) : '',
                                                                            cephe2ID: orig.cephe2ID !== null ? String(orig.cephe2ID) : '',
                                                                            m2: orig.m2 ? String(orig.m2) : '', fiyat: orig.fiyat ? String(orig.fiyat) : '',
                                                                            birimFiyat: orig.birimFiyat ? String(orig.birimFiyat) : '', dirty: false,
                                                                        })));
                                                                    }}
                                                                    style={{ opacity: ds.dirty ? 1 : 0.25, pointerEvents: ds.dirty ? 'auto' : 'none' }}
                                                                >
                                                                    <i className="fas fa-undo" style={{ color: '#6b7280' }}></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                }

                                                return (
                                                    <tr key={b.bolumID} className={hasPaylasim ? 'kka-row-paylasimli' : ''}>
                                                        <td style={{ whiteSpace: 'nowrap' }}>
                                                            <strong>{b.bolumAdi}</strong>
                                                            {bolumPaylasimOrani(b) >= 100 && <TamamIsareti title="Paylaşım tamamlandı" />}
                                                        </td>
                                                        <td>{b.bolumTipi ?? '–'}</td>
                                                        <td>{b.turu ?? '–'}</td>
                                                        <td>{b.blok?.blokAdi ?? '–'}</td>
                                                        <td>{b.kat?.katAdi ?? '–'}</td>
                                                        <td>
                                                            {[b.cephe1?.cepheAdi, b.cephe2?.cepheAdi, b.cephe3?.cepheAdi, b.cephe4?.cepheAdi]
                                                                .filter(Boolean).join(', ') || '–'}
                                                        </td>
                                                        <td>{fmt(b.m2)} m²</td>
                                                        <td>{fmt(b.fiyat)} ₺</td>
                                                        <td>{fmt(b.birimFiyat)} ₺</td>
                                                        <td>
                                                            {hasPaylasim ? (
                                                                <span className="kka-paylasilan-badge">
                                                                    {fmt(paylasılan)} m²
                                                                </span>
                                                            ) : <span style={{ color: '#bbb' }}>–</span>}
                                                        </td>
                                                        <td>
                                                            <button onClick={() => openPaylasimCreate(b.bolumID)} title="Paylaşımı görüntüle / düzenle"
                                                                style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', border: '1px solid transparent', background: 'none', borderRadius: 6, padding: '3px 4px', cursor: 'pointer' }}
                                                                onMouseEnter={e => { e.currentTarget.style.borderColor = '#cd6906'; e.currentTarget.style.background = '#fff8f0'; }}
                                                                onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.background = 'none'; }}>
                                                                <div style={{ flex: 1 }}><OranCubugu oran={bolumPaylasimOrani(b)} /></div>
                                                                <i className="fas fa-share-alt" style={{ color: '#cd6906', fontSize: 11 }}></i>
                                                            </button>
                                                        </td>
                                                        <td>
                                                            <div className="kka-td-actions">
                                                                <button className="kka-icon-btn edit" onClick={() => openBolumEdit(b)}><i className="fas fa-pen" style={{ color: '#1a2340' }}></i></button>
                                                                <button className="kka-icon-btn del" onClick={() => deleteBolum(b.bolumID)}><i className="fas fa-trash" style={{ color: '#1a2340' }}></i></button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    {!topluDuzenleMod && toplamSayfa > 1 && (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, padding: '8px 4px' }}>
                                            <span style={{ fontSize: 12, color: '#6b7280' }}>
                                                <strong>{filtered.length}</strong> bölüm — Sayfa <strong>{gecerliSayfa}</strong> / <strong>{toplamSayfa}</strong>
                                                <span style={{ marginLeft: 8, color: '#9ca3af' }}>
                                                    ({(gecerliSayfa - 1) * BOLUM_SAYFA_BOYUTU + 1}–{Math.min(gecerliSayfa * BOLUM_SAYFA_BOYUTU, filtered.length)})
                                                </span>
                                            </span>
                                            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                                <button
                                                    onClick={() => setBolumPage(1)}
                                                    disabled={gecerliSayfa === 1}
                                                    style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 6, background: gecerliSayfa === 1 ? '#f3f4f6' : '#fff', color: gecerliSayfa === 1 ? '#d1d5db' : '#374151', cursor: gecerliSayfa === 1 ? 'default' : 'pointer', fontSize: 12 }}
                                                    title="İlk sayfa"
                                                >«</button>
                                                <button
                                                    onClick={() => setBolumPage(p => Math.max(1, p - 1))}
                                                    disabled={gecerliSayfa === 1}
                                                    style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 6, background: gecerliSayfa === 1 ? '#f3f4f6' : '#fff', color: gecerliSayfa === 1 ? '#d1d5db' : '#374151', cursor: gecerliSayfa === 1 ? 'default' : 'pointer', fontSize: 12 }}
                                                >‹</button>
                                                {Array.from({ length: toplamSayfa }, (_, i) => i + 1)
                                                    .filter(p => p === 1 || p === toplamSayfa || Math.abs(p - gecerliSayfa) <= 2)
                                                    .reduce<(number | '…')[]>((acc, p, i, arr) => {
                                                        if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push('…');
                                                        acc.push(p);
                                                        return acc;
                                                    }, [])
                                                    .map((p, i) => p === '…'
                                                        ? <span key={`e${i}`} style={{ padding: '4px 6px', fontSize: 12, color: '#9ca3af' }}>…</span>
                                                        : <button key={p} onClick={() => setBolumPage(p as number)}
                                                            style={{ padding: '4px 9px', border: '1px solid', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: p === gecerliSayfa ? 700 : 400, borderColor: p === gecerliSayfa ? '#1a2340' : '#d1d5db', background: p === gecerliSayfa ? '#1a2340' : '#fff', color: p === gecerliSayfa ? '#fff' : '#374151' }}
                                                          >{p}</button>
                                                    )}
                                                <button
                                                    onClick={() => setBolumPage(p => Math.min(toplamSayfa, p + 1))}
                                                    disabled={gecerliSayfa === toplamSayfa}
                                                    style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 6, background: gecerliSayfa === toplamSayfa ? '#f3f4f6' : '#fff', color: gecerliSayfa === toplamSayfa ? '#d1d5db' : '#374151', cursor: gecerliSayfa === toplamSayfa ? 'default' : 'pointer', fontSize: 12 }}
                                                >›</button>
                                                <button
                                                    onClick={() => setBolumPage(toplamSayfa)}
                                                    disabled={gecerliSayfa === toplamSayfa}
                                                    style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 6, background: gecerliSayfa === toplamSayfa ? '#f3f4f6' : '#fff', color: gecerliSayfa === toplamSayfa ? '#d1d5db' : '#374151', cursor: gecerliSayfa === toplamSayfa ? 'default' : 'pointer', fontSize: 12 }}
                                                    title="Son sayfa"
                                                >»</button>
                                            </div>
                                        </div>
                                    )}
                                    {!topluDuzenleMod && toplamSayfa <= 1 && filtered.length > 0 && (
                                        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 8, textAlign: 'right' }}>
                                            Toplam {filtered.length} bölüm
                                        </div>
                                    )}
                                    </>
                                );
                            })()}
                        </>
                    ) : bolumGorunumu === 'tablo' ? (
                        /* ── Tablo Görünümü ── */
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                                <select
                                    className="kka-filter-select"
                                    style={{ minWidth: 200 }}
                                    value={bolumTabloBlokId ?? ''}
                                    onChange={e => setBolumTabloBlokId(e.target.value ? Number(e.target.value) : null)}
                                >
                                    <option value="">{t('kka.blokSecin')}</option>
                                    {arsa.bloklar.map(b => (
                                        <option key={b.blokID} value={b.blokID}>{b.blokAdi}</option>
                                    ))}
                                </select>
                                <div style={{ display: 'flex', gap: 6, alignItems: 'center', background: '#f4f6fb', border: '1px solid #e2e6f0', borderRadius: 8, padding: '5px 12px' }}>
                                    <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginRight: 4 }}>Göster:</span>
                                    {([
                                        { key: 'fiyat', label: 'Fiyat', color: '#1a2340' },
                                        { key: 'cephe', label: 'Cephe', color: '#0369a1' },
                                        { key: 'ortaklar', label: 'Ortaklar / Paylaşım', color: '#15803d' },
                                    ] as { key: keyof typeof tabloGoster; label: string; color: string }[]).map(({ key, label, color }) => (
                                        <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', userSelect: 'none', padding: '2px 8px', borderRadius: 6, background: tabloGoster[key] ? `${color}12` : 'transparent', border: `1px solid ${tabloGoster[key] ? color : 'transparent'}`, transition: 'all 0.15s' }}>
                                            <input
                                                type="checkbox"
                                                checked={tabloGoster[key]}
                                                onChange={e => setTabloGoster(g => ({ ...g, [key]: e.target.checked }))}
                                                style={{ accentColor: color, width: 13, height: 13 }}
                                            />
                                            <span style={{ fontSize: 12, fontWeight: 600, color: tabloGoster[key] ? color : '#9ca3af' }}>{label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            {/* Sürükle-bırak yan panel */}
                            {(arsa.ortaklar.length > 0 || arsa.muteahhitler.length > 0) && (
                                <div style={{ float: 'right', width: 210, marginLeft: 16, marginBottom: 16, flexShrink: 0 }}>
                                    {renderSurukleBirakPaneli()}
                                </div>
                            )}
                            {!bolumTabloBlokId ? (
                                <div className="kka-empty">Lütfen bir blok seçin.</div>
                            ) : (() => {
                                const blok = arsa.bloklar.find(b => b.blokID === bolumTabloBlokId);
                                if (!blok) return null;
                                const katlar = [...blok.katlar].sort((a, b) => b.siraNo - a.siraNo);
                                if (katlar.length === 0) return <div className="kka-empty">Bu blokta kat tanımlı değil.</div>;
                                return (
                                    <div className="kka-bolum-tablo-wrap" style={{ overflow: 'auto' }}>
                                        {katlar.map(kat => {
                                            const bolumlerInKat = arsa.bolumler.filter(b => b.katID === kat.katID);
                                            return (
                                                <div key={kat.katID} className="kka-bolum-tablo-row">
                                                    <div className="kka-bolum-tablo-kat-label">
                                                        <span>{kat.katAdi}</span>
                                                        <button
                                                            className="kka-kat-plan-btn"
                                                            title={t('btn.goruntule')}
                                                            onClick={() => setPlanModalKat({ blok, kat })}
                                                        >
                                                            <i className="fas fa-map" style={{ color: '#aaa' }}></i>
                                                        </button>
                                                    </div>
                                                    <div className="kka-bolum-tablo-cells">
                                                        {bolumlerInKat.length === 0 ? (
                                                            <div className="kka-bolum-tablo-empty-kat">–</div>
                                                        ) : bolumlerInKat.map(b => {
                                                            const hasPaylasim = b.paylasimlar.length > 0;
                                                            const paylasılan = bolumPaylasılanAlan(b);
                                                            const toplamOran = b.paylasimlar.reduce((acc, p) => acc + p.oranYuzde, 0);
                                                            const isHovered = hoveredBolumID === b.bolumID;
                                                            const cepheler = [b.cephe1?.cepheAdi, b.cephe2?.cepheAdi, b.cephe3?.cepheAdi, b.cephe4?.cepheAdi].filter(Boolean) as string[];

                                                            // Sürükle-bırak pasiflik kontrolü
                                                            const tamDolu = toplamOran >= 100;
                                                            const butceDisi = draggingKisi !== null && draggingKisi.kalanPay * 1.05 < b.fiyat;
                                                            const isPasif = draggingKisi !== null && (tamDolu || butceDisi);
                                                            const isDragOver = draggingKisi !== null && !isPasif;

                                                            return (
                                                                <div
                                                                    key={b.bolumID}
                                                                    className={`kka-bolum-cell${hasPaylasim && !isPasif ? ' paylasimli' : ''}`}
                                                                    style={isPasif ? { opacity: 0.35, cursor: 'not-allowed', filter: 'grayscale(60%)' } : undefined}
                                                                    onMouseEnter={() => setHoveredBolumID(b.bolumID)}
                                                                    onMouseLeave={() => setHoveredBolumID(null)}
                                                                    onDragOver={e => { if (!isPasif) e.preventDefault(); }}
                                                                    onDrop={e => {
                                                                        e.preventDefault();
                                                                        if (!draggingKisi || isPasif) return;
                                                                        openPaylasimCreate(b.bolumID, { tip: draggingKisi.tip, id: draggingKisi.id });
                                                                        setDraggingKisi(null);
                                                                    }}
                                                                >
                                                                    <div className="kka-bolum-cell-name">
                                                                        {b.bolumAdi}
                                                                        {tamDolu && <TamamIsareti title="Paylaşım tamamlandı" />}
                                                                    </div>
                                                                    {b.bolumTipi && <div className="kka-bolum-cell-tip">{b.bolumTipi}</div>}
                                                                    <div className="kka-bolum-cell-m2">{fmt(b.m2)} m²</div>
                                                                    {tabloGoster.fiyat && b.fiyat > 0 && (
                                                                        <div style={{ fontSize: '0.72rem', color: '#1a2340', fontWeight: 600, marginTop: 2 }}>
                                                                            {fmt(b.fiyat)} ₺
                                                                        </div>
                                                                    )}
                                                                    {tabloGoster.cephe && cepheler.length > 0 && (
                                                                        <div style={{ fontSize: '0.67rem', color: '#0369a1', marginTop: 3, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                                                            {cepheler.map(c => (
                                                                                <span key={c} style={{ background: '#e0f2fe', borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap' }}>{c}</span>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    {tabloGoster.ortaklar && hasPaylasim && (
                                                                        <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                            {b.paylasimlar.map(p => {
                                                                                const ad = p.ortak?.adSoyad ?? p.muteahhit?.adSoyad ?? '–';
                                                                                const tip = p.ortak ? 'O' : 'M';
                                                                                return (
                                                                                    <div key={p.paylasimID} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                                                                        <span style={{ fontSize: '0.6rem', fontWeight: 700, background: tip === 'O' ? '#dcfce7' : '#fef9c3', color: tip === 'O' ? '#15803d' : '#854d0e', borderRadius: 3, padding: '1px 4px', flexShrink: 0 }}>{tip}</span>
                                                                                        <span style={{ fontSize: '0.67rem', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }} title={ad}>{ad}</span>
                                                                                        <span style={{ fontSize: '0.67rem', fontWeight: 700, color: '#15803d', flexShrink: 0 }}>%{p.oranYuzde}</span>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                            <div style={{ fontSize: '0.65rem', color: '#6b7280', borderTop: '1px solid #d1fae5', marginTop: 2, paddingTop: 2 }}>
                                                                                Toplam: <strong style={{ color: toplamOran >= 100 ? '#dc2626' : '#15803d' }}>%{toplamOran.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}</strong>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {!tabloGoster.ortaklar && hasPaylasim && (
                                                                        <div className="kka-bolum-cell-oran">%{toplamOran.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}</div>
                                                                    )}
                                                                    <div className="kka-bolum-cell-actions">
                                                                        <button className="kka-icon-btn edit" onClick={() => openBolumEdit(b)} title={t('btn.duzenle')}><i className="fas fa-pen" style={{ fontSize: '0.7rem' }}></i></button>
                                                                        <button className="kka-icon-btn del" onClick={() => deleteBolum(b.bolumID)} title={t('btn.sil')}><i className="fas fa-trash" style={{ fontSize: '0.7rem' }}></i></button>
                                                                        <button className="kka-icon-btn" onClick={() => openPaylasimCreate(b.bolumID)} title={t('btn.ekle')} style={{ color: '#cd6906' }}><i className="fas fa-share-alt" style={{ fontSize: '0.7rem' }}></i></button>
                                                                    </div>
                                                                    {/* Hover Tooltip */}
                                                                    {isHovered && (
                                                                        <div className="kka-bolum-tooltip">
                                                                            <div className="kka-bolum-tooltip-header">
                                                                                <strong>{b.bolumAdi}</strong>
                                                                                {b.bolumTipi && <span> – {b.bolumTipi}</span>}
                                                                            </div>
                                                                            <div className="kka-bolum-tooltip-info">
                                                                                <span>{fmt(b.m2)} m²</span>
                                                                                <span>{fmt(b.fiyat)} ₺</span>
                                                                            </div>
                                                                            {b.paylasimlar.length > 0 ? (
                                                                                <>
                                                                                    <div className="kka-bolum-tooltip-paylasim-title">Paylaşımlar</div>
                                                                                    {b.paylasimlar.map(p => (
                                                                                        <div key={p.paylasimID} className="kka-bolum-tooltip-paylasim-row">
                                                                                            <span>{p.ortak?.adSoyad ?? p.muteahhit?.adSoyad ?? '–'}</span>
                                                                                            <span>%{p.oranYuzde}</span>
                                                                                            <span>{fmt(b.fiyat * p.oranYuzde / 100)} ₺</span>
                                                                                        </div>
                                                                                    ))}
                                                                                    <div className="kka-bolum-tooltip-toplam">
                                                                                        Paylaşılan: <strong>{fmt(paylasılan)} m²</strong>&nbsp;(%{fmt(toplamOran)})
                                                                                    </div>
                                                                                </>
                                                                            ) : (
                                                                                <div className="kka-bolum-tooltip-nopaylasim">Paylaşım yapılmamış</div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()}
                        </>
                    ) : bolumGorunumu === 'gorunum' ? (
                        /* ── Görünüm: fotoğraf üzerinde paylaşım ── */
                        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <KatKarsiligGorunumPaylasim
                                    arsaId={arsaId}
                                    bolumler={arsa.bolumler.map(b => ({
                                        bolumID: b.bolumID, bolumAdi: b.bolumAdi, bolumTipi: b.bolumTipi, m2: b.m2, fiyat: b.fiyat,
                                        paylasimlar: b.paylasimlar.map(p => ({
                                            paylasimID: p.paylasimID,
                                            ad: p.ortak?.adSoyad ?? p.muteahhit?.adSoyad ?? '–',
                                            tip: p.ortakID != null ? 'O' as const : 'M' as const,
                                            oranYuzde: p.oranYuzde,
                                        })),
                                    }))}
                                    surukleniyor={draggingKisi !== null}
                                    birakilamaz={bolumID => {
                                        const b = arsa.bolumler.find(x => x.bolumID === bolumID);
                                        return !b || bolumDropPasifMi(b);
                                    }}
                                    onBirak={bolumID => {
                                        if (!draggingKisi) return;
                                        openPaylasimCreate(bolumID, { tip: draggingKisi.tip, id: draggingKisi.id });
                                        setDraggingKisi(null);
                                    }}
                                    onPaylasimEkle={bolumID => openPaylasimCreate(bolumID)}
                                />
                            </div>
                            {(arsa.ortaklar.length > 0 || arsa.muteahhitler.length > 0) && (
                                <div style={{ width: 210, flexShrink: 0, position: 'sticky', top: 16 }}>
                                    {renderSurukleBirakPaneli()}
                                </div>
                            )}
                        </div>
                    ) : (
                        /* ── Vaziyet Görünümü ── */
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                                <select
                                    className="kka-filter-select"
                                    style={{ minWidth: 200 }}
                                    value={vaziyetGorunumBlokId ?? ''}
                                    onChange={e => {
                                        const id = e.target.value ? Number(e.target.value) : null;
                                        setVaziyetGorunumBlokId(id);
                                        const blok = id != null ? arsa.bloklar.find(b => b.blokID === id) : undefined;
                                        setVaziyetGorunumKatIdler(blok ? new Set(blok.katlar.map(k => k.katID)) : new Set());
                                        setVaziyetKatFiltreAcik(false);
                                    }}
                                >
                                    <option value="">{t('kka.blokSecin')}</option>
                                    {arsa.bloklar.map(b => (
                                        <option key={b.blokID} value={b.blokID}>{b.blokAdi}</option>
                                    ))}
                                </select>

                                {vaziyetGorunumBlokId && (() => {
                                    const blok = arsa.bloklar.find(b => b.blokID === vaziyetGorunumBlokId);
                                    if (!blok) return null;
                                    const katlar = [...blok.katlar].sort((a, c) => a.siraNo - c.siraNo);
                                    const tumSecili = katlar.length > 0 && katlar.every(k => vaziyetGorunumKatIdler.has(k.katID));
                                    return (
                                        <div style={{ position: 'relative' }}>
                                            <button
                                                className="kka-filter-select"
                                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                                                onClick={() => setVaziyetKatFiltreAcik(v => !v)}
                                            >
                                                <i className="fas fa-layer-group" style={{ color: '#6b7280' }}></i>
                                                {tumSecili ? 'Tüm Katlar' : vaziyetGorunumKatIdler.size === 0 ? 'Kat seçin' : `${vaziyetGorunumKatIdler.size} kat seçili`}
                                                <i className="fas fa-caret-down" style={{ fontSize: 11 }}></i>
                                            </button>
                                            {vaziyetKatFiltreAcik && (
                                                <div
                                                    onMouseLeave={() => setVaziyetKatFiltreAcik(false)}
                                                    style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 40, background: '#fff', border: '1px solid #e2e6f0', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.14)', padding: 10, minWidth: 200, maxHeight: 280, overflowY: 'auto' }}
                                                >
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', borderBottom: '1px solid #f1f5f9', marginBottom: 4, userSelect: 'none' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={tumSecili}
                                                            onChange={e => setVaziyetGorunumKatIdler(e.target.checked ? new Set(katlar.map(k => k.katID)) : new Set())}
                                                        />
                                                        Tümünü Seç
                                                    </label>
                                                    {katlar.map(k => (
                                                        <label key={k.katID} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px', fontSize: 12.5, cursor: 'pointer', userSelect: 'none' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={vaziyetGorunumKatIdler.has(k.katID)}
                                                                onChange={e => setVaziyetGorunumKatIdler(prev => {
                                                                    const next = new Set(prev);
                                                                    if (e.target.checked) next.add(k.katID); else next.delete(k.katID);
                                                                    return next;
                                                                })}
                                                            />
                                                            {k.katAdi}
                                                        </label>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>

                            {!vaziyetGorunumBlokId ? (
                                <div className="kka-empty">Lütfen bir blok seçin.</div>
                            ) : (() => {
                                const blok = arsa.bloklar.find(b => b.blokID === vaziyetGorunumBlokId);
                                if (!blok) return null;
                                const katlar = [...blok.katlar].sort((a, c) => c.siraNo - a.siraNo).filter(k => vaziyetGorunumKatIdler.has(k.katID));
                                if (katlar.length === 0) return <div className="kka-empty">Seçili kat yok.</div>;
                                return (
                                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 20 }}>
                                            <PaylasimRenkSkalasi ek="Ayrıntı için bölümün üzerine gelin ya da tıklayın" />
                                            {katlar.map(kat => (
                                                <div key={kat.katID} style={{ border: '1px solid #e4eaf4', borderRadius: 10 }}>
                                                    <div style={{ background: '#1a2340', color: '#fff', padding: '8px 16px', fontWeight: 700, fontSize: 13, borderRadius: '9px 9px 0 0' }}>
                                                        {blok.blokAdi} — {kat.katAdi}
                                                    </div>
                                                    <KatCizimPanel
                                                        key={kat.katID}
                                                        katId={kat.katID}
                                                        blokAdi={blok.blokAdi}
                                                        katAdi={kat.katAdi}
                                                        katEn={kat.en ?? blok.en}
                                                        katBoy={kat.boy ?? blok.boy}
                                                        arsaId={arsaId}
                                                        cepheler={arsa.cepheler.map(c => ({ cepheAdi: c.cepheAdi, yon: c.yon }))}
                                                        bolumler={arsa.bolumler
                                                            .filter(b => b.katID === kat.katID)
                                                            .map(b => ({
                                                                bolumID: b.bolumID,
                                                                bolumAdi: b.bolumAdi,
                                                                bolumTipi: b.bolumTipi,
                                                                fiyat: b.fiyat,
                                                                m2: b.m2,
                                                                xVal: b.xVal,
                                                                yVal: b.yVal,
                                                                paylasimlar: b.paylasimlar.map(p => ({
                                                                    adSoyad: p.ortak?.adSoyad ?? p.muteahhit?.adSoyad ?? '–',
                                                                    oranYuzde: p.oranYuzde,
                                                                    tip: p.ortakID != null ? 'O' as const : 'M' as const,
                                                                })),
                                                            }))}
                                                        onBolumKoordinatGuncellendi={(bolumID, xVal, yVal) => {
                                                            setArsa(prev => prev ? {
                                                                ...prev,
                                                                bolumler: prev.bolumler.map(b => b.bolumID === bolumID ? { ...b, xVal, yVal } : b),
                                                            } : prev);
                                                        }}
                                                        readOnly
                                                        pinStyle="durum"
                                                        onPaylasimEkle={bolumID => openPaylasimCreate(bolumID)}
                                                        onBolumDropTarget={bolumID => {
                                                            if (!draggingKisi) return;
                                                            const b = arsa.bolumler.find(x => x.bolumID === bolumID);
                                                            if (!b || bolumDropPasifMi(b)) return;
                                                            openPaylasimCreate(bolumID, { tip: draggingKisi.tip, id: draggingKisi.id });
                                                            setDraggingKisi(null);
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        {(arsa.ortaklar.length > 0 || arsa.muteahhitler.length > 0) && (
                                            <div style={{ width: 210, flexShrink: 0, position: 'sticky', top: 16 }}>
                                                {renderSurukleBirakPaneli()}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </>
                    )}
                </div>
            )}

            {/* ── Adım 7: Finansal Kontrol ── */}
            {activeTab === 'kontrol' && (
                <div className="kka-tab-section">
                    <div className="kka-section-header">
                        <h4>Hak Ediş Kontrolü</h4>
                        <div style={{ fontSize: 12.5, color: '#64748b' }}>
                            Toplam değer <b style={{ color: '#1a2340' }}>{paraFmt(arsa.arsaTutar ?? 0)}</b> ·
                            dağıtılan <b style={{ color: '#1a2340' }}>{paraFmt(taraflar.reduce((x, y) => x + (y.aldigi ?? 0), 0))}</b>
                        </div>
                    </div>
                    {taraflar.length === 0 ? (
                        <div className="kka-empty">Önce Adım 2'de tarafları tanımlayın.</div>
                    ) : (
                        <table className="kka-sub-table">
                            <thead>
                                <tr>
                                    <th>Taraf</th><th>Tür</th><th>Hak Ediş (%)</th><th>Hak Ettiği</th><th>Aldığı</th><th>Fark</th><th>Durum</th>
                                </tr>
                            </thead>
                            <tbody>
                                {taraflar.map((x, i) => {
                                    const d = tarafDurumu(x);
                                    const fark = (x.aldigi ?? 0) - (x.pay ?? 0);
                                    return (
                                        <tr key={i}>
                                            <td><strong>{x.ad}</strong>{d === 'tamam' && <TamamIsareti title="Hak ettiği payı aldı" />}</td>
                                            <td>{x.tur}</td>
                                            <td>%{x.yuzde.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}</td>
                                            <td>{x.pay ? paraFmt(x.pay) : '–'}</td>
                                            <td>{paraFmt(x.aldigi ?? 0)}</td>
                                            <td style={{ fontWeight: 700, color: Math.abs(fark) < 1 ? '#16a34a' : fark < 0 ? '#b45309' : '#b91c1c' }}>
                                                {x.pay ? `${fark > 0 ? '+' : ''}${paraFmt(fark)}` : '–'}
                                            </td>
                                            <td>
                                                <span className={`kka-kontrol-durum ${d}`}>
                                                    {d === 'tamam' ? 'Tamam' : d === 'eksik' ? 'Eksik' : d === 'fazla' ? 'Fazla' : 'Hak ediş yok'}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16, alignItems: 'center' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginRight: 4 }}><i className="fas fa-file-pdf" style={{ marginRight: 5 }}></i>Raporlar:</span>
                        <button className="kka-btn-secondary" onClick={exportOrtaklarPdf}>{t('kka.arsaOrtaklariRaporu', 'Arsa Ortağı Raporu')}</button>
                        <button className="kka-btn-secondary" onClick={exportGayrimenkullerPdf}>{t('kka.gayrimenkullerRaporu', 'Gayrimenkuller Raporu')}</button>
                        <button className="kka-btn-secondary" onClick={exportPaylasimlarPdf}>{t('kka.bolumPaylasimListesi', 'Bölüm Paylaşım Listesi')}</button>
                        <button className="kka-btn-secondary" onClick={() => { setBlokRaporPayliBlokId(null); setShowBlokRaporPayliModal(true); }}>{t('kka.blokGorunumRaporuPayli', 'Blok Görünüm Raporu (Paylaşımlı)')}</button>
                    </div>
                </div>
            )}

            {/* ── Adım 7: Bölüm bazında paylaşım listesi ── */}
            {activeTab === 'kontrol' && (
                <div className="kka-tab-section">
                    <div className="kka-section-header">
                        <h4>{t('kka.paylasimlar')}</h4>
                        <button
                            className="kka-btn-secondary"
                            onClick={fetchOneri}
                            disabled={oneriLoading}
                            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                        >
                            {oneriLoading ? <><i className="fas fa-spinner fa-spin" style={{ color: '#1a2340', marginRight: 6 }}></i>{t('msg.hesaplaniyor', 'Hesaplanıyor...')}</> : <><i className="fas fa-lightbulb" style={{ color: '#1a2340', marginRight: 6 }}></i>{t('kka.dagitimOneriHazirla', 'Dağıtım Önerisi Hazırla')}</>}
                        </button>
                    </div>
                    {arsa.bolumler.length === 0 ? (
                        <div className="kka-empty">Önce Bölüm sekmesinden bölüm ekleyin.</div>
                    ) : arsa.bolumler.map(b => {
                        const toplamOran = b.paylasimlar.reduce((acc, p) => acc + p.oranYuzde, 0);
                        const tamamlandi = toplamOran >= 100;
                        const cepheler = [b.cephe1, b.cephe2, b.cephe3, b.cephe4].filter(Boolean).map(c => c!.cepheAdi).join(', ');
                        return (
                        <div key={b.bolumID} className={`kka-paylasim-bolum${tamamlandi ? ' tamamlandi' : ''}`}>
                            <div className="kka-paylasim-bolum-header">
                                <div>
                                    <div className="kka-paylasim-bolum-title">
                                        <strong>{b.bolumAdi}{b.bolumTipi ? ` – ${b.bolumTipi}` : ''}</strong>
                                        {tamamlandi && <span className="kka-paylasim-tamamlandi-badge"><i className="fas fa-check"></i> Tamamlandı</span>}
                                    </div>
                                    <div className="kka-paylasim-bolum-meta">
                                        {b.blok && <span><i className="fas fa-building"></i> {b.blok.blokAdi}</span>}
                                        {b.kat && <span><i className="fas fa-layer-group"></i> {b.kat.katAdi}</span>}
                                        {cepheler && <span><i className="fas fa-compass"></i> {cepheler}</span>}
                                        <span><i className="fas fa-ruler-combined"></i> {b.m2.toLocaleString('tr-TR')} m²</span>
                                        <span><i className="fas fa-tag"></i> {fmt(b.fiyat)} ₺</span>
                                        <span className={`kka-paylasim-oran-badge${tamamlandi ? ' tam' : toplamOran > 0 ? ' kismi' : ''}`}>%{toplamOran.toLocaleString('tr-TR')} dağıtıldı</span>
                                    </div>
                                </div>
                                <button className="kka-btn-primary" style={{ fontSize: '0.8rem', padding: '6px 12px', flexShrink: 0 }} onClick={() => openPaylasimCreate(b.bolumID)}>+ Paylaşım Ekle</button>
                            </div>
                            {b.paylasimlar.length === 0 ? (
                                <p style={{ color: '#bbb', fontSize: '0.85rem', marginLeft: 8 }}>Henüz paylaşım yok.</p>
                            ) : (
                                <table className="kka-sub-table">
                                    <thead><tr><th>{t('kka.kisiKurum', 'Kişi / Kurum')}</th><th>{t('label.tur')}</th><th>{t('kka.oranYuzde', 'Oran (%)')}</th><th>{t('kka.payTL', 'Pay (TL)')}</th><th>{t('label.not', 'Not')}</th><th></th></tr></thead>
                                    <tbody>
                                        {b.paylasimlar.map(p => (
                                            <tr key={p.paylasimID}>
                                                <td><strong>{p.ortak?.adSoyad ?? p.muteahhit?.adSoyad ?? '–'}</strong></td>
                                                <td>{p.ortak ? t('kka.arsaOrtagi', 'Arsa Ortağı') : p.muteahhit ? t('kka.muteahhit', 'Müteahhit') : '–'}</td>
                                                <td>%{p.oranYuzde.toLocaleString('tr-TR')}</td>
                                                <td>{fmt(b.fiyat * p.oranYuzde / 100)} ₺</td>
                                                <td>{p.aciklamaNot ?? '–'}</td>
                                                <td>
                                                    <div className="kka-td-actions">
                                                        <button className="kka-icon-btn edit" onClick={() => openPaylasimEdit(b.bolumID, p)}><i className="fas fa-pen" style={{ color: '#1a2340' }}></i></button>
                                                        <button className="kka-icon-btn del" onClick={() => deletePaylasim(b.bolumID, p.paylasimID)}><i className="fas fa-trash" style={{ color: '#1a2340' }}></i></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        );
                    })}
                </div>
            )}

            {/* �?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?
                MODALLER
            �?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�? */}

            {/* Blok Raporu Seçim Modalı */}
            {showBlokRaporModal && arsa && (
                <div className="kka-modal-overlay" onClick={() => setShowBlokRaporModal(false)}>
                    <div className="kka-modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
                        <div className="kka-modal-header">
                            <h3><i className="fas fa-th-large" style={{ marginRight: 8, color: '#cd6906' }}></i>Blok Görünüm Raporu</h3>
                            <button className="kka-modal-close" onClick={() => setShowBlokRaporModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-modal-body">
                            <p style={{ fontSize: '0.88rem', color: '#555', marginBottom: 14 }}>Rapor alınacak bloğu seçin:</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {arsa.bloklar.map(b => (
                                    <button
                                        key={b.blokID}
                                        onClick={() => setBlokRaporBlokId(b.blokID)}
                                        style={{
                                            padding: '10px 14px', borderRadius: 7, cursor: 'pointer', textAlign: 'left',
                                            fontSize: '0.88rem', fontWeight: blokRaporBlokId === b.blokID ? 700 : 400,
                                            background: blokRaporBlokId === b.blokID ? '#eef3ff' : '#f9fafb',
                                            border: `1.5px solid ${blokRaporBlokId === b.blokID ? '#1a2340' : '#e0e4ef'}`,
                                            color: '#1a2340',
                                        }}
                                    >
                                        <i className="fas fa-layer-group" style={{ marginRight: 8, opacity: 0.55 }}></i>
                                        {b.blokAdi}{b.katSayisi ? ` (${b.katSayisi} kat)` : ''}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="kka-modal-footer">
                            <button className="kka-btn-secondary" onClick={() => setShowBlokRaporModal(false)}>{t('btn.iptal')}</button>
                            <button
                                className="kka-btn-primary"
                                disabled={blokRaporBlokId === null}
                                onClick={() => { setShowBlokRaporModal(false); exportBlokGorunumPdf(blokRaporBlokId!); }}
                            >
                                <i className="fas fa-file-pdf" style={{ marginRight: 6 }}></i>{t('btn.pdfIndir', 'PDF İndir')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Blok Paylaşımlı Raporu Seçim Modalı */}
            {showBlokRaporPayliModal && arsa && (
                <div className="kka-modal-overlay" onClick={() => setShowBlokRaporPayliModal(false)}>
                    <div className="kka-modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
                        <div className="kka-modal-header">
                            <h3><i className="fas fa-th-large" style={{ marginRight: 8, color: '#27ae60' }}></i>Blok Görünüm Raporu (Paylaşımlı)</h3>
                            <button className="kka-modal-close" onClick={() => setShowBlokRaporPayliModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-modal-body">
                            <p style={{ fontSize: '0.88rem', color: '#555', marginBottom: 14 }}>Rapor alınacak bloğu seçin:</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {arsa.bloklar.map(b => (
                                    <button
                                        key={b.blokID}
                                        onClick={() => setBlokRaporPayliBlokId(b.blokID)}
                                        style={{
                                            padding: '10px 14px', borderRadius: 7, cursor: 'pointer', textAlign: 'left',
                                            fontSize: '0.88rem', fontWeight: blokRaporPayliBlokId === b.blokID ? 700 : 400,
                                            background: blokRaporPayliBlokId === b.blokID ? '#edfaf3' : '#f9fafb',
                                            border: `1.5px solid ${blokRaporPayliBlokId === b.blokID ? '#27ae60' : '#e0e4ef'}`,
                                            color: '#1a2340',
                                        }}
                                    >
                                        <i className="fas fa-layer-group" style={{ marginRight: 8, opacity: 0.55 }}></i>
                                        {b.blokAdi}{b.katSayisi ? ` (${b.katSayisi} kat)` : ''}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="kka-modal-footer">
                            <button className="kka-btn-secondary" onClick={() => setShowBlokRaporPayliModal(false)}>{t('btn.iptal')}</button>
                            <button
                                className="kka-btn-primary"
                                disabled={blokRaporPayliBlokId === null}
                                onClick={() => { setShowBlokRaporPayliModal(false); exportBlokGorunumPayliPdf(blokRaporPayliBlokId!); }}
                            >
                                <i className="fas fa-file-pdf" style={{ marginRight: 6 }}></i>{t('btn.pdfIndir', 'PDF İndir')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Vaziyet Planları Blok Seçim Modalı */}
            {showVaziyetModal && arsa && (
                <div className="kka-modal-overlay" onClick={() => setShowVaziyetModal(false)}>
                    <div className="kka-modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
                        <div className="kka-modal-header">
                            <h3><i className="fas fa-map-marked-alt" style={{ marginRight: 8, color: '#8e44ad' }}></i>Vaziyet Planları</h3>
                            <button className="kka-modal-close" onClick={() => setShowVaziyetModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-modal-body">
                            <p style={{ fontSize: '0.88rem', color: '#555', marginBottom: 14 }}>Kat planları PDF'e aktarılacak bloğu seçin:</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {arsa.bloklar.map(b => (
                                    <button
                                        key={b.blokID}
                                        onClick={() => setVaziyetBlokId(b.blokID)}
                                        style={{
                                            padding: '10px 14px', borderRadius: 7, cursor: 'pointer', textAlign: 'left',
                                            fontSize: '0.88rem', fontWeight: vaziyetBlokId === b.blokID ? 700 : 400,
                                            background: vaziyetBlokId === b.blokID ? '#f5eeff' : '#f9fafb',
                                            border: `1.5px solid ${vaziyetBlokId === b.blokID ? '#8e44ad' : '#e0e4ef'}`,
                                            color: '#1a2340',
                                        }}
                                    >
                                        <i className="fas fa-layer-group" style={{ marginRight: 8, opacity: 0.55 }}></i>
                                        {b.blokAdi}{b.katSayisi ? ` (${b.katSayisi} kat)` : ''}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="kka-modal-footer">
                            <button className="kka-btn-secondary" onClick={() => setShowVaziyetModal(false)}>{t('btn.iptal')}</button>
                            <button
                                className="kka-btn-primary"
                                disabled={vaziyetBlokId === null}
                                style={{ background: '#8e44ad', borderColor: '#8e44ad' }}
                                onClick={() => { setShowVaziyetModal(false); exportVaziyetPlanPdf(vaziyetBlokId!); }}
                            >
                                <i className="fas fa-file-pdf" style={{ marginRight: 6 }}></i>{t('btn.pdfIndir', 'PDF İndir')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Kat Plan Modalı */}            {planModalKat && arsa && (                <div className="kka-modal-overlay" onClick={() => setPlanModalKat(null)}>
                    <div className="kka-plan-modal" onClick={e => e.stopPropagation()}>
                        <div className="kka-plan-modal-header">
                            <span><i className="fas fa-map" style={{ marginRight: 8, opacity: 0.7 }}></i>{planModalKat.blok.blokAdi} — {planModalKat.kat.katAdi}</span>
                            <button className="kka-modal-close" onClick={() => setPlanModalKat(null)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-plan-modal-body">
                            <KatCizimPanel
                                key={planModalKat.kat.katID}
                                katId={planModalKat.kat.katID}
                                blokAdi={planModalKat.blok.blokAdi}
                                katAdi={planModalKat.kat.katAdi}
                                katEn={planModalKat.kat.en ?? planModalKat.blok.en}
                                katBoy={planModalKat.kat.boy ?? planModalKat.blok.boy}
                                blokKatlar={planModalKat.blok.katlar.map(k => ({ katID: k.katID, katAdi: k.katAdi }))}
                                arsaId={arsaId}
                                cepheler={arsa.cepheler.map(c => ({ cepheAdi: c.cepheAdi, yon: c.yon }))}
                                bolumler={arsa.bolumler
                                    .filter(b => b.katID === planModalKat.kat.katID)
                                    .map(b => ({
                                        bolumID: b.bolumID,
                                        bolumAdi: b.bolumAdi,
                                        xVal: b.xVal,
                                        yVal: b.yVal,
                                        paylasimlar: b.paylasimlar.map(p => ({
                                            adSoyad: p.ortak?.adSoyad ?? p.muteahhit?.adSoyad ?? '–',
                                            oranYuzde: p.oranYuzde,
                                        })),
                                    }))}
                                onBolumKoordinatGuncellendi={(bolumID, xVal, yVal) => {
                                    setArsa(prev => {
                                        if (!prev) return prev;
                                        return {
                                            ...prev,
                                            bolumler: prev.bolumler.map(b =>
                                                b.bolumID === bolumID ? { ...b, xVal, yVal } : b
                                            ),
                                        };
                                    });
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Cepheler Modalı (Bölümler sekmesindeki "Cepheler" butonundan açılır) */}
            {showCephelerModal && arsa && (
                <div className="kka-modal-overlay" onClick={() => setShowCephelerModal(false)}>
                    <div className="kka-plan-modal" style={{ maxWidth: 640, maxHeight: '80vh' }} onClick={e => e.stopPropagation()}>
                        <div className="kka-plan-modal-header">
                            <span><i className="fas fa-compass" style={{ marginRight: 8, opacity: 0.85 }}></i>{t('kka.cepheler', 'Cepheler')}</span>
                            <button className="kka-modal-close" onClick={() => setShowCephelerModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-plan-modal-body" style={{ padding: 20 }}>
                            {renderCephelerIcerik()}
                        </div>
                    </div>
                </div>
            )}

            {/* Müteahhit Modal */}
            {muteahhitModal && (
                <div className="kka-modal-overlay" onClick={() => setMuteahhitModal(false)}>
                    <div className="kka-modal" onClick={e => e.stopPropagation()}>
                        <div className="kka-modal-header">
                            <h3>{muteahhitEdit ? t('kka.muteahhitiDuzenle', 'Müteahhiti Düzenle') : t('kka.muteahhitEkle', 'Müteahhit Ekle')}</h3>
                            <button className="kka-modal-close" onClick={() => setMuteahhitModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-modal-body">
                            {modalError && <div style={{ color: '#e74c3c', marginBottom: 10, fontSize: '0.87rem' }}>{modalError}</div>}
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>{t('label.adSoyad', 'Ad Soyad')} *</label>
                                    <input value={muteahhitForm.adSoyad} onChange={e => setMuteahhitForm(f => ({ ...f, adSoyad: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>{t('label.telefon', 'Telefon')}</label>
                                    <input value={muteahhitForm.telefon} onChange={e => setMuteahhitForm(f => ({ ...f, telefon: e.target.value }))} />
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>{t('label.eposta', 'E-Posta')}</label>
                                    <input value={muteahhitForm.eposta} onChange={e => setMuteahhitForm(f => ({ ...f, eposta: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>{t('kka.vergiNo', 'Vergi No')}</label>
                                    <input value={muteahhitForm.vergiNo} onChange={e => setMuteahhitForm(f => ({ ...f, vergiNo: e.target.value }))} />
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>{t('kka.hisseM2', 'Hisse m²')}</label>
                                    <input type="number" value={muteahhitForm.hisseMetre} onChange={e => setMuteahhitForm(f => ({ ...f, hisseMetre: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>{t('kka.payYuzde', 'Pay (%)')} <span style={{ fontSize: '0.78rem', color: '#888' }}>- {t('kka.manuel', 'Manuel')}</span></label>
                                    <input type="number" value={muteahhitForm.payYuzde} onChange={e => setMuteahhitForm(f => ({ ...f, payYuzde: e.target.value }))} />
                                </div>
                            </div>
                            {muteahhitEdit && (
                                <div className="kka-form-row">
                                    <div className="kka-form-group">
                                        <label>{t('kka.toplamPayYuzde', 'Toplam Pay (%)')} <span style={{ fontSize: '0.78rem', color: '#888' }}>- {t('kka.otomatik', 'Otomatik')}</span></label>
                                        <input readOnly style={{ background: '#f4f6fb', color: '#555', cursor: 'not-allowed' }} value={`%${muteahhitEdit.toplamPayYuzde.toLocaleString('tr-TR')}`} />
                                    </div>
                                    <div className="kka-form-group">
                                        <label>{t('kka.payTL', 'Pay (TL)')} <span style={{ fontSize: '0.78rem', color: '#888' }}>- {t('kka.otomatik', 'Otomatik')}</span></label>
                                        <input readOnly style={{ background: '#f4f6fb', color: '#555', cursor: 'not-allowed' }} value={fmt(muteahhitEdit.payTL)} />
                                    </div>
                                </div>
                            )}
                            {muteahhitEdit && muteahhitEdit.aldigiPayTL !== null && (
                                <div className="kka-form-row">
                                    <div className="kka-form-group">
                                        <label>{t('kka.aldigiPay')}</label>
                                        <input readOnly style={{ background: '#f4f6fb', color: '#555', cursor: 'not-allowed' }} value={fmt(muteahhitEdit.aldigiPayTL)} />
                                    </div>
                                </div>
                            )}
                            <div className="kka-form-row single">
                                <div className="kka-form-group">
                                    <label>{t('label.not', 'Not')}</label>
                                    <textarea value={muteahhitForm.not} onChange={e => setMuteahhitForm(f => ({ ...f, not: e.target.value }))} />
                                </div>
                            </div>
                        </div>
                        <div className="kka-modal-footer">
                            <button className="kka-btn-secondary" onClick={() => setMuteahhitModal(false)}>{t('btn.iptal')}</button>
                            <button className="kka-btn-primary" onClick={saveMuteahhit} disabled={saving}>{saving ? t('msg.kaydediliyor', 'Kaydediliyor...') : t('btn.kaydet', 'Kaydet')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Ortak Modal */}
            {ortakModal && (
                <div className="kka-modal-overlay" onClick={() => setOrtakModal(false)}>
                    <div className="kka-modal" onClick={e => e.stopPropagation()}>
                        <div className="kka-modal-header">
                            <h3>{ortakEdit ? t('kka.ortagiDuzenle', 'Ortağı Düzenle') : t('kka.ortakEkle', 'Ortak Ekle')}</h3>
                            <button className="kka-modal-close" onClick={() => setOrtakModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-modal-body">
                            {modalError && <div style={{ color: '#e74c3c', marginBottom: 10, fontSize: '0.87rem' }}>{modalError}</div>}
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>{t('label.adSoyad', 'Ad Soyad')} *</label>
                                    <input value={ortakForm.adSoyad} onChange={e => setOrtakForm(f => ({ ...f, adSoyad: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>{t('kka.tcKimlik', 'TC Kimlik')}</label>
                                    <input value={ortakForm.tcKimlik} onChange={e => setOrtakForm(f => ({ ...f, tcKimlik: e.target.value }))} />
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>{t('label.telefon', 'Telefon')}</label>
                                    <input value={ortakForm.telefon} onChange={e => setOrtakForm(f => ({ ...f, telefon: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>{t('label.eposta', 'E-Posta')}</label>
                                    <input value={ortakForm.eposta} onChange={e => setOrtakForm(f => ({ ...f, eposta: e.target.value }))} />
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>{t('kka.hisseM2', 'Hisse m²')}</label>
                                    <input
                                        type="number"
                                        placeholder={arsa.arsaMetrekare ? `Toplam: ${arsa.arsaMetrekare} m²` : 'Hisse alanı girin'}
                                        value={ortakForm.hisseMetre}
                                        onChange={e => setOrtakForm(f => ({ ...f, hisseMetre: e.target.value }))}
                                    />
                                </div>
                                <div className="kka-form-group">
                                    <label>{t('kka.hisseOrani', 'Hisse Oranı (%)')}</label>
                                    <input
                                        readOnly
                                        style={{ background: '#f4f6fb', color: '#555', cursor: 'not-allowed' }}
                                        value={
                                            ortakForm.hisseMetre !== '' && arsa.arsaMetrekare && arsa.arsaMetrekare > 0
                                                ? ((parseFloat(ortakForm.hisseMetre) / arsa.arsaMetrekare) * 100).toFixed(4)
                                                : '–'
                                        }
                                    />
                                </div>
                            </div>
                            {ortakEdit && (ortakEdit.payTL !== null || ortakEdit.aldigiPayTL !== null) && (
                                <div className="kka-form-row">
                                    <div className="kka-form-group">
                                        <label>{t('kka.payTL', 'Pay (TL)')}</label>
                                        <input readOnly style={{ background: '#f4f6fb', color: '#555', cursor: 'not-allowed' }} value={fmt(ortakEdit.payTL)} />
                                    </div>
                                    <div className="kka-form-group">
                                        <label>{t('kka.aldigiPay')}</label>
                                        <input readOnly style={{ background: '#f4f6fb', color: '#555', cursor: 'not-allowed' }} value={fmt(ortakEdit.aldigiPayTL)} />
                                    </div>
                                </div>
                            )}
                            <div className="kka-form-row single">
                                <div className="kka-form-group">
                                    <label>{t('label.not', 'Not')}</label>
                                    <textarea value={ortakForm.not} onChange={e => setOrtakForm(f => ({ ...f, not: e.target.value }))} />
                                </div>
                            </div>
                        </div>
                        <div className="kka-modal-footer">
                            <button className="kka-btn-secondary" onClick={() => setOrtakModal(false)}>{t('btn.iptal')}</button>
                            <button className="kka-btn-primary" onClick={saveOrtak} disabled={saving}>{saving ? t('msg.kaydediliyor', 'Kaydediliyor...') : t('btn.kaydet', 'Kaydet')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Blok Modal */}
            {blokModal && (
                <div className="kka-modal-overlay" onClick={() => setBlokModal(false)}>
                    <div className="kka-modal" onClick={e => e.stopPropagation()}>
                        <div className="kka-modal-header">
                            <h3>{blokEdit ? t('kka.bloguDuzenle', 'Bloğu Düzenle') : t('kka.blokEkle', 'Blok Ekle')}</h3>
                            <button className="kka-modal-close" onClick={() => setBlokModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-modal-body">
                            {modalError && <div style={{ color: '#e74c3c', marginBottom: 10, fontSize: '0.87rem' }}>{modalError}</div>}
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>{t('kka.blokAdi', 'Blok Adı')} *</label>
                                    <input value={blokForm.blokAdi} onChange={e => setBlokForm(f => ({ ...f, blokAdi: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>{t('kka.en', 'En (m)')}</label>
                                    <input type="number" value={blokForm.en} onChange={e => setBlokForm(f => ({ ...f, en: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>{t('kka.boy', 'Boy (m)')}</label>
                                    <input type="number" value={blokForm.boy} onChange={e => setBlokForm(f => ({ ...f, boy: e.target.value }))} />
                                </div>
                            </div>

                            {/* Kat otomatik oluşturma — sadece yeni blok */}
                            {!blokEdit && (
                                <div style={{ background: '#f4f6fb', border: '1px solid #e2e6f0', borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: '#1a2340', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <i className="fas fa-layer-group" style={{ color: '#cd6906' }}></i> {t('kka.otomatikKatOlusturma', 'Otomatik Kat Oluşturma')}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '0 16px', alignItems: 'end' }}>
                                        <div className="kka-form-group" style={{ marginBottom: 0 }}>
                                            <label>{t('kka.normalKatSayisi', 'Normal Kat Sayısı')}</label>
                                            <input
                                                type="number" min="0" max="99"
                                                placeholder={t('label.ara')}
                                                value={blokForm.katSayisi}
                                                onChange={e => setBlokForm(f => ({ ...f, katSayisi: e.target.value }))}
                                            />
                                        </div>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', background: blokForm.zemin ? '#fff7ed' : '#fff', borderColor: blokForm.zemin ? '#cd6906' : '#d1d5db', userSelect: 'none', whiteSpace: 'nowrap', marginBottom: 0 }}>
                                            <input
                                                type="checkbox"
                                                checked={blokForm.zemin}
                                                onChange={e => setBlokForm(f => ({ ...f, zemin: e.target.checked }))}
                                                style={{ accentColor: '#cd6906', width: 15, height: 15 }}
                                            />
                                            <span style={{ fontSize: 13, fontWeight: 600, color: blokForm.zemin ? '#cd6906' : '#374151' }}>{t('kka.zeminKat', 'Zemin Kat')}</span>
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', background: blokForm.bodrum ? '#eff6ff' : '#fff', borderColor: blokForm.bodrum ? '#3b82f6' : '#d1d5db', userSelect: 'none', whiteSpace: 'nowrap', marginBottom: 0 }}>
                                            <input
                                                type="checkbox"
                                                checked={blokForm.bodrum}
                                                onChange={e => setBlokForm(f => ({ ...f, bodrum: e.target.checked }))}
                                                style={{ accentColor: '#3b82f6', width: 15, height: 15 }}
                                            />
                                            <span style={{ fontSize: 13, fontWeight: 600, color: blokForm.bodrum ? '#3b82f6' : '#374151' }}>{t('kka.bodrum', 'Bodrum')}</span>
                                        </label>
                                    </div>
                                    {(blokForm.katSayisi || blokForm.zemin || blokForm.bodrum) && (() => {
                                        const preview: string[] = [];
                                        if (blokForm.bodrum) preview.push('Bodrum');
                                        if (blokForm.zemin) preview.push('Zemin Kat');
                                        const n = parseInt(blokForm.katSayisi) || 0;
                                        for (let i = 1; i <= Math.min(n, 5); i++) preview.push(`${i}. Kat`);
                                        if (n > 5) preview.push(`… ${n}. Kat`);
                                        return (
                                            <div style={{ marginTop: 10, padding: '7px 10px', background: '#fff', border: '1px solid #e2e6f0', borderRadius: 7, fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                <span style={{ color: '#6b7280', fontWeight: 600 }}>{t('kka.olusturulacakKatlar', 'Oluşturulacak katlar')}:</span>
                                                {preview.map((k, i) => (
                                                    <span key={i} style={{ background: '#f3f4f6', borderRadius: 5, padding: '2px 8px', fontSize: 11, color: '#1a2340' }}>{k}</span>
                                                ))}
                                                <span style={{ marginLeft: 4, color: '#9ca3af' }}>({(parseInt(blokForm.katSayisi) || 0) + (blokForm.zemin ? 1 : 0) + (blokForm.bodrum ? 1 : 0)} kat)</span>
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}

                            <div className="kka-form-row single">
                                <div className="kka-form-group">
                                    <label>{t('label.aciklama')}</label>
                                    <textarea value={blokForm.aciklama} onChange={e => setBlokForm(f => ({ ...f, aciklama: e.target.value }))} />
                                </div>
                            </div>
                        </div>
                        <div className="kka-modal-footer">
                            <button className="kka-btn-secondary" onClick={() => setBlokModal(false)}>{t('btn.iptal')}</button>
                            <button className="kka-btn-primary" onClick={saveBlok} disabled={saving}>
                                {saving ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }}></i>{t('msg.kaydediliyor', 'Kaydediliyor...')}</> : t('btn.kaydet', 'Kaydet')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Kat Düzenleme Modalı (sadece edit) */}
            {katModal && (
                <div className="kka-modal-overlay" onClick={() => setKatModal(false)}>
                    <div className="kka-modal" onClick={e => e.stopPropagation()}>
                        <div className="kka-modal-header">
                            <h3>{t('kka.katiDuzenle', 'Katı Düzenle')}</h3>
                            <button className="kka-modal-close" onClick={() => setKatModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-modal-body">
                            {modalError && <div style={{ color: '#e74c3c', marginBottom: 10, fontSize: '0.87rem' }}>{modalError}</div>}
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>{t('kka.katAdi', 'Kat Adı')} *</label>
                                    <input value={katForm.katAdi} onChange={e => setKatForm(f => ({ ...f, katAdi: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>{t('kka.siraNo', 'Sıra No')}</label>
                                    <input type="number" value={katForm.siraNo} onChange={e => setKatForm(f => ({ ...f, siraNo: e.target.value }))} />
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>{t('kka.en', 'En (m)')}</label>
                                    <input type="number" value={katForm.en} onChange={e => setKatForm(f => ({ ...f, en: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>{t('kka.boy', 'Boy (m)')}</label>
                                    <input type="number" value={katForm.boy} onChange={e => setKatForm(f => ({ ...f, boy: e.target.value }))} />
                                </div>
                            </div>
                        </div>
                        <div className="kka-modal-footer">
                            <button className="kka-btn-secondary" onClick={() => setKatModal(false)}>{t('btn.iptal')}</button>
                            <button className="kka-btn-primary" onClick={saveKat} disabled={saving}>{saving ? t('msg.kaydediliyor', 'Kaydediliyor...') : t('btn.kaydet', 'Kaydet')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cephe Modal */}
            {cepheModal && (
                <div className="kka-modal-overlay" onClick={() => setCepheModal(false)}>
                    <div className="kka-modal" onClick={e => e.stopPropagation()}>
                        <div className="kka-modal-header">
                            <h3>{cepheEdit ? t('kka.cepheyiDuzenle', 'Cepheyi Düzenle') : t('kka.cepheEkle', 'Cephe Ekle')}</h3>
                            <button className="kka-modal-close" onClick={() => setCepheModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-modal-body">
                            {modalError && <div style={{ color: '#e74c3c', marginBottom: 10, fontSize: '0.87rem' }}>{modalError}</div>}
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>{t('kka.cepheAdi', 'Cephe Adı')} *</label>
                                    <input value={cepheForm.cepheAdi} onChange={e => setCepheForm(f => ({ ...f, cepheAdi: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>{t('kka.yon', 'Yön')}</label>
                                    <select value={cepheForm.yon} onChange={e => setCepheForm(f => ({ ...f, yon: e.target.value }))}>
                                        <option value="">{t('label.seciniz')}</option>
                                        <option value="Kuzey">{t('kka.yon.kuzey', 'Kuzey')}</option>
                                        <option value="Güney">{t('kka.yon.guney', 'Güney')}</option>
                                        <option value="Doğu">{t('kka.yon.dogu', 'Doğu')}</option>
                                        <option value="Batı">{t('kka.yon.bati', 'Batı')}</option>
                                        <option value="Kuzey Doğu">{t('kka.yon.kuzeyDogu', 'Kuzey Doğu')}</option>
                                        <option value="Kuzey Batı">{t('kka.yon.kuzeyBati', 'Kuzey Batı')}</option>
                                        <option value="Güney Doğu">{t('kka.yon.guneyDogu', 'Güney Doğu')}</option>
                                        <option value="Güney Batı">{t('kka.yon.guneyBati', 'Güney Batı')}</option>
                                    </select>
                                </div>
                            </div>
                            <div className="kka-form-row single">
                                <div className="kka-form-group">
                                    <label>{t('label.aciklama')}</label>
                                    <textarea value={cepheForm.aciklama} onChange={e => setCepheForm(f => ({ ...f, aciklama: e.target.value }))} />
                                </div>
                            </div>
                        </div>
                        <div className="kka-modal-footer">
                            <button className="kka-btn-secondary" onClick={() => setCepheModal(false)}>{t('btn.iptal')}</button>
                            <button className="kka-btn-primary" onClick={saveCephe} disabled={saving}>{saving ? t('msg.kaydediliyor', 'Kaydediliyor...') : t('btn.kaydet', 'Kaydet')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Toplu Bölüm Ekleme Wizard Modal ── */}
            {topluBolumModal && arsa && (() => {
                const aktifSatirlar = topluListesi.filter(s => !s.silindi);
                const cakismaCount = aktifSatirlar.filter(s => s.isDuplicate).length;
                return (
                <div className="kka-modal-overlay" onClick={() => setTopluBolumModal(false)}>
                    <div className="kka-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 1200, width: '96vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', borderRadius: 14, overflow: 'hidden', boxShadow: '0 24px 64px rgba(26,35,64,0.22)' }}>

                        {/* ─ Başlık */}
                        <div style={{ background: '#1a2340', padding: '14px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(205,105,6,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="fas fa-hat-wizard" style={{ color: '#cd6906', fontSize: 16 }}></i>
                                </div>
                                <div>
                                    <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>{t('kka.topluBolumEkleme', 'Toplu Bölüm Ekleme')}</div>
                                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 1 }}>{t('kka.topluBolumAciklama', 'Parametreleri girin, listeyi oluşturun ve düzenleyin')}</div>
                                </div>
                            </div>
                            <button onClick={() => setTopluBolumModal(false)} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', width: 30, height: 30, borderRadius: 7, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                        </div>

                        {/* ─ Config paneli */}
                        <div style={{ padding: '16px 22px', background: '#f4f6fb', borderBottom: '1px solid #e2e6f0', flexShrink: 0 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#1a2340', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <i className="fas fa-sliders-h" style={{ color: '#cd6906' }}></i> {t('kka.parametreler', 'Parametreler')}
                            </div>
                            {/* Satır 1: yapısal parametreler */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr', gap: '0 14px', marginBottom: 12 }}>
                                <div className="kka-form-group">
                                    <label>{t('label.blok', 'Blok')} *</label>
                                    <select value={topluWizard.blokID} onChange={e => setTopluWizard(w => ({ ...w, blokID: e.target.value }))}>
                                        <option value="">— {t('label.seciniz', 'Seçiniz')} —</option>
                                        {arsa.bloklar.map(b => <option key={b.blokID} value={b.blokID}>{b.blokAdi}</option>)}
                                    </select>
                                </div>
                                <div className="kka-form-group">
                                    <label>{t('kka.baslangicKati', 'Başlangıç Katı (Sıra)')}</label>
                                    <input type="number" min="1" value={topluWizard.basKatSiraNo} onChange={e => setTopluWizard(w => ({ ...w, basKatSiraNo: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>{t('kka.kattakiDaireSayisi', 'Kattaki Daire Sayısı')}</label>
                                    <input type="number" min="1" value={topluWizard.kattakiDaireSayisi} onChange={e => setTopluWizard(w => ({ ...w, kattakiDaireSayisi: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>{t('kka.baslangicDaireNo', 'Başlangıç Daire No')}</label>
                                    <input type="number" min="1" value={topluWizard.baslamaDaireNo} onChange={e => setTopluWizard(w => ({ ...w, baslamaDaireNo: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>{t('kka.bolumSayisi', 'Bölüm Sayısı')} *</label>
                                    <input type="number" min="1" max="200" value={topluWizard.eklenecekSayi} onChange={e => setTopluWizard(w => ({ ...w, eklenecekSayi: e.target.value }))} />
                                </div>
                            </div>
                            {/* Satır 2: içerik parametreleri */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr', gap: '0 14px' }}>
                                <div className="kka-form-group">
                                    <label>{t('kka.tipi', 'Tipi')}</label>
                                    <input value={topluWizard.bolumTipi} onChange={e => setTopluWizard(w => ({ ...w, bolumTipi: e.target.value }))} placeholder={t('label.ara')} />
                                </div>
                                <div className="kka-form-group">
                                    <label>{t('kka.turu', 'Türü')}</label>
                                    <select value={topluWizard.turu} onChange={e => setTopluWizard(w => ({ ...w, turu: e.target.value }))}>
                                        <option value="">—</option>
                                        {turuListesi.map(item => <option key={item} value={item}>{item}</option>)}
                                    </select>
                                </div>
                                <div className="kka-form-group">
                                    <label>m²</label>
                                    <input type="number" min="0" step="0.01" value={topluWizard.m2} onChange={e => setTopluWizard(w => ({ ...w, m2: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>{t('kka.fiyatTL', 'Fiyat (TL)')}</label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={fmtBinlik(topluWizard.fiyat)}
                                        onChange={e => {
                                            const raw = parseBinlik(e.target.value).replace(/[^\d.]/g, '');
                                            setTopluWizard(w => ({ ...w, fiyat: raw }));
                                        }}
                                        placeholder="0"
                                    />
                                </div>
                            </div>
                            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                                <button className="kka-btn-primary" onClick={generateTopluListesi}>
                                    <i className="fas fa-magic" style={{ marginRight: 6 }}></i>{t('kka.listeOlustur', 'Liste Oluştur')}
                                </button>
                                {topluListesi.length > 0 && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <span style={{ fontSize: 12, color: '#374151', background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 6, padding: '3px 10px', fontWeight: 600 }}>
                                            ✓ {aktifSatirlar.length} bölüm eklenecek
                                        </span>
                                        {cakismaCount > 0 && (
                                            <span style={{ fontSize: 12, color: '#92400e', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6, padding: '3px 10px', fontWeight: 600 }}>
                                                ⚠ {cakismaCount} çakışma — aynı blokta bu adlar zaten mevcut
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ─ Tablo */}
                        {topluListesi.length > 0 && (
                            <div style={{ flex: 1, overflowY: 'auto', padding: '0 22px 8px' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 10 }}>
                                    <thead>
                                        <tr style={{ background: '#1a2340', position: 'sticky', top: 0, zIndex: 2 }}>
                                            {['#', t('kka.bolumAdi', 'Bölüm Adı'), t('label.kat', 'Kat'), t('kka.tipi', 'Tipi'), t('kka.turu', 'Türü'), 'm²', t('kka.fiyatTL', 'Fiyat (TL)'), t('kka.birimFKisa', 'Birim F.'), t('kka.cephe', 'Cephe'), t('label.not', 'Not'), ''].map(h => (
                                                <th key={h} style={{ padding: '8px 7px', textAlign: 'left', fontWeight: 700, color: 'rgba(255,255,255,0.75)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topluListesi.map((satir, idx) => {
                                            const blok = arsa.bloklar.find(b => b.blokID === satir.blokID);
                                            const katlar = blok ? [...blok.katlar].sort((a, b) => a.siraNo - b.siraNo) : [];
                                            if (satir.silindi) return null;
                                            const rowBg = satir.isDuplicate ? '#fff8f0' : idx % 2 === 0 ? '#fff' : '#f9fafb';
                                            const dupBorder = satir.isDuplicate ? '#fdba74' : undefined;
                                            const cellInput = (w: number, extra?: React.CSSProperties): React.CSSProperties => ({
                                                width: w, padding: '4px 7px', fontSize: 12, background: rowBg,
                                                border: `1px solid ${dupBorder ?? '#d1d5db'}`, borderRadius: 6,
                                                outline: 'none', boxSizing: 'border-box', ...extra,
                                            });
                                            const cellSel = (minW: number): React.CSSProperties => ({
                                                padding: '4px 6px', fontSize: 12, minWidth: minW, background: rowBg,
                                                border: `1px solid ${dupBorder ?? '#d1d5db'}`, borderRadius: 6,
                                                outline: 'none', cursor: 'pointer',
                                            });
                                            return (
                                                <tr key={satir.id} style={{ borderBottom: '1px solid #f0f0f0', background: rowBg }}>
                                                    <td style={{ padding: '4px 7px', color: '#9ca3af', fontSize: 11, whiteSpace: 'nowrap' }}>
                                                        <span style={{ color: '#6b7280' }}>{idx + 1}</span>
                                                        {satir.isDuplicate && (
                                                            <span title="Bu blokta aynı adlı bölüm zaten var!" style={{ marginLeft: 5, color: '#cd6906', fontWeight: 700, cursor: 'help', fontSize: 13 }}>⚠</span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '4px 4px' }}>
                                                        <input style={cellInput(72)} value={satir.bolumAdi}
                                                            onChange={e => {
                                                                const v = e.target.value;
                                                                const mevcutAdlar = new Set(arsa.bolumler.filter(b => b.blokID === satir.blokID).map(b => b.bolumAdi.trim().toLowerCase()));
                                                                setTopluListesi(prev => prev.map(s => s.id === satir.id ? { ...s, bolumAdi: v, isDuplicate: mevcutAdlar.has(v.trim().toLowerCase()) } : s));
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ padding: '4px 4px' }}>
                                                        <select style={cellSel(96)} value={satir.katID ?? ''} onChange={e => updateTopluSatir(satir.id, 'katID', e.target.value ? parseInt(e.target.value) : null)}>
                                                            <option value="">—</option>
                                                            {katlar.map(k => <option key={k.katID} value={k.katID}>{k.katAdi}</option>)}
                                                        </select>
                                                    </td>
                                                    <td style={{ padding: '4px 4px' }}>
                                                        <input style={cellInput(72)} value={satir.bolumTipi} onChange={e => updateTopluSatir(satir.id, 'bolumTipi', e.target.value)} placeholder="Daire…" />
                                                    </td>
                                                    <td style={{ padding: '4px 4px' }}>
                                                        <select style={cellSel(82)} value={satir.turu} onChange={e => updateTopluSatir(satir.id, 'turu', e.target.value)}>
                                                            <option value="">—</option>
                                                            {turuListesi.map(item => <option key={item} value={item}>{item}</option>)}
                                                        </select>
                                                    </td>
                                                    <td style={{ padding: '4px 4px' }}>
                                                        <input type="number" style={cellInput(58)} value={satir.m2} onChange={e => updateTopluSatir(satir.id, 'm2', e.target.value)} />
                                                    </td>
                                                    <td style={{ padding: '4px 4px' }}>
                                                        <input
                                                            type="text" inputMode="numeric"
                                                            style={cellInput(92)}
                                                            value={fmtBinlik(satir.fiyat)}
                                                            onChange={e => {
                                                                const raw = parseBinlik(e.target.value).replace(/[^\d.]/g, '');
                                                                updateTopluSatir(satir.id, 'fiyat', raw);
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ padding: '4px 4px' }}>
                                                        <input
                                                            type="text" inputMode="numeric"
                                                            style={cellInput(84, { background: satir.isDuplicate ? '#fff8f0' : '#f3f4f6' })}
                                                            value={fmtBinlik(satir.birimFiyat)}
                                                            onChange={e => {
                                                                const raw = parseBinlik(e.target.value).replace(/[^\d.]/g, '');
                                                                updateTopluSatir(satir.id, 'birimFiyat', raw);
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ padding: '4px 4px' }}>
                                                        <select style={cellSel(82)} value={satir.cephe1ID} onChange={e => updateTopluSatir(satir.id, 'cephe1ID', e.target.value)}>
                                                            <option value="">—</option>
                                                            {arsa.cepheler.map(c => <option key={c.cepheID} value={c.cepheID}>{c.cepheAdi}</option>)}
                                                        </select>
                                                    </td>
                                                    <td style={{ padding: '4px 4px' }}>
                                                        <input style={cellInput(96)} value={satir.not} onChange={e => updateTopluSatir(satir.id, 'not', e.target.value)} />
                                                    </td>
                                                    <td style={{ padding: '4px 7px' }}>
                                                        <button onClick={() => updateTopluSatir(satir.id, 'silindi', true)} style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#dc2626', cursor: 'pointer', fontSize: 12, padding: '3px 7px', borderRadius: 5, lineHeight: 1 }} title="Listeden çıkar">×</button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* ─ Footer */}
                        <div style={{ padding: '12px 22px', borderTop: '1px solid #e2e6f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, background: '#f4f6fb' }}>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>
                                {aktifSatirlar.length > 0
                                    ? <><span style={{ fontWeight: 600, color: '#1a2340' }}>{aktifSatirlar.length}</span> bölüm kaydedilecek{cakismaCount > 0 && <span style={{ color: '#cd6906', marginLeft: 6 }}>({cakismaCount} çakışmalı — yine de kaydedilir, kontrol edin)</span>}</>
                                    : <span style={{ color: '#9ca3af' }}>Henüz liste oluşturulmadı.</span>
                                }
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="kka-btn-secondary" onClick={() => setTopluBolumModal(false)}>{t('btn.iptal')}</button>
                                <button
                                    className="kka-btn-primary"
                                    onClick={saveTopluBolumler}
                                    disabled={topluSaving || aktifSatirlar.length === 0}
                                    style={{ opacity: aktifSatirlar.length === 0 ? 0.45 : 1 }}
                                >
                                    {topluSaving
                                        ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }}></i>{t('msg.kaydediliyor', 'Kaydediliyor...')}</>
                                        : <><i className="fas fa-save" style={{ marginRight: 6 }}></i>{t('btn.kaydet', 'Kaydet')} ({aktifSatirlar.length} {t('kka.bolum', 'bölüm')})</>
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                );
            })()}

            {/* Bölüm Modal */}
            {bolumModal && (
                <div className="kka-modal-overlay" onClick={() => setBolumModal(false)}>
                    <div className="kka-modal kka-modal-lg" onClick={e => e.stopPropagation()}>
                        <div className="kka-modal-header">
                            <h3>{bolumEdit ? t('kka.bolumDuzenle', 'Bölümü Düzenle') : t('kka.bolumEkle', 'Bölüm Ekle')}</h3>
                            <button className="kka-modal-close" onClick={() => setBolumModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-modal-body">
                            {modalError && <div style={{ color: '#e74c3c', marginBottom: 10, fontSize: '0.87rem' }}>{modalError}</div>}
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>{t('kka.bolumAdi', 'Bölüm Adı')} *</label>
                                    <input value={bolumForm.bolumAdi} onChange={e => setBolumForm(f => ({ ...f, bolumAdi: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>{t('kka.bolumTipi', 'Bölüm Tipi')}</label>
                                    <select value={bolumForm.bolumTipi} onChange={e => setBolumForm(f => ({ ...f, bolumTipi: e.target.value }))}>
                                        <option value="">– Seçiniz –</option>
                                        <option>Daire</option>
                                        <option>{t('kira.dukkan')}</option>
                                        <option>Depo</option>
                                        <option>Ofis</option>
                                        <option>Arsa Payı</option>
                                        <option>Ortak Alan</option>
                                    </select>
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>{t('kka.turu', 'Türü')}</label>
                                    <input
                                        list="bolum-turu-datalist"
                                        value={bolumForm.turu}
                                        onChange={e => setBolumForm(f => ({ ...f, turu: e.target.value }))}
                                        placeholder={t('label.ara')}
                                    />
                                    <datalist id="bolum-turu-datalist">
                                        {turuListesi.map(item => <option key={item} value={item} />)}
                                    </datalist>
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>{t('label.blok', 'Blok')}</label>
                                    <select value={bolumForm.blokID} onChange={e => setBolumForm(f => ({ ...f, blokID: e.target.value, katID: '' }))}>
                                        <option value="">– {t('label.seciniz', 'Seçiniz')} –</option>
                                        {arsa.bloklar.map(b => <option key={b.blokID} value={b.blokID}>{b.blokAdi}</option>)}
                                    </select>
                                </div>
                                <div className="kka-form-group">
                                    <label>{t('label.kat', 'Kat')}</label>
                                    <select value={bolumForm.katID} onChange={e => setBolumForm(f => ({ ...f, katID: e.target.value }))}>
                                        <option value="">– {t('label.seciniz', 'Seçiniz')} –</option>
                                        {katsForBlok(bolumForm.blokID).map(k => <option key={k.katID} value={k.katID}>{k.katAdi}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>Cephe 1</label>
                                    <select value={bolumForm.cephe1ID} onChange={e => setBolumForm(f => ({ ...f, cephe1ID: e.target.value }))}>
                                        <option value="">– Seçiniz –</option>
                                        {arsa.cepheler.map(c => <option key={c.cepheID} value={c.cepheID}>{c.cepheAdi}</option>)}
                                    </select>
                                </div>
                                <div className="kka-form-group">
                                    <label>Cephe 2</label>
                                    <select value={bolumForm.cephe2ID} onChange={e => setBolumForm(f => ({ ...f, cephe2ID: e.target.value }))}>
                                        <option value="">– Seçiniz –</option>
                                        {arsa.cepheler.map(c => <option key={c.cepheID} value={c.cepheID}>{c.cepheAdi}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>Cephe 3</label>
                                    <select value={bolumForm.cephe3ID} onChange={e => setBolumForm(f => ({ ...f, cephe3ID: e.target.value }))}>
                                        <option value="">– Seçiniz –</option>
                                        {arsa.cepheler.map(c => <option key={c.cepheID} value={c.cepheID}>{c.cepheAdi}</option>)}
                                    </select>
                                </div>
                                <div className="kka-form-group">
                                    <label>Cephe 4</label>
                                    <select value={bolumForm.cephe4ID} onChange={e => setBolumForm(f => ({ ...f, cephe4ID: e.target.value }))}>
                                        <option value="">– Seçiniz –</option>
                                        {arsa.cepheler.map(c => <option key={c.cepheID} value={c.cepheID}>{c.cepheAdi}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>{t('kka.alanM2', 'Alan (m²)')}</label>
                                    <input type="number" value={bolumForm.m2} onChange={e => {
                                        const newM2 = e.target.value;
                                        const fiyat = parseFloat(bolumForm.fiyat) || 0;
                                        const m2 = parseFloat(newM2) || 0;
                                        setBolumForm(f => ({ ...f, m2: newM2, birimFiyat: m2 > 0 ? String(Math.round((fiyat / m2) * 100) / 100) : '' }));
                                    }} />
                                </div>
                                <div className="kka-form-group">
                                    <label>{t('kka.toplamFiyat', 'Toplam Fiyat (₺)')}</label>
                                    <input type="text" inputMode="numeric"
                                        value={bolumForm.fiyat !== '' && !isNaN(parseFloat(bolumForm.fiyat)) ? parseFloat(bolumForm.fiyat).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : bolumForm.fiyat}
                                        onChange={e => {
                                            const raw = e.target.value.replace(/\./g, '').replace(',', '.');
                                            const m2 = parseFloat(bolumForm.m2) || 0;
                                            const fiyat = parseFloat(raw) || 0;
                                            setBolumForm(f => ({ ...f, fiyat: raw, birimFiyat: m2 > 0 ? String(Math.round((fiyat / m2) * 100) / 100) : '' }));
                                        }} />
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>{t('kka.birimFiyatDetay', 'Birim Fiyat (₺/m²)')}</label>
                                    <input type="text" value={bolumForm.birimFiyat !== '' && !isNaN(parseFloat(bolumForm.birimFiyat)) ? parseFloat(bolumForm.birimFiyat).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : ''} readOnly style={{ background: '#f5f5f5', cursor: 'not-allowed' }} />
                                </div>
                                <div className="kka-form-group">
                                    <label>{t('kka.paylasılanAlan', 'Paylaşılan Alan (m²)')}</label>
                                    <input type="number" value={bolumEdit ? bolumEdit.paylasılanAlan : 0} readOnly style={{ background: '#f5f5f5', cursor: 'not-allowed' }} />
                                </div>
                            </div>
                            <div className="kka-form-row single">
                                <div className="kka-form-group">
                                    <label>{t('label.not', 'Not')}</label>
                                    <textarea value={bolumForm.not} onChange={e => setBolumForm(f => ({ ...f, not: e.target.value }))} />
                                </div>
                            </div>
                        </div>
                        <div className="kka-modal-footer">
                            <button className="kka-btn-secondary" onClick={() => setBolumModal(false)}>{t('btn.iptal')}</button>
                            <button className="kka-btn-primary" onClick={saveBolum} disabled={saving}>{saving ? t('msg.kaydediliyor', 'Kaydediliyor...') : t('btn.kaydet', 'Kaydet')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bölüm Paylaşımı Modal — mevcut paylaşımlar (düzenle / sil) + yeni paylaşım ekle */}
            {paylasimModal && (() => {
                const currentBolum = paylasimBolumId ? arsa.bolumler.find(b => b.bolumID === paylasimBolumId) ?? null : null;
                if (!currentBolum) return null;
                const kisiAdi = (p: Paylasim) => p.ortak?.adSoyad ?? p.muteahhit?.adSoyad ?? '–';
                const toplamOran = bolumPaylasimOrani(currentBolum);
                const selectedOrtakID = paylasimForm.ortakID ? parseInt(paylasimForm.ortakID) : null;
                const selectedMuteahhitID = paylasimForm.muteahhitID ? parseInt(paylasimForm.muteahhitID) : null;

                // Seçilen kişinin hak ettiği pay, şimdiye kadar aldığı ve kalan
                let kisiPayTL: number | null = null;
                if (selectedOrtakID) kisiPayTL = arsa.ortaklar.find(o => o.ortakID === selectedOrtakID)?.payTL ?? null;
                else if (selectedMuteahhitID) kisiPayTL = arsa.muteahhitler.find(m => m.muteahhitID === selectedMuteahhitID)?.payTL ?? null;
                let kisiAldigiTL = 0;
                arsa.bolumler.forEach(b => b.paylasimlar.forEach(p => {
                    if ((selectedOrtakID && p.ortakID === selectedOrtakID) || (selectedMuteahhitID && p.muteahhitID === selectedMuteahhitID))
                        kisiAldigiTL += b.fiyat * p.oranYuzde / 100;
                }));
                const kisiKalanTL = kisiPayTL !== null ? kisiPayTL - kisiAldigiTL : null;
                const yeniOran = parseFloat(paylasimForm.oranYuzde) || 0;
                const bolumToplamOran = toplamOran + yeniOran;
                const oranAsimi = bolumToplamOran > 100.0001;
                const bolumDolu = toplamOran >= 100;
                const zatenVar = currentBolum.paylasimlar.some(p =>
                    (selectedOrtakID && p.ortakID === selectedOrtakID) || (selectedMuteahhitID && p.muteahhitID === selectedMuteahhitID));

                return (
                    <div className="kka-modal-overlay" onClick={() => setPaylasimModal(false)}>
                        <div className="kka-modal" style={{ maxWidth: 760, width: '96vw' }} onClick={e => e.stopPropagation()}>
                            <div className="kka-modal-header">
                                <h3><i className="fas fa-share-alt" style={{ color: '#cd6906', marginRight: 8 }}></i>Bölüm Paylaşımı — {currentBolum.bolumAdi}</h3>
                                <button className="kka-modal-close" onClick={() => setPaylasimModal(false)}><i className="fas fa-times"></i></button>
                            </div>
                            <div className="kka-modal-body">
                                {modalError && <div style={{ color: '#e74c3c', marginBottom: 10, fontSize: '0.87rem' }}>{modalError}</div>}

                                {/* Bölüm bilgisi + paylaşım oranı */}
                                <div style={{ background: '#f0f4ff', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: '0.87rem', display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center' }}>
                                    <span><strong>{currentBolum.bolumAdi}</strong>{currentBolum.bolumTipi ? ` – ${currentBolum.bolumTipi}` : ''}</span>
                                    <span style={{ color: '#64748b' }}>{[currentBolum.blok?.blokAdi, currentBolum.kat?.katAdi].filter(Boolean).join(' / ') || '—'}</span>
                                    <span>{fmt(currentBolum.m2)} m²</span>
                                    <span>{t('kka.fiyat', 'Fiyat')}: <strong>{fmt(currentBolum.fiyat)} ₺</strong></span>
                                    <span style={{ marginLeft: 'auto', minWidth: 150 }}><OranCubugu oran={toplamOran} /></span>
                                </div>

                                {/* Mevcut paylaşımlar */}
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Mevcut Paylaşımlar</div>
                                {currentBolum.paylasimlar.length === 0 ? (
                                    <div style={{ fontSize: '0.86rem', color: '#94a3b8', fontStyle: 'italic', marginBottom: 14 }}>Bu bölüm henüz kimseye paylaştırılmadı.</div>
                                ) : (
                                    <table className="kka-sub-table" style={{ fontSize: '0.85rem', marginBottom: 16 }}>
                                        <thead><tr><th>Hak Sahibi</th><th>{t('label.tur')}</th><th style={{ width: 110 }}>Oran</th><th>Tutar</th><th>{t('label.not', 'Not')}</th><th style={{ width: 90 }}></th></tr></thead>
                                        <tbody>
                                            {currentBolum.paylasimlar.map(p => {
                                                const duzen = paylasimSatirDuzen[p.paylasimID];
                                                const digerOran = toplamOran - p.oranYuzde;
                                                const maxOran = 100 - digerOran;
                                                const duzenOran = duzen ? parseFloat(duzen.oran) : NaN;
                                                const gecersiz = duzen ? (isNaN(duzenOran) || duzenOran <= 0 || duzenOran > maxOran + 0.0001) : false;
                                                const islemde = paylasimIslemde === p.paylasimID;
                                                return (
                                                    <tr key={p.paylasimID} style={duzen ? { background: '#fffbf5' } : undefined}>
                                                        <td><strong>{kisiAdi(p)}</strong></td>
                                                        <td>{p.ortakID != null ? t('kka.arsaOrtagi', 'Arsa Ortağı') : t('kka.muteahhit', 'Müteahhit')}</td>
                                                        <td>
                                                            {duzen ? (
                                                                <>
                                                                    <input type="number" value={duzen.oran} min={0} max={maxOran} step="0.01" autoFocus
                                                                        onChange={e => setPaylasimSatirDuzen(d => ({ ...d, [p.paylasimID]: { ...duzen, oran: e.target.value } }))}
                                                                        style={{ width: 80, padding: '3px 6px', border: `1px solid ${gecersiz ? '#e74c3c' : '#cd6906'}`, borderRadius: 5 }} />
                                                                    <div style={{ fontSize: 10.5, color: gecersiz ? '#e74c3c' : '#94a3b8' }}>en fazla %{maxOran.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}</div>
                                                                </>
                                                            ) : <>%{p.oranYuzde.toLocaleString('tr-TR', { maximumFractionDigits: 4 })}</>}
                                                        </td>
                                                        <td>{fmt(currentBolum.fiyat * (duzen && !isNaN(duzenOran) ? duzenOran : p.oranYuzde) / 100)} ₺</td>
                                                        <td>
                                                            {duzen ? (
                                                                <input value={duzen.not} onChange={e => setPaylasimSatirDuzen(d => ({ ...d, [p.paylasimID]: { ...duzen, not: e.target.value } }))}
                                                                    style={{ width: '100%', padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 5 }} />
                                                            ) : <span style={{ color: '#64748b' }}>{p.aciklamaNot || '–'}</span>}
                                                        </td>
                                                        <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                                                            {islemde ? <i className="fas fa-spinner fa-spin" style={{ color: '#94a3b8' }}></i> : duzen ? (
                                                                <>
                                                                    <button className="kka-icon-btn" title="Kaydet" disabled={gecersiz} style={{ color: gecersiz ? '#cbd5e1' : '#16a34a' }}
                                                                        onClick={async () => {
                                                                            if (await paylasimGuncelle(currentBolum.bolumID, p, duzenOran, duzen.not.trim() || null))
                                                                                setPaylasimSatirDuzen(d => { const y = { ...d }; delete y[p.paylasimID]; return y; });
                                                                        }}><i className="fas fa-check"></i></button>
                                                                    <button className="kka-icon-btn" title="Vazgeç" style={{ color: '#64748b' }}
                                                                        onClick={() => setPaylasimSatirDuzen(d => { const y = { ...d }; delete y[p.paylasimID]; return y; })}><i className="fas fa-times"></i></button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button className="kka-icon-btn edit" title="Oranı / notu düzenle"
                                                                        onClick={() => setPaylasimSatirDuzen(d => ({ ...d, [p.paylasimID]: { oran: String(p.oranYuzde), not: p.aciklamaNot ?? '' } }))}><i className="fas fa-pen" style={{ color: '#1a2340' }}></i></button>
                                                                    <button className="kka-icon-btn del" title="Paylaşımı sil"
                                                                        onClick={() => paylasimIptal(currentBolum.bolumID, p, kisiAdi(p), currentBolum.bolumAdi)}><i className="fas fa-trash" style={{ color: '#dc2626' }}></i></button>
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}

                                {/* Yeni paylaşım */}
                                <div style={{ borderTop: '1px solid #eef1f6', paddingTop: 12 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Yeni Paylaşım Ekle</div>
                                    {bolumDolu ? (
                                        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', borderRadius: 7, padding: '8px 12px', fontSize: '0.85rem' }}>
                                            <i className="fas fa-check-circle" style={{ marginRight: 6 }}></i>
                                            Bölüm tamamen paylaştırıldı. Yeni bir kişi eklemek için önce mevcut bir paylaşımın oranını düşürün ya da paylaşımı silin.
                                        </div>
                                    ) : (
                                        <>
                                            <div className="kka-form-row">
                                                <div className="kka-form-group">
                                                    <label>{t('kka.arsaOrtagi', 'Arsa Ortağı')}</label>
                                                    <select value={paylasimForm.ortakID} onChange={e => setPaylasimForm(f => ({ ...f, ortakID: e.target.value, muteahhitID: e.target.value ? '' : f.muteahhitID }))}>
                                                        <option value="">– {t('label.seciniz', 'Seçiniz')} –</option>
                                                        {arsa.ortaklar.map(o => <option key={o.ortakID} value={o.ortakID}>{o.adSoyad}</option>)}
                                                    </select>
                                                </div>
                                                <div className="kka-form-group">
                                                    <label>{t('kka.muteahhit', 'Müteahhit')}</label>
                                                    <select value={paylasimForm.muteahhitID} onChange={e => setPaylasimForm(f => ({ ...f, muteahhitID: e.target.value, ortakID: e.target.value ? '' : f.ortakID }))}>
                                                        <option value="">– {t('label.seciniz', 'Seçiniz')} –</option>
                                                        {arsa.muteahhitler.map(m => <option key={m.muteahhitID} value={m.muteahhitID}>{m.adSoyad}</option>)}
                                                    </select>
                                                </div>
                                            </div>

                                            {(selectedOrtakID || selectedMuteahhitID) && (() => {
                                                const maxYuzdeKisi = (kisiKalanTL !== null && currentBolum.fiyat > 0)
                                                    ? Math.min(100 - toplamOran, (kisiKalanTL / currentBolum.fiyat) * 100)
                                                    : null;
                                                return (
                                                    <div style={{ background: '#f8f9fb', border: '1px solid #e3e8f0', borderRadius: 7, padding: '10px 14px', marginBottom: 12, fontSize: '0.85rem' }}>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                                                            <div><div style={{ color: '#888', marginBottom: 2 }}>{t('kka.toplamPayTL', 'Toplam Pay (TL)')}</div><div style={{ fontWeight: 600, color: '#1a2340' }}>{kisiPayTL !== null ? `${fmt(kisiPayTL)} ₺` : '–'}</div></div>
                                                            <div><div style={{ color: '#888', marginBottom: 2 }}>{t('kka.atanmisTL', 'Atanmış (TL)')}</div><div style={{ fontWeight: 600, color: '#cd6906' }}>{fmt(kisiAldigiTL)} ₺</div></div>
                                                            <div><div style={{ color: '#888', marginBottom: 2 }}>{t('kka.kalanTL', 'Kalan (TL)')}</div><div style={{ fontWeight: 600, color: kisiKalanTL !== null && kisiKalanTL < 0 ? '#e74c3c' : '#27ae60' }}>{kisiKalanTL !== null ? `${fmt(kisiKalanTL)} ₺` : '–'}</div></div>
                                                        </div>
                                                        {maxYuzdeKisi !== null && (
                                                            <div style={{ marginTop: 8, color: maxYuzdeKisi <= 0 ? '#e74c3c' : '#1a6bb5', fontWeight: 600, fontSize: '0.84rem' }}>
                                                                <i className={`fas ${maxYuzdeKisi <= 0 ? 'fa-times-circle' : 'fa-info-circle'}`} style={{ marginRight: 6 }}></i>
                                                                {maxYuzdeKisi <= 0 ? 'Bu kişinin bu bölümden pay alacak bakiyesi kalmadı.' : (
                                                                    <>Bu bölümden en fazla %{maxYuzdeKisi.toLocaleString('tr-TR', { maximumFractionDigits: 2 })} alabilir.{' '}
                                                                        <button style={{ border: 'none', background: 'none', color: '#cd6906', fontWeight: 700, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                                                                            onClick={() => setPaylasimForm(f => ({ ...f, oranYuzde: String(Math.floor(maxYuzdeKisi * 100) / 100) }))}>Bu oranı kullan</button></>
                                                                )}
                                                            </div>
                                                        )}
                                                        {zatenVar && (
                                                            <div style={{ marginTop: 6, color: '#b45309', fontSize: '0.82rem' }}>
                                                                <i className="fas fa-exclamation-triangle" style={{ marginRight: 6 }}></i>Bu kişinin bu bölümde zaten bir paylaşımı var; yeni satır eklemek yerine yukarıdan oranını düzenleyebilirsiniz.
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}

                                            <div className="kka-form-row">
                                                <div className="kka-form-group">
                                                    <label>{t('kka.oranYuzde', 'Oran (%)')} <span style={{ color: '#94a3b8', fontWeight: 400 }}>— kalan %{(100 - toplamOran).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}</span></label>
                                                    <input type="number" value={paylasimForm.oranYuzde} onChange={e => setPaylasimForm(f => ({ ...f, oranYuzde: e.target.value }))}
                                                        style={oranAsimi ? { borderColor: '#e74c3c' } : {}} />
                                                    {oranAsimi && <small style={{ color: '#e74c3c' }}>Toplam %100'ü aşamaz!</small>}
                                                </div>
                                                <div className="kka-form-group">
                                                    <label>{t('label.not', 'Not')}</label>
                                                    <input value={paylasimForm.aciklamaNot} onChange={e => setPaylasimForm(f => ({ ...f, aciklamaNot: e.target.value }))} />
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <button className="kka-btn-primary" onClick={savePaylasim}
                                                    disabled={saving || oranAsimi || (!selectedOrtakID && !selectedMuteahhitID) || yeniOran <= 0}>
                                                    <i className="fas fa-plus" style={{ marginRight: 6 }}></i>{saving ? t('msg.kaydediliyor', 'Kaydediliyor...') : 'Paylaşım Ekle'}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="kka-modal-footer">
                                <button className="kka-btn-secondary" onClick={() => setPaylasimModal(false)}>Kapat</button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Tarafın paylaşımdan aldığı daireler — oran değiştir / iptal */}
            {tarafModal && (() => {
                const kisi = tarafModal.tip === 'ortak'
                    ? arsa.ortaklar.find(o => o.ortakID === tarafModal.id)
                    : arsa.muteahhitler.find(m => m.muteahhitID === tarafModal.id);
                if (!kisi) return null;
                const satirlar = arsa.bolumler.flatMap(b => b.paylasimlar
                    .filter(p => (tarafModal.tip === 'ortak' ? p.ortakID === tarafModal.id : p.muteahhitID === tarafModal.id))
                    .map(p => ({ b, p })));
                const aldigi = satirlar.reduce((x, { b, p }) => x + b.fiyat * p.oranYuzde / 100, 0);
                const pay = kisi.payTL ?? 0;
                const kalan = pay - aldigi;
                const tamamOran = payTamamlanmaOrani(kisi);
                return (
                    <div className="kka-modal-overlay" onClick={() => setTarafModal(null)}>
                        <div className="kka-modal" style={{ maxWidth: 900, width: '96vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                            <div className="kka-modal-header">
                                <h3><i className="fas fa-home" style={{ color: '#cd6906', marginRight: 8 }}></i>{kisi.adSoyad} — Aldığı Daireler</h3>
                                <button className="kka-modal-close" onClick={() => setTarafModal(null)}><i className="fas fa-times"></i></button>
                            </div>
                            <div className="kka-modal-body">
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
                                    {[
                                        { e: 'Tür', d: tarafModal.tip === 'ortak' ? 'Arsa Ortağı' : 'Müteahhit', r: '#1a2340' },
                                        { e: 'Hak Ettiği', d: pay ? `${fmt(pay)} ₺` : '–', r: '#1a2340' },
                                        { e: 'Aldığı', d: `${fmt(aldigi)} ₺`, r: '#cd6906' },
                                        { e: 'Kalan', d: pay ? `${fmt(kalan)} ₺` : '–', r: kalan < -1 ? '#dc2626' : '#16a34a' },
                                    ].map(k => (
                                        <div key={k.e} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px' }}>
                                            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{k.e}</div>
                                            <div style={{ fontSize: 15, fontWeight: 800, color: k.r }}>{k.d}</div>
                                        </div>
                                    ))}
                                    {tamamOran !== null && (
                                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px' }}>
                                            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>Tamamlanma</div>
                                            <OranCubugu oran={tamamOran} tamamEsigi={PAY_TAMAM_ESIGI} />
                                        </div>
                                    )}
                                </div>
                                {kalan < -1 && (
                                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 7, padding: '8px 12px', fontSize: '0.85rem', marginBottom: 12 }}>
                                        <i className="fas fa-exclamation-triangle" style={{ marginRight: 6 }}></i>
                                        Bu kişi hak ettiğinden {fmt(-kalan)} ₺ fazla pay almış. Bir paylaşımın oranını düşürün ya da iptal edin.
                                    </div>
                                )}
                                {satirlar.length === 0 ? (
                                    <div className="kka-empty">Bu kişiye henüz daire atanmadı. Adım 6'dan sürükle-bırak ile atama yapabilirsiniz.</div>
                                ) : (
                                    <table className="kka-sub-table" style={{ fontSize: '0.86rem' }}>
                                        <thead>
                                            <tr><th>Bölüm</th><th>Blok / Kat</th><th>Fiyat</th><th style={{ width: 120 }}>Oran</th><th>Tutar</th><th>Bölüm Toplamı</th><th style={{ width: 110 }}></th></tr>
                                        </thead>
                                        <tbody>
                                            {satirlar.map(({ b, p }) => {
                                                const bolumToplam = bolumPaylasimOrani(b);
                                                const maxOran = 100 - (bolumToplam - p.oranYuzde);
                                                const duzen = tarafSatirDuzen[p.paylasimID];
                                                const yeni = duzen !== undefined ? parseFloat(duzen) : NaN;
                                                const gecersiz = duzen !== undefined && (isNaN(yeni) || yeni <= 0 || yeni > maxOran + 0.0001);
                                                const islemde = paylasimIslemde === p.paylasimID;
                                                return (
                                                    <tr key={p.paylasimID} style={duzen !== undefined ? { background: '#fffbf5' } : undefined}>
                                                        <td>
                                                            <strong>{b.bolumAdi}</strong>{b.bolumTipi ? <span style={{ color: '#94a3b8' }}> – {b.bolumTipi}</span> : null}
                                                        </td>
                                                        <td>{[b.blok?.blokAdi, b.kat?.katAdi].filter(Boolean).join(' / ') || '–'}</td>
                                                        <td>{fmt(b.fiyat)} ₺</td>
                                                        <td>
                                                            {duzen !== undefined ? (
                                                                <>
                                                                    <input type="number" value={duzen} autoFocus min={0} max={maxOran} step="0.01"
                                                                        onChange={e => setTarafSatirDuzen(d => ({ ...d, [p.paylasimID]: e.target.value }))}
                                                                        style={{ width: 80, padding: '3px 6px', border: `1px solid ${gecersiz ? '#e74c3c' : '#cd6906'}`, borderRadius: 5 }} />
                                                                    <div style={{ fontSize: 10.5, color: gecersiz ? '#e74c3c' : '#94a3b8' }}>en fazla %{maxOran.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}</div>
                                                                </>
                                                            ) : <>%{p.oranYuzde.toLocaleString('tr-TR', { maximumFractionDigits: 4 })}</>}
                                                        </td>
                                                        <td style={{ fontWeight: 600 }}>{fmt(b.fiyat * (duzen !== undefined && !isNaN(yeni) ? yeni : p.oranYuzde) / 100)} ₺</td>
                                                        <td><OranCubugu oran={bolumToplam} /></td>
                                                        <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                                                            {islemde ? <i className="fas fa-spinner fa-spin" style={{ color: '#94a3b8' }}></i> : duzen !== undefined ? (
                                                                <>
                                                                    <button className="kka-icon-btn" title="Kaydet" disabled={gecersiz} style={{ color: gecersiz ? '#cbd5e1' : '#16a34a' }}
                                                                        onClick={async () => {
                                                                            const fark = b.fiyat * (yeni - p.oranYuzde) / 100;
                                                                            if (pay > 0 && aldigi + fark > pay * 1.005 &&
                                                                                !window.confirm(`Bu değişiklikle ${kisi.adSoyad} hak ettiğinden ${fmt(aldigi + fark - pay)} ₺ fazla pay almış olacak. Yine de kaydedilsin mi?`)) return;
                                                                            if (await paylasimGuncelle(b.bolumID, p, yeni, p.aciklamaNot))
                                                                                setTarafSatirDuzen(d => { const y = { ...d }; delete y[p.paylasimID]; return y; });
                                                                        }}><i className="fas fa-check"></i></button>
                                                                    <button className="kka-icon-btn" title="Vazgeç" style={{ color: '#64748b' }}
                                                                        onClick={() => setTarafSatirDuzen(d => { const y = { ...d }; delete y[p.paylasimID]; return y; })}><i className="fas fa-times"></i></button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button className="kka-icon-btn edit" title="Oranı değiştir"
                                                                        onClick={() => setTarafSatirDuzen(d => ({ ...d, [p.paylasimID]: String(p.oranYuzde) }))}><i className="fas fa-pen" style={{ color: '#1a2340' }}></i></button>
                                                                    <button className="kka-icon-btn" title="Bölümün tüm paylaşımlarını aç" style={{ color: '#cd6906' }}
                                                                        onClick={() => { setTarafModal(null); openPaylasimCreate(b.bolumID); }}><i className="fas fa-share-alt"></i></button>
                                                                    <button className="kka-icon-btn del" title="Paylaşımı iptal et"
                                                                        onClick={() => paylasimIptal(b.bolumID, p, kisi.adSoyad, b.bolumAdi)}><i className="fas fa-ban" style={{ color: '#dc2626' }}></i></button>
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                            <div className="kka-modal-footer">
                                <button className="kka-btn-secondary" onClick={() => setTarafModal(null)}>Kapat</button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Dağıtım Önerisi Modal */}
            {oneriModal && oneriData && (
                <div className="kka-modal-overlay" onClick={() => setOneriModal(false)}>
                    <div className="kka-modal" style={{ maxWidth: '96vw', width: '96vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                        <div className="kka-modal-header">
                            <h3><i className="fas fa-lightbulb" style={{ color: '#1a2340', marginRight: 8 }}></i>{t('kka.akılliDagitimOnerileri', 'Akıllı Dağıtım Önerileri')}</h3>
                            <button className="kka-modal-close" onClick={() => setOneriModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-modal-body">
                            <div style={{ background: '#f0f4ff', borderRadius: 8, padding: '10px 16px', marginBottom: 18, fontSize: '0.86rem', color: '#444', display: 'flex', alignItems: 'center', gap: 12 }}>
                                <span style={{ flex: 1 }}>
                                    Bu rapor, her kişinin kalan hak ettiği TL tutarına göre dağıtılmamış bölüm paylarını orantısal olarak önerir.
                                    Bir öneriyi kaydetmek için satırdaki <b>Uygula</b>'ya basın; uygulanmayan öneriler değişiklik yapmaz.
                                    Öneriler, rapor hazırlandığı andaki duruma göredir — birkaç uygulamadan sonra güncel öneri için raporu yenileyin.
                                </span>
                                <button className="kka-btn-secondary" onClick={() => { setOneriUygulanan(new Set()); fetchOneri(); }} disabled={oneriLoading}>
                                    <i className={oneriLoading ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'} style={{ marginRight: 6 }}></i>Önerileri Yenile
                                </button>
                            </div>

                            {oneriData.bolumler.filter(b => b.kalanOran > 0).length === 0 ? (
                                <div className="kka-empty">Tüm bölümler %100 dağıtılmış, öneri üretilecek alan yok.</div>
                            ) : (
                                oneriData.bolumler.map(b => (
                                    <div key={b.bolumID} style={{ marginBottom: 28 }}>
                                        {/* Bölüm başlığı */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, borderBottom: '2px solid #e8edf5', paddingBottom: 6 }}>
                                            <strong style={{ color: '#1a2340', fontSize: '0.97rem' }}>
                                                {b.bolumAdi}{b.bolumTipi ? ` – ${b.bolumTipi}` : ''}
                                            </strong>
                                            <div style={{ display: 'flex', gap: 12, fontSize: '0.83rem' }}>
                                                <span>Fiyat: <strong>{fmt(b.fiyat)} ₺</strong></span>
                                                <span style={{ color: '#27ae60' }}>Dağıtılan: <strong>%{b.dagitilanOran.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}</strong></span>
                                                <span style={{ color: b.kalanOran > 0 ? '#cd6906' : '#27ae60' }}>
                                                    Kalan: <strong>%{b.kalanOran.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}</strong>
                                                </span>
                                                {b.atanmayanFiyat > 0 && (
                                                    <span style={{ color: '#c0392b', fontStyle: 'italic' }}>
                                                        Atanamayan: <strong>{fmt(b.atanmayanFiyat)} ₺</strong>
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Mevcut paylaşımlar */}
                                        {b.mevcutPaylasimlar.length > 0 && (
                                            <div style={{ marginBottom: 10 }}>
                                                <div style={{ fontSize: '0.78rem', color: '#888', marginBottom: 4 }}>Mevcut paylaşımlar:</div>
                                                <table className="kka-sub-table" style={{ fontSize: '0.82rem', opacity: 0.85 }}>
                                                    <thead><tr><th>{t('label.adSoyad')}</th><th>{t('label.tur')}</th><th>Oran</th><th>Pay (TL)</th></tr></thead>
                                                    <tbody>
                                                        {b.mevcutPaylasimlar.map(p => (
                                                            <tr key={p.paylasimID}>
                                                                <td>{p.adSoyad}</td>
                                                                <td>{p.tip === 'Ortak' ? 'Arsa Ortağı' : 'Müteahhit'}</td>
                                                                <td>%{p.oranYuzde}</td>
                                                                <td>{fmt(p.payTL)} ₺</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}

                                        {/* Öneriler */}
                                        {b.kalanOran > 0 && b.oneriler.length > 0 && (
                                            <div>
                                                <div style={{ fontSize: '0.78rem', color: '#cd6906', marginBottom: 4, fontWeight: 600 }}>
                                                    <i className="fas fa-magic" style={{ marginRight: 5 }}></i>Önerilen dağıtım (%{b.kalanOran.toLocaleString('tr-TR', { maximumFractionDigits: 2 })} kalan — en yakın kalanTL'ye öncelik, %5 altı atlandı):
                                                </div>
                                                <table className="kka-sub-table" style={{ fontSize: '0.85rem' }}>
                                                    <thead>
                                                        <tr>
                                                            <th>{t('label.adSoyad')}</th>
                                                            <th>{t('label.tur')}</th>
                                                            <th>{t('kka.toplamHakTL', 'Toplam Hak (TL)')}</th>
                                                            <th>{t('kka.oncekiKalanTL', 'Önceki Kalan (TL)')}</th>
                                                            <th>{t('kka.oneriOran', 'Öneri Oran')}</th>
                                                            <th>{t('kka.oneriTL', 'Öneri TL')}</th>
                                                            <th>{t('kka.sonrakiKalanTL', 'Sonraki Kalan (TL)')}</th>
                                                            <th>{t('label.durum')}</th>
                                                            <th style={{ textAlign: 'right' }}>
                                                                {b.oneriler.some(o => !oneriUygulanan.has(`${b.bolumID}-${o.tip}-${o.id}`)) && b.oneriler.length > 1 && (
                                                                    <button className="kka-oneri-uygula tumu" onClick={() => bolumOnerileriniUygula(b)} disabled={oneriUygulaniyor !== null}>
                                                                        <i className="fas fa-check-double"></i> Tümünü Uygula
                                                                    </button>
                                                                )}
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {b.oneriler.map(o => (
                                                            <tr key={`${o.tip}-${o.id}`} style={oneriUygulanan.has(`${b.bolumID}-${o.tip}-${o.id}`) ? { background: '#f0fdf4' } : undefined}>
                                                                <td><strong>{o.adSoyad}</strong></td>
                                                                <td>{o.tip === 'Ortak' ? t('kka.arsaOrtagi', 'Arsa Ortağı') : t('kka.muteahhit', 'Müteahhit')}</td>
                                                                <td>{fmt(o.payTL)} ₺</td>
                                                                <td style={{ color: '#cd6906', fontWeight: 600 }}>{fmt(o.kalanOncesi)} ₺</td>
                                                                <td style={{ fontWeight: 700, color: '#1a2340' }}>%{o.oneriOran.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}</td>
                                                                <td style={{ fontWeight: 700, color: '#1a5cad' }}>{fmt(o.oneriTL)} ₺</td>
                                                                <td style={{ color: o.durum === 'tamamlandi' ? '#27ae60' : '#cd6906', fontWeight: 600 }}>{fmt(o.kalanSonra)} ₺</td>
                                                                <td>
                                                                    {o.durum === 'tamamlandi'
                                                                        ? <span style={{ color: '#27ae60' }}><i className="fas fa-check" style={{ marginRight: 4 }}></i>{t('durum.tamamlandi')}</span>
                                                                        : <span style={{ color: '#cd6906' }}>{fmt(o.kalanSonra)} ₺ devam</span>}
                                                                </td>
                                                                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                                    {(() => {
                                                                        const anahtar = `${b.bolumID}-${o.tip}-${o.id}`;
                                                                        if (oneriUygulanan.has(anahtar)) {
                                                                            return <span style={{ color: '#16a34a', fontWeight: 700 }}><i className="fas fa-check-circle" style={{ marginRight: 4 }}></i>Uygulandı</span>;
                                                                        }
                                                                        return (
                                                                            <button className="kka-oneri-uygula" onClick={() => oneriUygula(b, o)} disabled={oneriUygulaniyor !== null}
                                                                                title={`%${o.oneriOran.toLocaleString('tr-TR', { maximumFractionDigits: 2 })} paylaşımı kaydet`}>
                                                                                <i className={oneriUygulaniyor === anahtar ? 'fas fa-spinner fa-spin' : 'fas fa-check'}></i> Uygula
                                                                            </button>
                                                                        );
                                                                    })()}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}

                                        {b.kalanOran > 0 && b.oneriler.length === 0 && (
                                            <div style={{ fontSize: '0.84rem', color: '#aaa', fontStyle: 'italic' }}>
                                                Kalan pay hakkı olan kişi yok — tüm kişilerin payları tamamlanmış.
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                        {/* Atanamayanlar */}
                        {oneriData && oneriData.atanamamis && oneriData.atanamamis.length > 0 && (
                            <div style={{ margin: '12px 0 4px', padding: '12px 16px', background: '#fff3cd', borderRadius: 8, border: '1px solid #f0c040' }}>
                                <div style={{ fontWeight: 700, color: '#856404', marginBottom: 8, fontSize: '0.9rem' }}>
                                    <i className="fas fa-exclamation-triangle" style={{ marginRight: 5 }}></i>Dağıtılamayan Kişiler ({oneriData.atanamamis.length} kişi)
                                </div>
                                <table className="kka-sub-table" style={{ fontSize: '0.85rem' }}>
                                    <thead>
                                        <tr>
                                            <th>{t('label.adSoyad')}</th>
                                            <th>{t('label.tur')}</th>
                                            <th>{t('kka.toplamHakTL', 'Toplam Hak (TL)')}</th>
                                            <th>{t('kka.dagitilamayanTL', 'Dağıtılamayan (TL)')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {oneriData.atanamamis.map(a => (
                                            <tr key={`atanamamis-${a.tip}-${a.id}`}>
                                                <td><strong>{a.adSoyad}</strong></td>
                                                <td>{a.tip === 'Ortak' ? t('kka.arsaOrtagi', 'Arsa Ortağı') : t('kka.muteahhit', 'Müteahhit')}</td>
                                                <td>{fmt(a.payTL)} ₺</td>
                                                <td style={{ color: '#c0392b', fontWeight: 700 }}>{fmt(a.kalanTL)} ₺</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <div className="kka-modal-footer">
                            <button
                                className="kka-btn-primary"
                                style={{ background: '#c0392b', borderColor: '#c0392b' }}
                                onClick={exportOneriPdf}
                            >
                                <i className="fas fa-file-pdf" style={{ marginRight: 6 }}></i>{t('btn.pdfIndir', 'PDF İndir')}
                            </button>
                            <button className="kka-btn-secondary" onClick={() => setOneriModal(false)}>{t('btn.kapat')}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default KatKarsiligArsaDetay;

